import React, { useState, useCallback, useEffect, useLayoutEffect, useRef, useMemo, lazy, Suspense } from 'react';
import POSPage from './pages/POSPage';
import MobilePOSPage from './pages/MobilePOSPage';
import KitchenDisplayPage from './pages/KitchenDisplayPage';
import ServicePage from './pages/ServicePage';
import DashboardPage from './pages/DashboardPage';
import MenuManagementPage from './pages/MenuManagementPage';
import InventoryPage from './pages/InventoryPage';
import AssetRegisterPage from './pages/AssetRegisterPage';
import StaffManagementPage from './pages/StaffManagementPage';
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const MonthlyOperatingReportPage = lazy(() => import('./pages/MonthlyOperatingReportPage'));
const CouponCampaignsPage = lazy(() => import('./pages/CouponCampaignsPage'));
const ExpensesPage = lazy(() => import('./pages/ExpensesPage'));
const InvoicesPage = lazy(() => import('./pages/InvoicesPage'));
const PurchasingPage = lazy(() => import('./pages/PurchasingPage'));
const ReceiptSharePage = lazy(() => import('./pages/ReceiptSharePage'));
import CustomerManagementPage from './pages/CustomerManagementPage';
import LoyaltyPage from './pages/LoyaltyPage';
import CustomerAppPushPage from './pages/CustomerAppPushPage';
import CustomerAppFeedbackPage from './pages/CustomerAppFeedbackPage';
import CustomerAppChatPage from './pages/CustomerAppChatPage';
import CateringPage from './pages/CateringPage';
import { NavigationProvider } from './contexts/NavigationContext';
import CustomerAppAnnouncementsPage from './pages/CustomerAppAnnouncementsPage';
import QrManagementPage from './pages/QrManagementPage';
import TableManagementPage from './pages/TableManagementPage';
import ReservationManagementPage from './pages/ReservationManagementPage';
import PublicBookingPage from './pages/PublicBookingPage';
import CustomerMenuPage from './pages/CustomerMenuPage';
import OnlineOrderPage from './pages/OnlineOrderPage';
import DeliveryIntegrationsPage from './pages/DeliveryIntegrationsPage';
import UserRolesPage from './pages/UserRolesPage';
import SettingsPage from './pages/SettingsPage';
import LockScreenPage from './pages/LockScreenPage';
import SignInPage from './pages/SignInPage';
import MainLayout from './components/layout/MainLayout';
import PinEntryModal from './components/auth/PinEntryModal';
import StandbyScreen from './components/StandbyScreen';
import ProfilePage from './pages/ProfilePage';
import { isManager, canManageRegister } from './lib/authUtils';
import { useInactivityTimer } from './hooks/useInactivityTimer';
import {
  clearWaitstaffMobileSession,
  readWaitstaffMobileSession,
  writeWaitstaffMobileSession,
} from './lib/waitstaffMobileSession';
import { useCategories, useMenuItems, useCustomers, useStaff, useInventory, useAssets, useTables, useOrders, useReservations, useEvents, useRegisterSessions, useQuickAddCombos, useFloorFixtures, useUserRoles, useRecipes, useSuppliers, usePurchaseOrders, useBillingClients, useSupplierInvoices, useClientInvoices, useLoyaltyRewards, useAppOffers, useAppAnnouncements } from './hooks';
import { useAutoSoftKeyboard } from './hooks/useAutoSoftKeyboard';
import { setPublicGuestOrderingSessionActive } from './lib/publicGuestRoutes';
import { applyPurchaseOrderReceive, formatReceiveSummary } from './lib/purchaseOrderReceive';
import { normalizePurchaseOrder } from './hooks/usePurchaseOrders';
import { initialDeliveryOrders } from './data/deliveryOrders';
import { useUberPendingOrders } from './hooks/useUberPendingOrders';
import { acceptUberEatsOrder, denyUberEatsOrder, reconcileUberPendingOrders } from './lib/uberEatsAuth';
import { initialIntegrationSettings } from './data/integrations';
import { normalizeEventStallSection, collectEventTerminalIdsFromStall } from './lib/normalizeEventStallSettings';
import { logConfig } from './lib/config';
import { logger } from './lib/logger';
import { checkAndResetDailyAvailability } from './lib/dailyAvailability';
import { isPublicOnlineOrderingEnabled } from './lib/onlineOrderVisibility';
import { clearAllMenuPhotos } from './lib/clearMenuImages';
import { ALL_PERMISSIONS } from './lib/permissions';
import { getEffectiveQuantityCompleted, getOrderTotalFromItems, getOrderTotals, hasBalanceDue, orderHasMeaningfulItems, orderMeaningfulItemQty, computeSubBillTotals, getItemLineId, getUnpaidQuantityForItem, hasUnpaidItems, isFloorPlanActiveTableOrder, isActiveBuffetSession, mergeOrderPreferComplete, normalizeOrderRecord, finalizeOrderDocumentForWrite, capRunningOrderBatchDeltasToItems, isIntentionalOrderAmendment } from './lib/orderUtils';
import { holidaySurchargeSnapshotFields } from './lib/holidaySurcharge';
import { getBuffetPackages, migrateLegacyBuffetPricing } from './lib/buffetPackages';
import { liveFishSnapshotFields } from './lib/liveFishPrice';
import {
  reservationHoldsTable,
  tableHasSeatedReservation,
  isReservationSoonForFloor,
  seatedReservationsToMoveOnOrderTransfer,
  buildReservationUpdatesForOrderTransfer,
  buildReservationUpdatesForTableTransfer,
  RESERVATION_TABLE_TRANSFER_STATUSES,
} from './lib/reservationCapacity';
import { deriveOrderLifecycleStatus, mapOrderStatusToLegacy } from './lib/orderStateMachine';
import { orderCreatedAtMs } from './lib/orderTimestamps';
import { initialUserRoles } from './data/userRoles';
import { auditLogsService, registerSessionsService } from './lib/database';
import { setAuditActor } from './lib/auditActor';
import { orderEditSummaries, formatAuditItemList } from './lib/auditSummaries';
import { normalizeRegisterSession } from './lib/registerSessionUtils';
import { liveDataStatus, type FeedStatus } from './lib/liveDataStatus';
import type { Order, OrderItem, OrderStatus, Page, MenuItem, Category, InventoryItem, RestaurantAsset, StaffMember, Customer, LoyaltyReward, OrderType, Table, Supplier, PurchaseOrder, BillingClient, SupplierInvoice, ClientInvoice, DeliveryOrder, UserRole, IntegrationSettings, CurrentUser, ReceiptPrinter, Payment, Recipe, RegisterSession, Reservation, Event, QuickAddCombo, FloorFixture, SmartpayTerminal, OverseasShipment, ShipmentItem, PublicOnlineOrderingConfig, SubBill, BusinessInfo, BuffetPackage, MealPlanSale } from './types';

/**
 * Last-known active register for this browser tab (instant UI after hard refresh; Firestore snapshot replaces it).
 *
 * The register is shared across every signed-in device, so this cache is no longer keyed by terminalId —
 * any tab/device just remembers the last active session it observed, then re-confirms via Firestore.
 */
const ACTIVE_REGISTER_SESSION_CACHE_KEY = 'kingspark_active_register_session';

function readCachedRegisterSession(): RegisterSession | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(ACTIVE_REGISTER_SESSION_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { session: RegisterSession; storedAt: number };
    const s = parsed?.session;
    if (!s?.id || !s.isActive) return null;
    if (Date.now() - (parsed.storedAt ?? 0) > 24 * 60 * 60 * 1000) return null;
    return normalizeRegisterSession(s);
  } catch {
    return null;
  }
}

function writeCachedRegisterSession(session: RegisterSession | null) {
  try {
    if (session) {
      sessionStorage.setItem(
        ACTIVE_REGISTER_SESSION_CACHE_KEY,
        JSON.stringify({ session: normalizeRegisterSession(session), storedAt: Date.now() }),
      );
    } else {
      sessionStorage.removeItem(ACTIVE_REGISTER_SESSION_CACHE_KEY);
    }
  } catch {
    /* ignore */
  }
}

/** Merge remote integration settings and align Event/Stall with active profile (same as Settings normalize). */
function withNormalizedEventStall(base: IntegrationSettings): IntegrationSettings {
  return {
    ...base,
    eventStall: normalizeEventStallSection(base),
  };
}

// Helper to generate a UUID
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

// Helper to remove undefined values before sending to Firestore
const removeUndefined = <T,>(obj: T): T => {
  if (Array.isArray(obj)) {
    return obj.map(removeUndefined) as T;
  }
  if (obj && typeof obj === 'object') {
    const cleaned: Record<string, any> = {};
    Object.entries(obj as Record<string, any>).forEach(([key, value]) => {
      if (value === undefined) return;
      cleaned[key] = removeUndefined(value);
    });
    return cleaned as T;
  }
  return obj;
};

/** User-facing detail for merge/split table Firestore failures (permission, network, etc.). */
function describeMergeSplitFirestoreError(error: unknown): string {
  const message =
    error instanceof Error && typeof error.message === 'string' ? error.message.trim() : '';
  const code =
    error && typeof error === 'object' && 'code' in error
      ? String((error as { code: unknown }).code)
      : '';
  if (code === 'permission-denied') {
    return 'Permission denied. Sign in with an account that can edit tables.';
  }
  if (/missing\s+or\s+insufficient\s+permissions/i.test(message)) {
    return 'Missing permissions to save tables. Check your staff role and try again.';
  }
  if (/network|offline|failed\s+to\s+fetch|unavailable/i.test(message)) {
    return 'Network issue while saving. Check your connection and try again.';
  }
  if (message) return code ? `${message} (${code})` : message;
  return code ? `Unknown error (${code})` : 'Unknown error';
}

const PIN_PROTECTED_PAGES: Page[] = ['pos', 'kds', 'service'];
const ACTIVE_PAGE_STORAGE_KEY = 'kingspark_active_page';
const VALID_PAGES: Page[] = [
  'dashboard', 'pos', 'pos-mobile', 'kds', 'service', 'menu', 'inventory', 'assets', 'staff', 'reports',
  'reports-sales-insights', 'reports-event-stall', 'reports-monthly', 'reports-financial', 'reports-reconciliation', 'reports-food-cost',
  'expenses', 'qr-management', 'customers', 'loyalty', 'customer-app-push', 'customer-app-feedback', 'customer-app-chat', 'catering', 'customer-app-announcements', 'table-management', 'reservations',
  'purchasing', 'delivery', 'user-roles', 'coupon-campaigns', 'settings', 'profile',
];

const getStoredActivePage = (): Page | null => {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(ACTIVE_PAGE_STORAGE_KEY);
  if (!raw) return null;
  return VALID_PAGES.includes(raw as Page) ? (raw as Page) : null;
};

type PinAction =
  | { type: 'page'; page: Page }
  | { type: 'register'; action: 'open' | 'close'; onApproved: () => void };

const App: React.FC = () => {
  // Auth state first so staff-only hooks can be gated (avoid Firestore permission errors when signed out)
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const staffDataEnabled = !!currentUser;
  const [activePage, setActivePage] = useState<Page>(() => getStoredActivePage() ?? 'dashboard');
  // Remembers the page before the current one (for "Back to POS" after following a POS alert).
  const [previousPage, setPreviousPage] = useState<Page | null>(null);
  const lastPageRef = useRef<Page>(activePage);
  useEffect(() => {
    if (lastPageRef.current !== activePage) {
      setPreviousPage(lastPageRef.current);
      lastPageRef.current = activePage;
    }
  }, [activePage]);
  const invoicePageEnabled = staffDataEnabled && activePage === 'invoices';
  const purchasingInvoiceDataEnabled = staffDataEnabled && (activePage === 'invoices' || activePage === 'purchasing');
  const purchasingDataEnabled = staffDataEnabled && (activePage === 'purchasing' || activePage === 'invoices');
  const deliveryDataEnabled = staffDataEnabled && activePage === 'delivery';
  const reservationsDataEnabled = staffDataEnabled && (
    activePage === 'reservations' || activePage === 'table-management' || activePage === 'dashboard' || activePage === 'pos'
  );
  const inventoryDataEnabled = staffDataEnabled && (
    activePage === 'inventory' || activePage === 'menu' || activePage === 'pos' || activePage === 'pos-mobile'
    || activePage === 'purchasing' || activePage === 'dashboard'
  );
  const assetsDataEnabled = staffDataEnabled && activePage === 'assets';
  const recipesDataEnabled = staffDataEnabled && (
    activePage === 'inventory' || activePage === 'menu' || activePage === 'pos' || activePage === 'pos-mobile'
  );
  // Events list only needed on the Events/reservations management page (not POS general bookings).
  const eventsDataEnabled = staffDataEnabled && (
    activePage === 'reservations' || activePage === 'table-management' || activePage === 'dashboard'
  );
  // Keep one global orders listener on ops surfaces so POS/KDS/Service stay in sync.
  // Settings/menu/staff/etc. do not attach the live feed (saves reconnect reads).
  // Reports use date-range fetches; still enable on report routes so shared App state stays warm.
  const ordersDataEnabled = staffDataEnabled && (
    activePage === 'pos' ||
    activePage === 'pos-mobile' ||
    activePage === 'kds' ||
    activePage === 'service' ||
    activePage === 'delivery' ||
    activePage === 'dashboard' ||
    activePage === 'table-management' ||
    activePage === 'expenses' ||
    activePage.startsWith('reports')
  );

  // Use data hooks for Firestore integration (staff-only hooks only fetch when signed in)
  const { categories, create: createCategory, update: updateCategory, remove: removeCategory, refresh: refreshCategories } = useCategories();
  const { menuItems, create: createMenuItem, update: updateMenuItem, remove: removeMenuItem, refresh: refreshMenuItems } = useMenuItems();
  const { customers, create: createCustomer, update: updateCustomer, remove: removeCustomer } = useCustomers(staffDataEnabled);
  const {
    rewards,
    save: saveLoyaltyReward,
    remove: removeLoyaltyReward,
  } = useLoyaltyRewards(staffDataEnabled);
  const {
    offers: appOffers,
    save: saveAppOffer,
    remove: removeAppOffer,
  } = useAppOffers(staffDataEnabled);
  const {
    announcements: appAnnouncements,
    save: saveAppAnnouncement,
    remove: removeAppAnnouncement,
  } = useAppAnnouncements(staffDataEnabled);
  const { staff, create: createStaffMember, update: updateStaffMember, remove: removeStaffMember, getByPin: getStaffByPin } = useStaff(staffDataEnabled);
  const { inventory, create: createInventoryItem, update: updateInventory, remove: removeInventoryItem, refresh: refreshInventory } = useInventory(inventoryDataEnabled);
  const { assets, loading: assetsLoading, create: createAsset, update: updateAsset, remove: removeAsset } = useAssets(assetsDataEnabled);
  const { suppliers, create: createSupplier, update: updateSupplier, remove: deleteSupplier } = useSuppliers(purchasingDataEnabled);
  const { purchaseOrders, create: createPurchaseOrder, update: updatePurchaseOrder } = usePurchaseOrders(purchasingDataEnabled, inventory);
  const { billingClients, loading: billingClientsLoading, create: createBillingClient, update: updateBillingClient, remove: deleteBillingClient } = useBillingClients(invoicePageEnabled);
  const { supplierInvoices, loading: supplierInvoicesLoading, create: createSupplierInvoice, update: updateSupplierInvoice } = useSupplierInvoices(purchasingInvoiceDataEnabled);
  const { clientInvoices, loading: clientInvoicesLoading, create: createClientInvoice, update: updateClientInvoice } = useClientInvoices(invoicePageEnabled);
  const [invoicePrefillPoId, setInvoicePrefillPoId] = useState<string | undefined>();
  const { tables, loading: tablesLoading, create: createTable, update: updateTable, remove: removeTable, refresh: refreshTables } = useTables(staffDataEnabled);
  const { orders, create: createOrder, update: updateOrder, remove: removeOrder, refresh: refreshOrders } = useOrders(ordersDataEnabled);
  const { reservations, create: createReservation, update: updateReservation, remove: removeReservation, refresh: refreshReservations } = useReservations(reservationsDataEnabled);
  const { events, create: createEvent, update: updateEvent, remove: removeEvent, refresh: refreshEvents } = useEvents(eventsDataEnabled);
  const { sessions: registerSessions, activeSessions, create: createRegisterSession, update: updateRegisterSession, refresh: refreshRegisterSessions } = useRegisterSessions(staffDataEnabled);
  const { combos: quickAddCombos, create: createCombo, update: updateCombo, remove: removeCombo } = useQuickAddCombos(staffDataEnabled);
  const { fixtures: floorFixtures, update: updateFloorFixture, create: createFloorFixture, remove: removeFloorFixture, refresh: refreshFloorFixtures } = useFloorFixtures(staffDataEnabled);

  // Terminal ID management - unique per device/browser
  const [terminalId, setTerminalId] = useState<string>(() => {
    let id = localStorage.getItem('pos_terminal_id');
    if (!id) {
      id = `TERM-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('pos_terminal_id', id);
    }
    return id;
  });
  const [terminalName, setTerminalName] = useState<string>(() => {
    const savedName = localStorage.getItem('pos_terminal_name');
    if (savedName) return savedName;
    const id = localStorage.getItem('pos_terminal_id') || `TERM-${Date.now()}`;
    return `Terminal ${id.slice(-4)}`;
  });
  const [currentSession, setCurrentSession] = useState<RegisterSession | null>(readCachedRegisterSession);
  // Layer 1 of duplicate-session defense: flipped to `true` after the first
  // `subscribeActive` snapshot lands. Until then, RegisterClosedGuard renders
  // a neutral spinner so a cashier can't tap "Open Register" before we know
  // whether another device has already opened one.
  const [registerSessionsHydrated, setRegisterSessionsHydrated] = useState(false);
  // Bumped on tab-visible/online so the register-session effect below tears down and
  // re-establishes its listener + does a fresh server read. Firestore's onSnapshot can go
  // silently stale after a long idle/sleep (especially with forced long-polling) without ever
  // firing an error, so `liveDataStatus.refreshUnhealthy()` won't catch it — this is what caused
  // a terminal left open overnight to still show an "active" register the next morning after it
  // had been closed elsewhere. Belt-and-braces: always re-verify on wake, not just when the feed
  // reports itself unhealthy.
  const [registerWakeTick, setRegisterWakeTick] = useState(0);
  const [isOpeningRegister, setIsOpeningRegister] = useState(false); // Flag to prevent race condition
  const [closeRegisterFailed, setCloseRegisterFailed] = useState(false); // Show Force Close only when normal close failed

  useEffect(() => {
    logger.debug('currentSession state changed', currentSession);
  }, [currentSession]);

  // Local state for UI and non-database data
  const [couponCampaignsInitialId, setCouponCampaignsInitialId] = useState<string | null>(null);
  const [isPinBasedAccess, setIsPinBasedAccess] = useState(false); // Track if user accessed via PIN
  const [waitstaffMobileMode, setWaitstaffMobileMode] = useState(() => readWaitstaffMobileSession());
  const [showPinEntry, setShowPinEntry] = useState(false); // Show PIN entry modal
  const [pendingPinAction, setPendingPinAction] = useState<PinAction | null>(null);
  const pendingPinActionRef = useRef<PinAction | null>(null);
  const currentUserRef = useRef<CurrentUser | null>(null);
  /** When we mark a table occupied on place order, avoid sync effect flipping it to available before new order is in state */
  const lastOccupiedTableRef = useRef<{ tableId: number; at: number } | null>(null);
  /** Tracks signed-in state so we only clear register sessionStorage on logout, not during auth bootstrap. */
  const hadStaffSessionRef = useRef(false);

  useEffect(() => {
    pendingPinActionRef.current = pendingPinAction;
  }, [pendingPinAction]);

  useEffect(() => {
    currentUserRef.current = currentUser;
  }, [currentUser]);
  const [shipments, setShipments] = useState<OverseasShipment[]>([]);
  const uberPendingOrders = useUberPendingOrders(deliveryDataEnabled);
  const mockDoorDashOrders = initialDeliveryOrders.filter((o) => o.platform === 'DoorDash');
  const [mockRejectedIds, setMockRejectedIds] = useState<Set<string>>(new Set());
  const deliveryOrders: DeliveryOrder[] = [
    ...uberPendingOrders,
    ...mockDoorDashOrders.filter((o) => !mockRejectedIds.has(o.id)).map((o) => ({ ...o, status: 'new' as const })),
  ];
  const {
    userRoles,
    loading: userRolesLoading,
    error: userRolesError,
    refresh: refreshUserRoles,
    create: createUserRole,
    update: updateUserRole,
    remove: deleteUserRole,
  } = useUserRoles(staffDataEnabled);
  const [integrationSettings, setIntegrationSettings] = useState<IntegrationSettings>(initialIntegrationSettings);

  // Exclude Event-assigned Smartpay terminals from the main restaurant POS (Event-only hardware).
  const mainPosTerminals = useMemo(() => {
    const allTerminals = integrationSettings?.smartpay?.terminals ?? [];
    const eventIds = collectEventTerminalIdsFromStall(integrationSettings?.eventStall);
    if (!integrationSettings?.eventStall?.enabled || eventIds.length === 0) return allTerminals;
    const excluded = new Set(eventIds);
    return allTerminals.filter((t) => !excluded.has(t.id));
  }, [integrationSettings]);

  const [publicOnlineOrderingConfig, setPublicOnlineOrderingConfig] = useState<PublicOnlineOrderingConfig | null>(null);
  const [publicOnlineOrderingLoading, setPublicOnlineOrderingLoading] = useState(false);
  /** Latch: Windcave return must not wait on public config fetch before mounting OnlineOrderPage. */
  const [windcaveBrowserReturn] = useState(() => {
    if (typeof window === 'undefined') return false;
    const search = window.location.search || (window.location.hash ? window.location.hash.replace(/^#/, '') : '');
    const params = new URLSearchParams(search.startsWith('?') ? search : `?${search}`);
    const wc = params.get('wc_return');
    return wc === 'approved' || wc === 'declined' || wc === 'cancelled';
  });
  const { recipes, save: saveRecipe, refresh: refreshRecipes } = useRecipes(recipesDataEnabled);
  const [fireModeActive, setFireModeActive] = useState(false);
  
  // Initialize viewMode from URL/host so online/customer links work on first paint (e.g. after deploy).
  // Path /order and subdomains like order.example.com are used for hosting (Hostinger etc.)
  // where hash/query can be stripped or lost on redirects.
  const [viewMode, setViewMode] = useState<'admin' | 'customer' | 'online' | 'book' | 'receipt'>(() => {
    if (typeof window === 'undefined') return 'admin';
    const host = window.location.hostname.toLowerCase();
    const path = (window.location.pathname || '/').replace(/\/$/, '') || '/';

    // Public event booking (e.g. /book?event=xxx or ?view=book&event=xxx)
    const search = window.location.search || (window.location.hash ? window.location.hash.replace(/^#/, '') : '');
    const params = new URLSearchParams(search.startsWith('?') ? search : `?${search}`);
    const eventParam = params.get('event');
    if ((path === '/book' || params.get('view') === 'book') && eventParam) return 'book';

    // Windcave HPP returns to callbackUrls with ?wc_return=approved|declined|cancelled (no sessionId).
    // Treat as public online ordering so OnlineOrderPage mounts and can finish confirmation.
    const wcReturn = params.get('wc_return');
    if (wcReturn === 'approved' || wcReturn === 'declined' || wcReturn === 'cancelled') return 'online';
    const mpReturn = params.get('mp_return');
    if (mpReturn === 'approved' || mpReturn === 'declined' || mpReturn === 'cancelled') return 'online';

    // Dedicated ordering subdomain (e.g. order.kingspark.co.nz) should always default to online view
    if (host.startsWith('order.')) return 'online';

    // Path-based ordering URL (e.g. https://kingspark.co.nz/order)
    if (path === '/order') return 'online';
    const view = params.get('view');
    if (view === 'online') return 'online';
    if (view === 'customer') return 'customer'; // customerViewParams will be set in useEffect
    if (view === 'receipt' && params.get('data')) return 'receipt';
    return 'admin';
  });

  useLayoutEffect(() => {
    setPublicGuestOrderingSessionActive(
      viewMode === 'online' || viewMode === 'customer' || viewMode === 'book'
    );
  }, [viewMode]);

  const [bookingEventId, setBookingEventId] = useState<string>(() => {
    if (typeof window === 'undefined') return '';
    const search = window.location.search || (window.location.hash ? window.location.hash.replace(/^#/, '') : '');
    const params = new URLSearchParams(search.startsWith('?') ? search : `?${search}`);
    return params.get('event') || '';
  });
  const [customerViewParams, setCustomerViewParams] = useState<{table: Table} | null>(null);
  const [receiptShareData, setReceiptShareData] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const search = window.location.search || (window.location.hash ? window.location.hash.replace(/^#/, '') : '');
    const params = new URLSearchParams(search.startsWith('?') ? search : `?${search}`);
    return params.get('data');
  });
  const [takeawayCounter, setTakeawayCounter] = useState(101);
  const [deliveryCounter, setDeliveryCounter] = useState(501);
  
  // Standby and lock state
  const [isStandby, setIsStandby] = useState(false);
  const [isRegisterLocked, setIsRegisterLocked] = useState(false);

  // Log configuration on mount
  useEffect(() => {
    logConfig();
  }, []);

  // Auto-attach soft keyboard to all inputs (disabled on Mobile POS so device keyboard is used)
  useAutoSoftKeyboard(activePage);

  useEffect(() => {
    try {
      window.localStorage.setItem(ACTIVE_PAGE_STORAGE_KEY, activePage);
    } catch {
      // Ignore storage failures (private mode, quota, etc.)
    }
  }, [activePage]);

  // Check and reset daily availability on app load and periodically (staff only — writes menu_items)
  useEffect(() => {
    if (!staffDataEnabled) return;
    if (menuItems.length === 0) return;

    const resetAvailability = async () => {
      const resetItems = checkAndResetDailyAvailability(menuItems);
      
      // Check if any items were reset by comparing by ID
      const itemsToUpdate = resetItems.filter(item => {
        const original = menuItems.find(mi => mi.id === item.id);
        return original && (
          item.dailyAvailability !== original.dailyAvailability ||
          item.lastAvailabilityReset !== original.lastAvailabilityReset
        );
      });

      if (itemsToUpdate.length > 0) {
        console.log('🔄 Resetting daily availability for menu items...');
        // Batch update all items that were reset
        const updatePromises = itemsToUpdate.map(item => updateMenuItem(item.id, {
          dailyAvailability: item.dailyAvailability,
          lastAvailabilityReset: item.lastAvailabilityReset,
        }));

        try {
          await Promise.all(updatePromises);
          console.log('✅ Daily availability reset complete');
        } catch (error) {
          console.error('❌ Error resetting daily availability:', error);
        }
      }
    };

    resetAvailability();

    // Check every hour for new day
    const interval = setInterval(resetAvailability, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [staffDataEnabled, menuItems, updateMenuItem]);

  // Load integration settings from Firestore when signed in (staff-only read)
  useEffect(() => {
    if (!staffDataEnabled) return;
    if (uberPendingOrders.length === 0) return;
    // Run once immediately so newly received cards get item details ASAP.
    void reconcileUberPendingOrders().catch(() => {
      // Best-effort background reconciliation; ignore transient callable errors.
    });
    const id = window.setInterval(() => {
      void reconcileUberPendingOrders().catch(() => {
        // Best-effort background reconciliation; ignore transient callable errors.
      });
    }, 60_000);
    return () => window.clearInterval(id);
  }, [staffDataEnabled, uberPendingOrders.length]);

  // Integration settings loading is consolidated below in the smartpay-aware effect
  // (was previously duplicated here, causing 2 Firestore reads per login).

  // Handle Uber Eats OAuth callback: ?code=...&state=uber_eats_pos (redirect back from Uber)
  useEffect(() => {
    if (!currentUser) return;
    const search = window.location.search || '';
    const params = new URLSearchParams(search.startsWith('?') ? search : `?${search}`);
    const code = params.get('code');
    const state = params.get('state');
    if (!code || state !== 'uber_eats_pos') return;

    let cancelled = false;
    (async () => {
      try {
        const { exchangeCodeForToken } = await import('./lib/uberEatsAuth');
        const tokenResponse = await exchangeCodeForToken(code);
        const { integrationSettingsService } = await import('./lib/database');
        const saved = await integrationSettingsService.get();
        const current: IntegrationSettings = saved ? { ...initialIntegrationSettings, ...saved } : initialIntegrationSettings;
        const uberConfig = current['Uber Eats'] || { enabled: false, apiKey: '', clientSecret: '', storeId: '' };
        const expiresAt = tokenResponse.expires_in
          ? Date.now() + tokenResponse.expires_in * 1000
          : undefined;
        const updated: IntegrationSettings = {
          ...current,
          'Uber Eats': {
            ...uberConfig,
            accessToken: tokenResponse.access_token,
            refreshToken: tokenResponse.refresh_token,
            tokenExpiresAt: expiresAt,
            apiKey: uberConfig.apiKey || (await import('./lib/config')).config.uber.clientId,
          },
        };
        await integrationSettingsService.save(updated);
        if (!cancelled) {
          setIntegrationSettings(withNormalizedEventStall(updated));
          window.history.replaceState({}, '', window.location.pathname || '/');
          setActivePage('settings');
        }
      } catch (err) {
        console.error('Uber Eats OAuth callback error:', err);
        if (!cancelled) {
          window.history.replaceState({}, '', window.location.pathname || '/');
          alert('Uber Eats connection failed. Check console and try again.');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser]);

  // Check URL path/host and params for customer view (QR) and online ordering.
  // Supports:
  // - Dedicated ordering subdomain (order.example.com)
  // - Path /order
  // - ?view=online and #view=online
  useEffect(() => {
    const host = window.location.hostname.toLowerCase();
    const path = (window.location.pathname || '/').replace(/\/$/, '') || '/';
    const search = window.location.search || (window.location.hash ? window.location.hash.replace(/^#/, '') : '');
    const urlParams = new URLSearchParams(search.startsWith('?') ? search : `?${search}`);
    const eventParam = urlParams.get('event');
    if ((path === '/book' || urlParams.get('view') === 'book') && eventParam) {
      setBookingEventId(eventParam);
      setViewMode('book');
      return;
    }
    const wcReturn = urlParams.get('wc_return');
    if (wcReturn === 'approved' || wcReturn === 'declined' || wcReturn === 'cancelled') {
      setViewMode('online');
      return;
    }
    if (host.startsWith('order.') || path === '/order') {
      setViewMode('online');
      return;
    }
    const view = urlParams.get('view');
    const tableIdParam = urlParams.get('tableId');
    const receiptDataParam = urlParams.get('data');
    if (view === 'online') {
      setViewMode('online');
      return;
    }
    if (view === 'receipt' && receiptDataParam) {
      setReceiptShareData(receiptDataParam);
      setViewMode('receipt');
      return;
    }
    if (view === 'customer' && tableIdParam) {
      const tableId = parseInt(tableIdParam, 10);
      const table = tables.find(t => t.id === tableId);
      if (table) {
        setCustomerViewParams({ table });
        setViewMode('customer');
      }
    }
  }, [tables]);

  // Load public online ordering config when in online view (readable without auth)
  useEffect(() => {
    if (viewMode !== 'online') return;
    let cancelled = false;
    setPublicOnlineOrderingLoading(true);
    (async () => {
      try {
        const { publicOnlineOrderingService } = await import('./lib/database');
        const config = await publicOnlineOrderingService.get();
        if (!cancelled) {
          setPublicOnlineOrderingConfig(config);
        }
      } catch {
        if (!cancelled) setPublicOnlineOrderingConfig(null);
      } finally {
        if (!cancelled) setPublicOnlineOrderingLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [viewMode]);

  // Listen to Firebase Auth state changes (for session persistence)
  useEffect(() => {
    let isMounted = true;
    let unsubscribe: (() => void) | null = null;
    let loadingTimeout: NodeJS.Timeout | null = null;
    
    const setupAuthListener = async () => {
      try {
        const { authService } = await import('./lib/auth');
        
        // Set a timeout to ensure loading is cleared even if auth state doesn't fire
        loadingTimeout = setTimeout(() => {
          if (isMounted) {
            console.log('⏱️ Auth loading timeout - clearing loading state');
            setIsAuthLoading(false);
          }
        }, 3000); // 3 second timeout
        
        unsubscribe = authService.onAuthStateChange(async (user) => {
          if (!isMounted) return;
          
          // Clear the timeout since we got a response
          if (loadingTimeout) {
            clearTimeout(loadingTimeout);
            loadingTimeout = null;
          }
          
          // Log auth state change (sanitized)
          const { logger } = await import('./lib/logger');
          logger.auth('Auth state changed', { 
            signedIn: !!user,
            userId: user?.uid ? '[REDACTED]' : undefined 
          });
          
          try {
            if (user) {
              // Refresh ID token so custom claims (staffId, isAdmin) are up to date
              await (user as any).getIdToken?.(true);
              const authUserId = (user as any).uid || user.id;
              const staffRecord = await authService.getStaffByAuthUserId(authUserId);
              
              if (staffRecord) {
                // Use a ref to check current user state
                const currentUserSnapshot = currentUser;
                
                if (!currentUserSnapshot) {
                  console.log('✅ Restoring session for:', staffRecord.name);
                  if (handleSignInRef.current) {
                    await handleSignInRef.current(authUserId, staffRecord);
                  }
                }
              }
            } else {
              // User is signed out - clear session if exists
              console.log('🚪 No user signed in');
              if (isMounted && setCurrentUser) {
                setCurrentUser(null);
              }
            }
          } catch (error: any) {
            // Only log as error if it's a critical issue
            // Expected errors (like staff not found during initial check) should be warnings
            if (error?.message?.includes('not found') || error?.code === 'not-found') {
              // Staff record not found is expected in some cases (e.g., new user, unlinked account)
              // This is handled gracefully by the auth flow
              console.debug('Auth state change: Staff record not found (expected in some cases)');
            } else {
              console.error('Error in auth state change handler:', error);
            }
          } finally {
            // Always set loading to false after auth check
            if (isMounted) setIsAuthLoading(false);
          }
        });
      } catch (error: any) {
        // Log error but don't prevent app from loading
        // Firebase might not be available in some environments
        if (error?.message?.includes('Firebase') || error?.code?.includes('firebase')) {
          console.warn('Firebase auth listener setup issue (app will continue):', error?.message || error);
        } else {
          console.error('Error setting up auth listener:', error);
        }
        if (loadingTimeout) clearTimeout(loadingTimeout);
        if (isMounted) setIsAuthLoading(false);
      }
    };
    
    setupAuthListener();
    
    return () => {
      isMounted = false;
      if (loadingTimeout) clearTimeout(loadingTimeout);
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []); // Empty deps - only run once on mount, use ref for handleSignIn
  
  // Single source-of-truth loader for integration settings.
  // - Hits Firestore exactly once per signed-in session (avoids duplicate cold reads).
  // - Handles legacy → new SmartPay terminal migration in-place.
  // - If `smartpay` is missing on an existing doc, ADDS it (no longer overwrites unrelated saved settings).
  // - On transient errors, leaves existing in-memory state untouched (no destructive default-reset).
  useEffect(() => {
    if (!currentUser) return;
    let cancelled = false;

    const loadIntegrationSettings = async () => {
      try {
        // Note: integrationSettingsService.get() logs its own '📦 Loading…' line; no need to duplicate here.
        const { integrationSettingsService } = await import('./lib/database');
        const savedSettings = await integrationSettingsService.get();
        if (cancelled) return;

        let settings: IntegrationSettings = savedSettings
          ? { ...initialIntegrationSettings, ...(savedSettings as IntegrationSettings) }
          : { ...initialIntegrationSettings };
        let needsSave = !savedSettings;

        const sp: any = settings.smartpay;
        if (sp && sp.posRegisterId && !sp.terminals) {
          // Migrate legacy single-register shape to terminals[]
          const defaultTerminal: SmartpayTerminal = {
            id: 'terminal-1',
            name: 'Main Terminal',
            registerId: sp.posRegisterId,
            registerName: 'Main Register',
            isPaired: sp.isPaired || false,
            lastPairedAt: sp.isPaired ? Date.now() : undefined,
          };
          settings = {
            ...settings,
            smartpay: {
              terminals: [defaultTerminal],
              posVendorName: sp.posVendorName || 'KINGS PARK POS',
              posBusinessName: sp.posBusinessName || 'Kings Park Restaurant',
            },
          };
          needsSave = true;
        } else if (!sp || !Array.isArray(sp.terminals) || sp.terminals.length === 0) {
          // No SmartPay block (or empty terminals) - seed a default terminal without
          // clobbering any other saved sections.
          const defaultTerminal: SmartpayTerminal = {
            id: 'terminal-1',
            name: 'Main Terminal',
            registerId: generateUUID(),
            registerName: 'Main Register',
            isPaired: false,
          };
          settings = {
            ...settings,
            smartpay: {
              terminals: [defaultTerminal],
              posVendorName: sp?.posVendorName || 'KINGS PARK POS',
              posBusinessName: sp?.posBusinessName || 'Kings Park Restaurant',
            },
          };
          needsSave = true;
        }

        // Migrate legacy Settings buffet prices → buffetPackages list (once)
        if (!(settings.buffetPackages?.length) && settings.buffetPricing) {
          const seeded = migrateLegacyBuffetPricing(settings);
          if (seeded.length > 0) {
            settings = { ...settings, buffetPackages: seeded };
            needsSave = true;
          }
        }

        setIntegrationSettings(withNormalizedEventStall(settings));

        if (needsSave) {
          try {
            await integrationSettingsService.save(settings);
          } catch (saveErr: any) {
            console.debug('Could not persist seeded integration settings (will retry next load):', saveErr?.message);
          }
        }
      } catch (error: any) {
        // Non-critical: keep whatever is currently in state (don't wipe to defaults on transient errors).
        console.debug('Integration settings not loaded (keeping current state):', error?.message || 'Settings not found');
      }
    };

    loadIntegrationSettings();
    return () => { cancelled = true; };
  }, [currentUser]);

  // Transaction recovery on app startup - handles power failure scenarios
  useEffect(() => {
    const recoverTransactions = async () => {
      // Only recover if user is authenticated
      if (!currentUser) return;

      try {
        const { transactionRecoveryService } = await import('./lib/transactionRecovery');
        const { ordersService } = await import('./lib/database');

        // Helper to get order
        const getOrder = async (orderId: string) => {
          try {
            return await ordersService.getById(orderId);
          } catch (error) {
            console.error('Error getting order for recovery:', error);
            return null;
          }
        };

        // Helper to process payment
        const processPayment = async (orderId: string, payment: Payment) => {
          try {
            const order = await ordersService.getById(orderId);
            if (!order) {
              console.warn('Order not found for recovered payment:', orderId);
              return;
            }

            // Add payment to order
            const updatedPayments = [...order.payments, payment];
            const totalPaid = updatedPayments.reduce((sum, p) => sum + p.amount, 0);
            const paymentStatus = totalPaid >= order.totalAmount - 0.01 ? 'paid' : 'unpaid';

            await ordersService.update(orderId, {
              payments: updatedPayments,
              paymentStatus,
            });

            console.log('✅ Recovered payment processed', {
              orderId,
              amount: payment.amount,
              transactionId: payment.transactionId,
            });
          } catch (error) {
            console.error('Error processing recovered payment:', error);
          }
        };

        // Recover all pending transactions
        const results = await transactionRecoveryService.recoverAllPendingTransactions(
          (result) => {
            if (result.recovered && result.outcome === 1) {
              // TransactionOutcome.Accepted
              console.log('✅ Recovered accepted transaction', {
                transactionId: result.transactionId,
                orderId: result.orderId,
              });
            } else if (result.recovered) {
              console.log('ℹ️ Recovered transaction (not accepted)', {
                transactionId: result.transactionId,
                orderId: result.orderId,
                outcome: result.outcome,
              });
            }
          },
          getOrder,
          processPayment
        );

        if (results.length > 0) {
          const recovered = results.filter(r => r.recovered && r.outcome === 1).length;
          if (recovered > 0) {
            console.log(`✅ Recovered ${recovered} transaction(s) after app restart`);
            // Optionally show a notification to user
          }
        }
      } catch (error) {
        console.error('Error during transaction recovery:', error);
      }
    };

    // Run recovery after a short delay to ensure app is fully loaded
    const timeout = setTimeout(() => {
      recoverTransactions();
    }, 2000);

    return () => clearTimeout(timeout);
  }, [currentUser]);

  const isActiveTableOrder = useCallback((order: Order, tableId: number, _todayKey?: string) => {
    const businessTimeZone = integrationSettings?.businessHours?.timezone || 'Pacific/Auckland';
    return isFloorPlanActiveTableOrder(order, tableId, businessTimeZone);
  }, [integrationSettings?.businessHours?.timezone]);

  /** Filled after handleSplitTables is defined — payment/served/cancel paths call this to auto-unmerge. */
  const maybeAutoUnmergeMergedTablesRef = useRef<(order: Order) => Promise<void>>(async () => {});
  /** Filled after handleUpdateReservation — mark matching seated bookings completed when the visit ends. */
  const completeSeatedReservationsForOrderRef = useRef<(order: Order) => Promise<void>>(async () => {});

  // Update table status based on active orders — debounced; only write when status must change.
  // (Unthrottled writes on every listener tick caused millions of Firestore writes/day.)
  // Option A: seated reservations (no POS order yet) keep the table occupied; later-today
  // pending/confirmed bookings must NOT hold occupied (reserved paint is ±60m only).
  const tableSyncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tableOccupancySigRef = useRef<string>('');
  useEffect(() => {
    if (!staffDataEnabled) return;
    if (tablesLoading) return;
    if (tables.length === 0) return;

    const todayKey = new Date().toDateString();
    const todayISO = new Date().toISOString().split('T')[0];
    const occupancySig = tables
      .map((table) => {
        const isMerged = table.mergedWith && table.mergedWith.length > 1;
        const primaryId = table.mergedWith?.[0];
        const isPrimaryMerged = isMerged && primaryId != null && Number(primaryId) === Number(table.id);
        if (isMerged && !isPrimaryMerged) return '';
        const hasActiveOrder = orders.some((order) => isActiveTableOrder(order, table.id, todayKey));
        const hasSeated = tableHasSeatedReservation(reservations, table.id, todayISO);
        return `${table.id}:${hasActiveOrder ? 'occ' : 'free'}:${hasSeated ? 'seated' : 'noseat'}:${table.status}`;
      })
      .join('|');

    if (occupancySig === tableOccupancySigRef.current) return;
    tableOccupancySigRef.current = occupancySig;

    if (tableSyncTimerRef.current) clearTimeout(tableSyncTimerRef.current);
    tableSyncTimerRef.current = setTimeout(() => {
      const syncTableStatus = async () => {
        for (const table of tables) {
          const isMerged = table.mergedWith && table.mergedWith.length > 1;
          const primaryId = table.mergedWith?.[0];
          const isPrimaryMerged = isMerged && primaryId != null && Number(primaryId) === Number(table.id);
          if (isMerged && !isPrimaryMerged) continue;

          const hasActiveOrder = orders.some((order) => isActiveTableOrder(order, table.id, todayKey));
          const hasSeatedReservation = tableHasSeatedReservation(reservations, table.id, todayISO);

          if ((hasActiveOrder || hasSeatedReservation) && table.status !== 'occupied') {
            try {
              await updateTable(table.id, { status: 'occupied' });
            } catch (error) {
              console.error(`Failed to update table ${table.id} to occupied:`, error);
            }
          } else if (!hasActiveOrder && !hasSeatedReservation && table.status === 'occupied') {
            const justOccupied =
              lastOccupiedTableRef.current != null &&
              Number(lastOccupiedTableRef.current.tableId) === Number(table.id) &&
              Date.now() - lastOccupiedTableRef.current.at < 4000;
            if (justOccupied) continue;

            try {
              await updateTable(table.id, { status: 'available' });
            } catch (error) {
              console.error(`Failed to update table ${table.id} to available:`, error);
            }
          }
        }
      };
      void syncTableStatus();
    }, 2000);

    return () => {
      if (tableSyncTimerRef.current) clearTimeout(tableSyncTimerRef.current);
    };
  }, [staffDataEnabled, tablesLoading, orders, tables, updateTable, reservations, isActiveTableOrder]);

  // Sync table statuses with today's reservations — debounced; skip no-op status writes.
  // Amber reserved only in lead/grace window (60m before → 15m after), not all day.
  // Never overwrite occupied (active order or seated reservation) with reserved.
  // Re-evaluate every minute so amber turns on/off as the clock crosses the window.
  const [floorReservationTick, setFloorReservationTick] = useState(0);
  useEffect(() => {
    if (!staffDataEnabled) return;
    const id = window.setInterval(() => setFloorReservationTick((n) => n + 1), 60_000);
    return () => window.clearInterval(id);
  }, [staffDataEnabled]);

  const reservationTableTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reservationTableSigRef = useRef<string>('');
  useEffect(() => {
    if (!staffDataEnabled) return;
    const today = new Date().toISOString().split('T')[0];
    const now = new Date();

    const upcomingReservations = reservations.filter((r) => isReservationSoonForFloor(r, now));

    const sig = `${floorReservationTick}|` + tables
      .map((table) => {
        const isMerged = table.mergedWith && table.mergedWith.length > 1;
        const primaryId = table.mergedWith?.[0];
        const isPrimaryMerged = isMerged && primaryId != null && Number(primaryId) === Number(table.id);
        if (isMerged && !isPrimaryMerged) return '';
        const hasActiveOrder = orders.some((order) => isActiveTableOrder(order, table.id));
        const hasSeated = tableHasSeatedReservation(reservations, table.id, today);
        const hasSoonReservation = upcomingReservations.some((r) => reservationHoldsTable(r, table.id));
        const desired =
          hasActiveOrder || hasSeated
            ? 'occupied'
            : hasSoonReservation
              ? 'reserved'
              : table.status === 'reserved'
                ? 'available'
                : table.status;
        return `${table.id}:${desired}`;
      })
      .join('|');

    if (sig === reservationTableSigRef.current) return;
    reservationTableSigRef.current = sig;

    if (reservationTableTimerRef.current) clearTimeout(reservationTableTimerRef.current);
    reservationTableTimerRef.current = setTimeout(() => {
      tables.forEach((table) => {
        const isMerged = table.mergedWith && table.mergedWith.length > 1;
        const primaryId = table.mergedWith?.[0];
        const isPrimaryMerged = isMerged && primaryId != null && Number(primaryId) === Number(table.id);
        if (isMerged && !isPrimaryMerged) return;

        const hasActiveOrder = orders.some((order) => isActiveTableOrder(order, table.id));
        const hasSeated = tableHasSeatedReservation(reservations, table.id, today);
        if (hasActiveOrder || hasSeated) return;

        const hasSoonReservation = upcomingReservations.some((r) => reservationHoldsTable(r, table.id));

        if (hasSoonReservation && table.status === 'available') {
          void updateTable(table.id, { status: 'reserved' });
        } else if (!hasSoonReservation && table.status === 'reserved') {
          void updateTable(table.id, { status: 'available' });
        }
      });
    }, 2000);

    return () => {
      if (reservationTableTimerRef.current) clearTimeout(reservationTableTimerRef.current);
    };
  }, [staffDataEnabled, reservations, tables, orders, updateTable, isActiveTableOrder, floorReservationTick]);

  // Merge Firestore roles with initialUserRoles so roles added in code (e.g. Kitchen Hand) appear even if not yet in Firestore
  const mergedUserRoles = useMemo(() => {
    const fromFirestore = userRoles;
    const missing = initialUserRoles.filter((ir) => !fromFirestore.some((f) => f.id === ir.id));
    return [...fromFirestore, ...missing];
  }, [userRoles]);

  // Get permissions for a staff member based on their role
  const getStaffPermissions = useCallback((staff: StaffMember): Set<Permission> => {
    if (staff.isAdmin) {
      console.log('🔑 Admin user - granting all permissions');
      return new Set(ALL_PERMISSIONS);
    }
    
    const rolesToSearch = mergedUserRoles;
    
    // Try exact match first, then case-insensitive match
    let role = rolesToSearch.find(r => r.id === staff.role);
    if (!role && staff.role) {
      role = rolesToSearch.find(r => r.id.toLowerCase() === staff.role.toLowerCase());
    }
    const permissions = new Set(role?.permissions || []);
    console.log(`🔑 Getting permissions for role "${staff.role}":`, {
      roleFound: !!role,
      roleName: role?.name,
      permissionsCount: permissions.size,
      permissions: Array.from(permissions),
      availableRoles: rolesToSearch.map(r => r.id),
      mergedRolesCount: rolesToSearch.length
    });
    if (!role && staff.role) {
      console.warn(`⚠️ Role "${staff.role}" not found in userRoles. Available roles:`, rolesToSearch.map(r => r.id));
    }
    return permissions;
  }, [mergedUserRoles]);

  // Recalculate permissions when userRoles changes (important for when roles load after PIN login)
  useEffect(() => {
    const user = currentUserRef.current;
    // Recalculate if user exists and either userRoles loaded OR we have initialUserRoles as fallback
    if (user && (userRoles.length > 0 || initialUserRoles.length > 0)) {
      const newPermissions = getStaffPermissions(user);
      const currentPermissionsArray = Array.from(user.permissions);
      const newPermissionsArray = Array.from(newPermissions);
      
      // Always update if permissions are empty (userRoles just loaded) or if they've changed
      const permissionsChanged = currentPermissionsArray.length !== newPermissionsArray.length ||
          !currentPermissionsArray.every(p => newPermissionsArray.includes(p)) ||
          !newPermissionsArray.every(p => currentPermissionsArray.includes(p));
      
      if (permissionsChanged || currentPermissionsArray.length === 0) {
        console.log('🔄 UserRoles updated - recalculating permissions for current user:', {
          user: user.name,
          role: user.role,
          oldPermissionsCount: currentPermissionsArray.length,
          newPermissionsCount: newPermissionsArray.length,
          newPermissions: newPermissionsArray,
          wasEmpty: currentPermissionsArray.length === 0,
          userRolesCount: userRoles.length,
          usingFallback: userRoles.length === 0 && initialUserRoles.length > 0
        });
        setCurrentUser(prev => {
          if (!prev) return null;
          return { ...prev, permissions: newPermissions };
        });
      }
    }
  }, [userRoles, getStaffPermissions]); // Use ref to avoid dependency issues

  // Keep POS MNG MENU / MNG CUST gates in sync when staff docs update
  useEffect(() => {
    setCurrentUser((prev) => {
      if (!prev) return prev;
      const prevFid = (prev as { firestoreId?: string }).firestoreId;
      const match = staff.find(
        (s) =>
          s.id === prev.id ||
          (!!prevFid && (s as { firestoreId?: string }).firestoreId === prevFid),
      );
      if (!match) return prev;
      const nextMenu = match.canManageMenu === true;
      const nextCust = match.canManageCustomers === true;
      if (
        (prev.canManageMenu === true) === nextMenu &&
        (prev.canManageCustomers === true) === nextCust
      ) {
        return prev;
      }
      return {
        ...prev,
        canManageMenu: nextMenu,
        canManageCustomers: nextCust,
      };
    });
  }, [staff]);

  // Ref to store handleSignIn so auth listener can access it
  const handleSignInRef = useRef<((authUserId: string, staffData: any, options?: { waitstaffMobile?: boolean }) => Promise<void>) | null>(null);

  const handleSignIn = useCallback(async (authUserId: string, staffData: any, options?: { waitstaffMobile?: boolean }) => {
    // Don't log sensitive user data
    const { logger } = await import('./lib/logger');
    logger.auth('User authenticated successfully');
    
    // Convert snake_case from DB to camelCase (support legacy field names from older schemas)
    const user = {
      id: staffData.id,
      name: staffData.name,
      role: staffData.role,
      pin: staffData.pin || '0000',
      email: staffData.email,
      authUserId: staffData.authUserId || staffData.auth_user_id || authUserId,
      isAdmin: staffData.isAdmin !== undefined ? staffData.isAdmin : staffData.is_admin,
      emailVerified: staffData.emailVerified !== undefined ? staffData.emailVerified : staffData.email_verified,
      canManageMenu: staffData.canManageMenu === true,
      canManageCustomers: staffData.canManageCustomers === true,
      includeInRoster: staffData.includeInRoster,
      firestoreId: staffData.firestoreId,
    };
    
    const permissions = getStaffPermissions(user);
    setCurrentUser({ ...user, permissions });
    const asWaitstaffMobile =
      options?.waitstaffMobile === true ||
      (options?.waitstaffMobile !== false && options == null && readWaitstaffMobileSession());
    setIsPinBasedAccess(asWaitstaffMobile || options?.waitstaffMobile === true);
    setWaitstaffMobileMode(asWaitstaffMobile);
    writeWaitstaffMobileSession(asWaitstaffMobile);
    if (asWaitstaffMobile) {
      setActivePage('pos-mobile');
    } else {
      setActivePage(getStoredActivePage() ?? 'dashboard');
    }
    console.log('✅ Sign in complete - User:', user.name, 'Role:', user.role, 'Permissions count:', permissions.size);

  }, [getStaffPermissions]);

  // Update ref whenever handleSignIn changes
  useEffect(() => {
    handleSignInRef.current = handleSignIn;
  }, [handleSignIn]);

  // Handle PIN-based access for all staff (managers and non-managers)
  const handlePinVerified = useCallback((staff: StaffMember) => {
    const permissions = getStaffPermissions(staff);
    
    const currentUserData: CurrentUser = {
      ...staff,
      permissions,
    };
    
    setCurrentUser(currentUserData);
    setIsPinBasedAccess(true); // Mark as PIN-based access (even for managers using PIN)
    console.log('✅ PIN verified - User accessed:', currentUserData.name, 'Role:', currentUserData.role, 'Is Manager:', isManager(staff), 'Permissions count:', permissions.size, 'Permissions:', Array.from(permissions), 'UserRoles loaded:', userRoles.length > 0);
    
    // If permissions are empty but userRoles are loaded, there might be a role mismatch
    if (permissions.size === 0 && userRoles.length > 0 && !staff.isAdmin) {
      console.warn('⚠️ WARNING: No permissions assigned! Role:', staff.role, 'Available roles:', userRoles.map(r => r.id));
    }

    const action = pendingPinActionRef.current;
    if (action) {
      if (action.type === 'page') {
        setActivePage(action.page);
      } else if (action.type === 'register') {
        if (!isManager(staff)) {
          alert('Only managers can perform register operations.');
        } else {
          action.onApproved();
        }
      }
      setPendingPinAction(null);
    } else {
      setActivePage(getStoredActivePage() ?? 'dashboard');
    }

    setShowPinEntry(false);
  }, [getStaffPermissions, setActivePage, isManager]);

  const handleUpdateCurrentUser = useCallback((updates: Partial<CurrentUser>) => {
    setCurrentUser(prev => {
      if (!prev) return prev;
      // Don't log sensitive data like PIN
      console.log('Updating current user profile');
      return { ...prev, ...updates };
    });
  }, []);

  const handleLockScreenUnlock = useCallback((pin: string): boolean => {
    const staff = getStaffByPin(pin);
    if (!staff) {
      console.log('❌ Invalid PIN');
      return false;
    }

    const permissions = getStaffPermissions(staff);
    setCurrentUser({ ...staff, permissions });
    setIsPinBasedAccess(true);
    setIsStandby(false);
    setIsRegisterLocked(false);
    console.log('✅ Register unlocked by PIN:', staff.name, 'Role:', staff.role, 'Permissions count:', permissions.size);
    return true;
  }, [getStaffByPin, getStaffPermissions]);

  const requestPageAccess = useCallback((page: Page) => {
    if (!PIN_PROTECTED_PAGES.includes(page)) {
      setActivePage(page);
      return;
    }
    setPendingPinAction({ type: 'page', page });
    setShowPinEntry(true);
  }, []);

  const clearCouponCampaignInitialId = useCallback(() => {
    setCouponCampaignsInitialId(null);
  }, []);

  const requestManagerPin = useCallback((action: 'open' | 'close', onApproved: () => void) => {
    setPendingPinAction({
      type: 'register',
      action,
      onApproved
    });
    setShowPinEntry(true);
  }, []);

  const handlePinModalClose = useCallback(() => {
    setShowPinEntry(false);
    setPendingPinAction(null);
  }, []);

  const handleLogout = useCallback(async () => {
    console.log('🚪 Logging out...');
    const { authService } = await import('./lib/auth');
    await authService.signOut();
    setCurrentUser(null);
    setIsStandby(false);
    setIsRegisterLocked(false);
    setWaitstaffMobileMode(false);
    clearWaitstaffMobileSession();
    console.log('✅ Logged out successfully');
  }, []);

  const handleWaitstaffLock = useCallback(async () => {
    await handleLogout();
  }, [handleLogout]);

  // Lock register handler
  const handleLockRegister = useCallback(() => {
    setIsRegisterLocked(true);
    setIsStandby(true);
  }, []);

  // Unlock register handler (uses same PIN as login)
  const handleUnlockRegister = useCallback((pin: string): boolean => {
    if (currentUser) {
      // Verify PIN matches current user
      const user = getStaffByPin(pin);
      if (user && user.id === currentUser.id) {
        setIsRegisterLocked(false);
        setIsStandby(false);
        return true;
      }
    }
    return false;
  }, [currentUser, getStaffByPin]);

  // Inactivity timer - goes to standby after 1 minute of inactivity
  // Only active when user is logged in and register is not locked
  const { isActive, reset } = useInactivityTimer({
    timeout: 60000, // 1 minute
    onInactive: () => {
      console.log('⏱️ Inactivity detected - going to standby');
      if (currentUser && !isRegisterLocked) {
        setIsStandby(true);
      }
    },
    onActive: () => {
      // User became active again
      console.log('✅ Activity detected - resuming from standby');
      if (!isRegisterLocked) {
        setIsStandby(false);
      }
    },
    // Only track activity when user is logged in
    enabled: !!currentUser && !isRegisterLocked,
  });

  // Reset inactivity timer on user activity (when not on lock screen)
  useEffect(() => {
    if (currentUser && !isStandby && !isRegisterLocked) {
      reset();
    }
  }, [currentUser, isStandby, isRegisterLocked, reset]);

  const forceCloseSession = useCallback(async (session: RegisterSession, reason = 'Auto-closed before new session') => {
    try {
      console.warn('⚠️ Force closing session:', { sessionId: session.id, reason });
      // Only the register session document is updated. Orders are never modified or deleted.
      const closingFloat = session.closingFloat ?? session.openingFloat ?? 0;
      const variance = closingFloat - ((session.openingFloat ?? 0) + (session.cashSales ?? 0));
      await updateRegisterSession(session.id, {
        isActive: false,
        closingFloat,
        endTime: Date.now(),
        variance,
      });
      if (currentSession?.id === session.id) {
        setCurrentSession(null);
        writeCachedRegisterSession(null);
      }
      setCloseRegisterFailed(false);
      await refreshRegisterSessions();
    } catch (error) {
      console.error('❌ Failed to force close session:', error);
      alert('Failed to close the previous register session automatically. Please try again.');
    }
  }, [updateRegisterSession, refreshRegisterSessions, currentSession]);

  /** Add an audit log entry (fire-and-forget; never throws). Details stored as JSON in Firestore. */
  const addAuditLog = useCallback(
    (action: string, entityType?: string, entityId?: string, details?: Record<string, unknown>) => {
      const actor = currentUser;
      auditLogsService
        .add({
          timestamp: Date.now(),
          actorId: actor?.id,
          actorName: actor?.name,
          actorRole: actor?.role,
          action,
          entityType,
          entityId,
          details: details ?? {},
        })
        .catch((e) => console.error('Audit log failed', e));
    },
    [currentUser],
  );

  useEffect(() => {
    setAuditActor(currentUser ? { id: currentUser.id, name: currentUser.name, role: currentUser.role } : null);
  }, [currentUser]);

  // ── Live-data subscription health ────────────────────────────────────────
  // 1. When the tab returns from hidden (idle/throttled) or the network comes
  //    back online, force every reconnector to re-subscribe. Firestore's own
  //    persistence usually handles this, but we've seen long-running tabs sit
  //    on a dead onSnapshot until the user reloads. Belt-and-braces.
  // 2. Emit sampled audit events when feeds drop/reconnect so we can see in
  //    production how often this is happening. Sampled (5 min minimum gap per
  //    feed) so a flaky network can't spam the audit log.
  useEffect(() => {
    const handleRefresh = () => {
      // Only act when we're actually back; visibilitychange fires both on
      // 'visible' and 'hidden'. We don't care about 'hidden'.
      if (document.visibilityState !== 'visible') return;
      liveDataStatus.refreshUnhealthy();
      // Hung "connected" listeners: force preferServer refresh when last snapshot is stale.
      liveDataStatus.refreshStale(45_000);
    };
    document.addEventListener('visibilitychange', handleRefresh);
    window.addEventListener('online', handleRefresh);
    return () => {
      document.removeEventListener('visibilitychange', handleRefresh);
      window.removeEventListener('online', handleRefresh);
    };
  }, []);

  // Audit logging of subscription state transitions. We track the last logged
  // timestamp per (feed, transition) so we don't spam if a network is flapping.
  const subAuditSampleRef = useRef<Map<string, number>>(new Map());
  const subDisconnectedAtRef = useRef<Map<string, number>>(new Map());
  useEffect(() => {
    const SAMPLE_GAP_MS = 5 * 60_000; // 5 minutes
    const unsub = liveDataStatus.onStateChange((next: FeedStatus, prev: FeedStatus | null) => {
      // Disconnected = entering 'reconnecting' or 'offline' from a healthy state.
      const wasHealthy = !prev || prev.state === 'connected' || prev.state === 'connecting';
      const isDown = next.state === 'reconnecting' || next.state === 'offline';
      const isBack = next.state === 'connected' && prev && (prev.state === 'reconnecting' || prev.state === 'offline');

      if (wasHealthy && isDown) {
        subDisconnectedAtRef.current.set(next.name, Date.now());
        const key = `${next.name}:disconnected`;
        const lastLogged = subAuditSampleRef.current.get(key) ?? 0;
        if (Date.now() - lastLogged >= SAMPLE_GAP_MS) {
          subAuditSampleRef.current.set(key, Date.now());
          addAuditLog('subscription.disconnected', 'subscription', next.name, {
            feed: next.name,
            errorCode: next.errorCode,
            retryDelayMs: next.retryDelayMs,
            state: next.state,
          });
        }
      } else if (isBack) {
        const downAt = subDisconnectedAtRef.current.get(next.name);
        const downtimeMs = downAt ? Date.now() - downAt : undefined;
        subDisconnectedAtRef.current.delete(next.name);
        const key = `${next.name}:reconnected`;
        const lastLogged = subAuditSampleRef.current.get(key) ?? 0;
        if (Date.now() - lastLogged >= SAMPLE_GAP_MS) {
          subAuditSampleRef.current.set(key, Date.now());
          addAuditLog('subscription.reconnected', 'subscription', next.name, {
            feed: next.name,
            downtimeMs,
          });
        }
      }
    });
    return unsub;
  }, [addAuditLog]);

  const handlePlaceOrder = useCallback(async (items: OrderItem[], orderType: OrderType, details: { tableId?: number, tableName?: string, preferredOrderId?: string, customerId?: number, customerNumber?: number, customerName?: string, notes?: string, appliedReward?: LoyaltyReward, manualDiscount?: number, discountRequiresApproval?: boolean, discountApprovedBy?: number, discountApprovalReason?: string, discountApprovalTimestamp?: number, buffetType?: 'breakfast' | 'lunch' | 'dinner', buffetPackageId?: string, buffetStartTime?: number, buffetEndTime?: number, servingStaffId?: number, servingStaffName?: string, deliveryInfo?: { platform: 'Uber Eats' | 'DoorDash'; customerName: string }, couponCode?: string, couponCampaignId?: string, couponDiscountType?: 'percentage' | 'fixed', couponDiscountValue?: number, couponDiscountAmount?: number, couponAppliedByStaffId?: number, couponAppliedAt?: number, couponUnitLabel?: string, couponBuildingWing?: 'hotel_wing' | 'tower_wing', residentDiscountPercent?: number, residentDiscountAmount?: number, mealPlanBookingId?: string, mealPlanWeekId?: string, mealPlanPeriod?: 'breakfast' | 'lunch' | 'dinner', mealPlanEntitlementId?: string, mealPlanDiscountAmount?: number, mealPlanSale?: MealPlanSale, mealPlanSales?: MealPlanSale[], deliveryCharge?: number, eventFulfillmentType?: 'eat-now' | 'takeaway', pickupTime?: number, eventOperatorId?: string, eventOperatorName?: string, eventId?: string, eventNameSnapshot?: string, eventMenuSnapshotId?: string, eventCostSnapshot?: Record<string, number>, eventQueueToken?: number }): Promise<Order | void> => {
    // Check fire mode - queue orders if active. A meal-plan sale is not a kitchen ticket.
    if (fireModeActive && !details.mealPlanSale) {
      if (orderType === 'delivery' || orderType === 'takeaway' || orderType === 'uber-doordash') {
        // Queue delivery/takeaway orders
        const table = details.tableId != null
          ? tables.find(t => Number(t.id) === Number(details.tableId))
          : null;
        const sanitizedItems = items.map((item, i) => {
          const trimmedImageUrl =
            typeof item.imageUrl === 'string' && item.imageUrl.startsWith('data:image')
              ? ''
              : item.imageUrl;
          return {
            ...item,
            imageUrl: trimmedImageUrl,
            served: item.served ?? false,
            lineId: item.lineId ?? `line-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 9)}`,
          };
        });
        const itemsToSendQueue = sanitizedItems.filter((i) => !i.removed);

        const totalBeforeDiscount = itemsToSendQueue.reduce((sum, item) => sum + item.price * item.quantity, 0);
        const rewardDiscount = details.appliedReward ? details.appliedReward.discountValue : 0;
        const manualDiscount = details.manualDiscount || 0;
        const couponDiscount = details.couponDiscountAmount || 0;
        const residentDiscount = details.residentDiscountAmount || 0;
        const mealPlanDiscount = details.mealPlanDiscountAmount || 0;
        const totalAfterDiscountsBase = totalBeforeDiscount - rewardDiscount - manualDiscount - couponDiscount - residentDiscount - mealPlanDiscount;
        const totalDiscountAmount = rewardDiscount + manualDiscount + couponDiscount + residentDiscount + mealPlanDiscount;
        const holidayTzQueued =
          integrationSettings?.businessHours?.timezone?.trim() || 'Pacific/Auckland';
        
        // Create initial batch for queued orders
        const queuedInitialBatchId = `batch-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const queuedInitialBatch = {
          batchId: queuedInitialBatchId,
          timestamp: Date.now(),
          itemIndices: itemsToSendQueue.map((_, index) => index)
        };

        let orderData: Omit<Order, 'id'>;
        if (orderType === 'takeaway') {
          const holidayQueued = holidaySurchargeSnapshotFields({
            orderType: 'takeaway',
            baseInclGst: totalAfterDiscountsBase,
            config: integrationSettings?.holidaySurcharge,
            timeZone: holidayTzQueued,
          });
          const totalQueued = totalAfterDiscountsBase + holidayQueued.holidaySurchargeAmount;
          const taxAmount = totalQueued * (15 / 115);
          const subtotal = totalQueued - taxAmount;
          orderData = { 
            items: itemsToSendQueue,
            orderType: 'takeaway',
            status: 'queued',
            timestamp: Date.now(),
            paymentStatus: 'unpaid',
            payments: [],
            notes: details.notes,
            customerId: details.customerId,
            customerNumber: details.customerNumber,
            customerName: details.customerName,
            appliedReward: details.appliedReward,
            discountAmount: totalDiscountAmount,
            manualDiscount: details.manualDiscount,
            couponCode: details.couponCode,
            couponCampaignId: details.couponCampaignId,
            couponDiscountType: details.couponDiscountType,
            couponDiscountValue: details.couponDiscountValue,
            couponDiscountAmount: details.couponDiscountAmount,
            couponAppliedByStaffId: details.couponAppliedByStaffId,
            couponAppliedAt: details.couponAppliedAt,
            couponUnitLabel: details.couponUnitLabel,
            couponBuildingWing: details.couponBuildingWing,
            residentDiscountPercent: details.residentDiscountPercent,
            residentDiscountAmount: details.residentDiscountAmount,
            discountRequiresApproval: details.discountRequiresApproval,
            discountApprovedBy: details.discountApprovedBy,
            discountApprovalReason: details.discountApprovalReason,
            discountApprovalTimestamp: details.discountApprovalTimestamp,
            ...holidayQueued,
            subtotal,
            taxAmount,
            totalAmount: totalQueued,
            takeawayNumber: takeawayCounter,
            orderBatches: [queuedInitialBatch]
          };
          setTakeawayCounter(prev => prev + 1);
        } else if (orderType === 'uber-doordash') {
          const pct = integrationSettings?.uberDoordashPriceIncreasePercent ?? 0;
          const multiplier = 1 + pct / 100;
          const totalAfterIncrease = totalAfterDiscountsBase * multiplier;
          const taxAfterIncrease = totalAfterIncrease * (15 / 115);
          const subtotalAfterIncrease = totalAfterIncrease - taxAfterIncrease;
          orderData = {
            items: itemsToSendQueue,
            orderType: 'uber-doordash',
            status: 'queued',
            timestamp: Date.now(),
            paymentStatus: 'unpaid',
            payments: [],
            notes: details.notes,
            discountAmount: totalDiscountAmount,
            manualDiscount: details.manualDiscount,
            couponCode: details.couponCode,
            couponCampaignId: details.couponCampaignId,
            couponDiscountType: details.couponDiscountType,
            couponDiscountValue: details.couponDiscountValue,
            couponDiscountAmount: details.couponDiscountAmount,
            couponAppliedByStaffId: details.couponAppliedByStaffId,
            couponAppliedAt: details.couponAppliedAt,
            couponUnitLabel: details.couponUnitLabel,
            couponBuildingWing: details.couponBuildingWing,
            residentDiscountPercent: details.residentDiscountPercent,
            residentDiscountAmount: details.residentDiscountAmount,
            discountRequiresApproval: details.discountRequiresApproval,
            discountApprovedBy: details.discountApprovedBy,
            discountApprovalReason: details.discountApprovalReason,
            discountApprovalTimestamp: details.discountApprovalTimestamp,
            holidaySurchargeAmount: 0,
            holidaySurchargePercent: 0,
            subtotal: subtotalAfterIncrease,
            taxAmount: taxAfterIncrease,
            totalAmount: totalAfterIncrease,
            orderBatches: [queuedInitialBatch],
            deliveryInfo: details.deliveryInfo ? { platform: details.deliveryInfo.platform, customerName: details.deliveryInfo.customerName } : undefined,
          };
        } else {
          const deliveryChargeQueued = Math.max(0, Number(details.deliveryCharge) || 0);
          const baseDeliveryQueued = totalAfterDiscountsBase + deliveryChargeQueued;
          const holidayQueued = holidaySurchargeSnapshotFields({
            orderType: 'delivery',
            baseInclGst: baseDeliveryQueued,
            config: integrationSettings?.holidaySurcharge,
            timeZone: holidayTzQueued,
          });
          const totalDeliveryQueued = baseDeliveryQueued + holidayQueued.holidaySurchargeAmount;
          const taxDeliveryQueued = totalDeliveryQueued * (15 / 115);
          const subtotalDeliveryQueued = totalDeliveryQueued - taxDeliveryQueued;
          orderData = { 
            items: itemsToSendQueue,
            orderType: 'delivery',
            status: 'queued',
            timestamp: Date.now(),
            paymentStatus: 'unpaid',
            payments: [],
            notes: details.notes,
            customerId: details.customerId,
            customerNumber: details.customerNumber,
            customerName: details.customerName,
            appliedReward: details.appliedReward,
            discountAmount: totalDiscountAmount,
            manualDiscount: details.manualDiscount,
            couponCode: details.couponCode,
            couponCampaignId: details.couponCampaignId,
            couponDiscountType: details.couponDiscountType,
            couponDiscountValue: details.couponDiscountValue,
            couponDiscountAmount: details.couponDiscountAmount,
            couponAppliedByStaffId: details.couponAppliedByStaffId,
            couponAppliedAt: details.couponAppliedAt,
            couponUnitLabel: details.couponUnitLabel,
            couponBuildingWing: details.couponBuildingWing,
            residentDiscountPercent: details.residentDiscountPercent,
            residentDiscountAmount: details.residentDiscountAmount,
            discountRequiresApproval: details.discountRequiresApproval,
            discountApprovedBy: details.discountApprovedBy,
            discountApprovalReason: details.discountApprovalReason,
            discountApprovalTimestamp: details.discountApprovalTimestamp,
            ...holidayQueued,
            subtotal: subtotalDeliveryQueued,
            taxAmount: taxDeliveryQueued,
            totalAmount: totalDeliveryQueued,
            deliveryNumber: deliveryCounter,
            deliveryCharge: deliveryChargeQueued,
            orderBatches: [queuedInitialBatch]
          };
          setDeliveryCounter(prev => prev + 1);
        }
        
        if (currentSession) {
          orderData.sessionId = currentSession.id;
        }
        
        const queuedOrder = await createOrder(removeUndefined(orderData));
        alert('Order queued due to fire evacuation mode. It will be processed when fire mode is deactivated.');
        return queuedOrder;
      } else {
        // Block dine-in orders during fire mode
        alert('Cannot place orders during fire evacuation mode. Please wait until fire mode is deactivated.');
        return;
      }
    }

    const tableFromList = details.tableId != null
      ? tables.find(t => Number(t.id) === Number(details.tableId))
      : null;
    // Buffet/dine-in must not fail when local tables[] is briefly out of sync — use id + optional name.
    const table =
      tableFromList ??
      (details.tableId != null && (orderType === 'buffet' || orderType === 'dine-in')
        ? ({
            id: Number(details.tableId),
            name: details.tableName || `Table ${details.tableId}`,
            capacity: 0,
            status: 'available' as const,
          } as (typeof tables)[number])
        : null);
    const tableIdToMarkOccupied = table?.id ?? (orderType === 'dine-in' || orderType === 'buffet' ? details.tableId : undefined);
    // Strip large base64 images or other heavy fields from order items before persisting; ensure each line has a stable lineId for update-diff (NEW/removed) on reprints
    const sanitizedItems = items.map((item, i) => {
      const trimmedImageUrl =
        typeof item.imageUrl === 'string' && item.imageUrl.startsWith('data:image')
          ? ''
          : item.imageUrl;
      const price = Number(item.price);
      return {
        ...item,
        price: Number.isFinite(price) ? price : 0,
        imageUrl: trimmedImageUrl,
        served: item.served ?? false,
        lineId: item.lineId ?? `line-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 9)}`,
      };
    });
    // Items removed before send are not sent to kitchen – exclude them from the order entirely
    const itemsToSend = sanitizedItems.filter((i) => !i.removed);

    // Totals from line items (buffet package is price × covers; extras add on).
    let totalBeforeDiscount = itemsToSend.reduce((sum, item) => sum + item.price * item.quantity, 0);
    if (orderType === 'buffet') {
      const packageLine = itemsToSend.find((i) => i.isBuffetPackage);
      const packagePrice = Number(packageLine?.price);
      if (!packageLine || !Number.isFinite(packagePrice) || packagePrice <= 0) {
        alert('Buffet package price is missing. Add buffet packages in Menu Management.');
        return;
      }
    }
    
    const rewardDiscount = details.appliedReward ? details.appliedReward.discountValue : 0;
    const manualDiscount = details.manualDiscount || 0;
    const couponDiscount = details.couponDiscountAmount || 0;
    const residentDiscount = details.residentDiscountAmount || 0;
    const mealPlanDiscount = details.mealPlanDiscountAmount || 0;
    let totalAfterDiscounts = totalBeforeDiscount - rewardDiscount - manualDiscount - couponDiscount - residentDiscount - mealPlanDiscount;
    if (orderType === 'delivery') {
      const dc = Math.max(0, Number(details.deliveryCharge) || 0);
      totalAfterDiscounts += dc;
    }
    const totalDiscountAmount = rewardDiscount + manualDiscount + couponDiscount + residentDiscount + mealPlanDiscount;

    // Uber/DoorDash: apply configured price increase % to match platform sales price
    if (orderType === 'uber-doordash') {
      const pct = integrationSettings?.uberDoordashPriceIncreasePercent ?? 0;
      const multiplier = 1 + pct / 100;
      totalAfterDiscounts = totalAfterDiscounts * multiplier;
    }

    const holidaySnap = holidaySurchargeSnapshotFields({
      orderType,
      baseInclGst: totalAfterDiscounts,
      config: integrationSettings?.holidaySurcharge,
      timeZone: integrationSettings?.businessHours?.timezone?.trim() || 'Pacific/Auckland',
    });
    totalAfterDiscounts =
      Math.round((totalAfterDiscounts + holidaySnap.holidaySurchargeAmount) * 100) / 100;
    let taxAmount = totalAfterDiscounts * (15 / 115);
    let subtotal = totalAfterDiscounts - taxAmount;
    
    // Create initial order batch for tracking
    const initialBatchId = `batch-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const initialBatch = {
      batchId: initialBatchId,
      timestamp: Date.now(),
      itemIndices: itemsToSend.map((_, index) => index)
    };

    // Build a frozen snapshot of the initial send to kitchen so KDS base cards
    // can always show the original quantity, even after later running orders.
    // Buffet package covers are billing-only — never put them on kitchen snapshots.
    const isDiscountItemForSnapshot = (item: { price?: number; id?: unknown; isBuffetPackage?: boolean; isMealPlanSale?: boolean }) =>
      item.isBuffetPackage === true ||
      item.isMealPlanSale === true ||
      (typeof item.price === 'number' && item.price < 0) ||
      (typeof item.id === 'string' && item.id.includes('combo-discount'));
    const initialSnapshot = itemsToSend
      .filter((i) => !isDiscountItemForSnapshot(i))
      .map((i, idx) => ({
        lineId: i.lineId ?? `pos-${idx}-${i.id}-${i.selectedMeatOption ?? ''}`,
        name: i.name,
        quantity: i.quantity,
        notes: i.notes,
        selectedMeatOption: i.selectedMeatOption,
        ...liveFishSnapshotFields(i),
      }));

    let baseOrder: Omit<Order, 'id' | 'orderType'> = {
        items: itemsToSend,
        status: 'new',
        timestamp: Date.now(),
        paymentStatus: 'unpaid',
        payments: [],
        notes: details.notes,
        pickupTime: details.pickupTime,
        customerId: details.customerId,
        appliedReward: details.appliedReward,
        discountAmount: totalDiscountAmount,
        manualDiscount: details.manualDiscount,
        couponCode: details.couponCode,
        couponCampaignId: details.couponCampaignId,
        couponDiscountType: details.couponDiscountType,
        couponDiscountValue: details.couponDiscountValue,
        couponDiscountAmount: details.couponDiscountAmount,
        couponAppliedByStaffId: details.couponAppliedByStaffId,
        couponAppliedAt: details.couponAppliedAt,
        couponUnitLabel: details.couponUnitLabel,
        couponBuildingWing: details.couponBuildingWing,
        residentDiscountPercent: details.residentDiscountPercent,
        residentDiscountAmount: details.residentDiscountAmount,
        mealPlanBookingId: details.mealPlanBookingId,
        mealPlanWeekId: details.mealPlanWeekId,
        mealPlanPeriod: details.mealPlanPeriod,
        mealPlanEntitlementId: details.mealPlanEntitlementId,
        mealPlanDiscountAmount: details.mealPlanDiscountAmount,
        discountRequiresApproval: details.discountRequiresApproval,
        discountApprovedBy: details.discountApprovedBy,
        discountApprovalReason: details.discountApprovalReason,
        discountApprovalTimestamp: details.discountApprovalTimestamp,
        eventOperatorId: details.eventOperatorId,
        eventOperatorName: details.eventOperatorName,
        eventId: details.eventId,
        eventNameSnapshot: details.eventNameSnapshot,
        eventMenuSnapshotId: details.eventMenuSnapshotId,
        eventCostSnapshot: details.eventCostSnapshot,
        ...holidaySnap,
        subtotal,
        taxAmount,
        totalAmount: totalAfterDiscounts,
        orderBatches: [initialBatch],
        initialKitchenSnapshot: initialSnapshot,
        ...(details.mealPlanSale
          ? {
              mealPlanSale: details.mealPlanSale,
              ...(details.mealPlanSales && details.mealPlanSales.length > 1 ? { mealPlanSales: details.mealPlanSales } : {}),
              status: 'served' as const,
              servedAt: Date.now(),
              hideFromKitchen: true,
              items: itemsToSend.map((item) =>
                item.isMealPlanSale
                  ? { ...item, status: 'completed' as const, quantityCompleted: item.quantity }
                  : item,
              ),
            }
          : {}),
    };

    let orderData: Omit<Order, 'id'>;
    if (orderType === 'takeaway') {
        orderData = { ...baseOrder, orderType: 'takeaway', takeawayNumber: takeawayCounter, customerName: details.customerName };
        setTakeawayCounter(prev => prev + 1);
    } else if (orderType === 'event') {
        orderData = {
          ...baseOrder,
          orderType: 'event',
          customerName: details.customerName,
          eventFulfillmentType: details.eventFulfillmentType ?? 'eat-now',
          ...(typeof details.eventQueueToken === 'number' &&
          Number.isFinite(details.eventQueueToken) &&
          details.eventQueueToken > 0
            ? { eventQueueToken: Math.floor(details.eventQueueToken) }
            : {}),
        };
    } else if (orderType === 'delivery') {
        const deliveryChargePlaced = Math.max(0, Number(details.deliveryCharge) || 0);
        orderData = {
          ...baseOrder,
          orderType: 'delivery',
          deliveryNumber: deliveryCounter,
          customerName: details.customerName,
          deliveryCharge: deliveryChargePlaced,
        };
        setDeliveryCounter(prev => prev + 1);
    } else if (orderType === 'uber-doordash') {
        if (!details.deliveryInfo) {
          alert('Uber/DoorDash order requires platform and customer name.');
          return;
        }
        orderData = {
          ...baseOrder,
          orderType: 'uber-doordash',
          deliveryInfo: { platform: details.deliveryInfo.platform, customerName: details.deliveryInfo.customerName },
        };
    } else if (orderType === 'buffet') {
        if (!table) { alert("Error: Table not found for buffet order."); return; }
        if (!details.buffetType) { alert("Error: Buffet type not specified."); return; }
        if (!details.buffetStartTime || !details.buffetEndTime) { alert("Error: Buffet time not specified."); return; }
        
        const buffetReadyAt = Date.now();
        const buffetItems = itemsToSend.map((item) =>
          item.isBuffetPackage
            ? {
                ...item,
                status: 'completed' as const,
                quantityCompleted: item.quantity,
              }
            : {
                ...item,
                status: item.status ?? ('pending' as const),
                served: false,
              },
        );
        const hasKitchenExtras = buffetItems.some(
          (item) => !item.isBuffetPackage && item.price >= 0 && !String(item.id).includes('combo-discount'),
        );
        orderData = {
          ...baseOrder,
          items: buffetItems,
          orderType: 'buffet',
          // Package-only → ready; extras → kitchen will drive status
          status: hasKitchenExtras ? 'new' : 'ready',
          readyAt: hasKitchenExtras ? undefined : buffetReadyAt,
          tableId: table.id,
          tableNumber: table.id,
          tableName: table.name,
          customerNumber: details.customerNumber,
          customerName: details.customerName,
          buffetType: details.buffetType,
          buffetPackageId: details.buffetPackageId,
          buffetStartTime: details.buffetStartTime,
          buffetEndTime: details.buffetEndTime,
          ...((details.servingStaffId != null && details.servingStaffName) ? { servingStaffId: details.servingStaffId, servingStaffName: details.servingStaffName } : (currentUser && { servingStaffId: currentUser.id, servingStaffName: currentUser.name })),
        };
    } else {
        if (!table) { alert("Error: Table not found for dine-in order."); return; }
        orderData = { 
          ...baseOrder, 
          orderType: 'dine-in', 
          tableId: table.id, 
          tableNumber: table.id,
          tableName: table.name,
          customerNumber: details.customerNumber,
          customerName: details.customerName,
          ...((details.servingStaffId != null && details.servingStaffName) ? { servingStaffId: details.servingStaffId, servingStaffName: details.servingStaffName } : (currentUser && { servingStaffId: currentUser.id, servingStaffName: currentUser.name })),
        };
    }
    // Loyalty (best-effort) — never block order create on customer write failures.
    let pointsEarned = 0;
    let pointsRedeemed = 0;
    if (details.customerId) {
      pointsEarned = Math.floor(totalAfterDiscounts);
      const customer = customers.find(c => c.id === details.customerId);
      if (customer) {
        let newPoints = customer.loyaltyPoints + pointsEarned;
        if (details.appliedReward) {
          pointsRedeemed = details.appliedReward.pointsRequired;
          newPoints -= pointsRedeemed;
        }
        try {
          await updateCustomer(details.customerId, { loyaltyPoints: Math.max(0, newPoints) });
        } catch (e) {
          console.warn('Loyalty points update failed (order still placing):', e);
        }
      }
    }
    if (pointsEarned > 0 || pointsRedeemed > 0) {
      orderData.pointsEarned = pointsEarned;
      orderData.pointsRedeemed = pointsRedeemed;
    }

    // Create the order first. A failed pre-create updateTable used to abort buffet/dine-in
    // entirely — covers confirmed but no package line / payment UI appeared.
    try {
      if (currentSession) {
        orderData.sessionId = currentSession.id;
      }

      const newOrder = await createOrder(removeUndefined(orderData), {
        preferredId: details.preferredOrderId,
      });
      const placedItems = formatAuditItemList((newOrder.items ?? []).filter((item) => !item.removed));
      addAuditLog('order.placed', 'order', newOrder.id, {
        orderType: newOrder.orderType,
        tableId: newOrder.tableId,
        tableName: newOrder.tableName,
        itemCount: newOrder.items?.length ?? 0,
        totalAmount: newOrder.totalAmount,
        status: newOrder.status,
        summary: placedItems ? `Order placed · ${placedItems}` : 'Order placed',
      });

      if (details.mealPlanBookingId) {
        try {
          const { redeemWeeklyBookingOnPos } = await import('./lib/mealPlanRedeem');
          await redeemWeeklyBookingOnPos({
            bookingId: details.mealPlanBookingId,
            orderId: String(newOrder.id),
            staffId: currentUser?.id,
          });
        } catch (e) {
          console.warn('Meal plan booking redeem after place failed:', e);
          alert(
            e instanceof Error
              ? `Order placed but meal plan booking could not be redeemed: ${e.message}`
              : 'Order placed but meal plan booking could not be redeemed.',
          );
        }
      } else if (details.mealPlanEntitlementId && (details.mealPlanDiscountAmount || 0) > 0) {
        // Legacy pack path (no longer used by till UI)
        console.warn('Legacy meal plan entitlement redeem skipped — use weekly bookings.');
      }

      const orderTableId = newOrder.tableId != null ? Number(newOrder.tableId) : undefined;
      const tableIdToOccupy =
        orderTableId ??
        (tableIdToMarkOccupied != null ? Number(tableIdToMarkOccupied) : undefined);
      if (
        tableIdToOccupy != null &&
        Number.isFinite(tableIdToOccupy) &&
        (newOrder.orderType === 'dine-in' || newOrder.orderType === 'buffet')
      ) {
        lastOccupiedTableRef.current = { tableId: tableIdToOccupy, at: Date.now() };
        try {
          await updateTable(tableIdToOccupy, { status: 'occupied' });
        } catch (e) {
          console.warn('Post-place table status update failed:', e);
        }
      }
      return newOrder;
    } catch (error) {
      console.error('Failed to create order:', error);
      alert('Failed to create order. Please try again.');
      return;
    }
  }, [tables, takeawayCounter, deliveryCounter, viewMode, currentSession, currentUser, createOrder, updateOrder, customers, updateCustomer, updateTable, addAuditLog, fireModeActive, integrationSettings]);

  const handleUpdateOrder = useCallback(async (updatedOrder: Order) => {
    const originalOrder = orders.find(o => o.id === updatedOrder.id);
    const auditBeforeItems = (originalOrder?.items ?? []).map((item) => ({
      lineId: item.lineId,
      id: item.id,
      name: item.name,
      quantity: item.quantity,
      notes: item.notes,
      removed: item.removed === true,
      selectedMeatOption: item.selectedMeatOption,
    }));
    // Capture the POS mutation payload before merge — merge is only for kitchen snapshot recovery.
    const client = updatedOrder;
    const clientItems = client.items;
    const clientStatus = client.status;

    updatedOrder = normalizeOrderRecord(
      originalOrder ? mergeOrderPreferComplete(originalOrder, client) : client,
      menuItems,
    );

    // POS payload is authoritative for business fields. mergeOrderPreferComplete exists to
    // repair truncated items/snapshots — it must not drop discounts, cancel, paid, or served.
    updatedOrder = {
      ...updatedOrder,
      items: Array.isArray(clientItems) ? clientItems : updatedOrder.items,
      status: clientStatus ?? updatedOrder.status,
      paymentStatus: client.paymentStatus ?? updatedOrder.paymentStatus,
      payments: client.payments ?? updatedOrder.payments,
      subBills: client.subBills ?? updatedOrder.subBills,
      notes: client.notes,
      hideFromKitchen: client.hideFromKitchen ?? updatedOrder.hideFromKitchen,
      appliedReward: client.appliedReward,
      manualDiscount: client.manualDiscount,
      discountMethod: client.discountMethod,
      firstTableDiscountAmount: client.firstTableDiscountAmount,
      discountRequiresApproval: client.discountRequiresApproval,
      discountApprovedBy: client.discountApprovedBy,
      discountApprovalReason: client.discountApprovalReason,
      discountApprovalTimestamp: client.discountApprovalTimestamp,
      discountAmount: client.discountAmount,
      couponCode: client.couponCode,
      couponCampaignId: client.couponCampaignId,
      couponDiscountType: client.couponDiscountType,
      couponDiscountValue: client.couponDiscountValue,
      couponDiscountAmount: client.couponDiscountAmount,
      couponUnitLabel: client.couponUnitLabel,
      couponBuildingWing: client.couponBuildingWing,
      residentDiscountPercent: client.residentDiscountPercent,
      residentDiscountAmount: client.residentDiscountAmount,
      customerId: client.customerId ?? updatedOrder.customerId,
      customerNumber: client.customerNumber ?? updatedOrder.customerNumber,
      customerName: client.customerName ?? updatedOrder.customerName,
      servingStaffId: client.servingStaffId ?? updatedOrder.servingStaffId,
      servingStaffName: client.servingStaffName ?? updatedOrder.servingStaffName,
      ...((client as { cancelledAt?: number }).cancelledAt != null
        ? { cancelledAt: (client as { cancelledAt?: number }).cancelledAt }
        : {}),
      ...((client as { cancellationReason?: string }).cancellationReason != null
        ? { cancellationReason: (client as { cancellationReason?: string }).cancellationReason }
        : {}),
      ...((client as { cancelledBy?: number }).cancelledBy != null
        ? { cancelledBy: (client as { cancelledBy?: number }).cancelledBy }
        : {}),
    } as Order;

    // Safety check: ensure items array exists
    if (!updatedOrder.items || !Array.isArray(updatedOrder.items)) {
      console.error('❌ Order update failed: items array is missing or invalid', {
        orderId: updatedOrder.id,
        items: updatedOrder.items
      });
      // If items is missing, try to get it from the original order
      const fallbackOrder = orders.find(o => o.id === updatedOrder.id);
      if (fallbackOrder && fallbackOrder.items) {
        updatedOrder.items = fallbackOrder.items;
      } else {
        // If we can't find items, set to empty array to prevent errors
        updatedOrder.items = [];
      }
    }

    // When items are added to an existing order, ensure status allows it to show in kitchen display
    // Check if there are any new pending items (items that weren't in the original order)
    let orderStatus = updatedOrder.status;
    
    // Ensure all items have proper status, served flags, and lineIds (for update-diff on reprints); preserve removed
    const processedItems = updatedOrder.items.map((item, i) => {
      let next = { ...item };
      if (!next.status) next = { ...next, status: 'pending' as const, served: false };
      if (next.served === undefined) next = { ...next, served: false };
      if (!next.lineId) next = { ...next, lineId: `line-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 9)}` };
      return next;
    });

    // Authoritative totals (includes manual / First Table / coupon / resident / reward / holiday surcharge)
    const orderBeforeHoliday: Order = {
      ...updatedOrder,
      items: processedItems,
      holidaySurchargeAmount: 0,
      holidaySurchargePercent: 0,
    };
    const baseWithoutHoliday = getOrderTotalFromItems(orderBeforeHoliday);
    const holidaySnapUpdate = holidaySurchargeSnapshotFields({
      orderType: updatedOrder.orderType,
      baseInclGst: baseWithoutHoliday,
      config: integrationSettings?.holidaySurcharge,
      timeZone: integrationSettings?.businessHours?.timezone?.trim() || 'Pacific/Auckland',
    });
    const orderForTotals: Order = { ...updatedOrder, items: processedItems, ...holidaySnapUpdate };
    const { subtotal, taxAmount, totalAmount: totalAfterDiscounts } = getOrderTotals(orderForTotals);
    const totalDiscountAmount =
      (updatedOrder.appliedReward?.discountValue ?? 0) +
      (updatedOrder.discountMethod === 'first-table'
        ? (updatedOrder.firstTableDiscountAmount ?? 0)
        : (updatedOrder.manualDiscount ?? 0)) +
      (updatedOrder.couponDiscountAmount ?? 0) +
      (updatedOrder.residentDiscountAmount ?? 0);
    
    // Track order batches for new items
    let updatedBatches = updatedOrder.orderBatches || [];
    let deltaIndices: number[] = [];

    if (originalOrder) {
      // Check if there are new items (items in updatedOrder that aren't in originalOrder)
      const originalItemKeys = new Set(
        originalOrder.items.map((item, idx) => {
          // Create a unique key that includes index to handle duplicates
          return `${item.id}-${item.selectedMeatOption || ''}-${idx}`;
        })
      );
      
      // Find new item indices (new lines only)
      const newItemIndices: number[] = [];
      processedItems.forEach((item, newIdx) => {
        const originalItem = originalOrder.items[newIdx];
        if (!originalItem ||
            originalItem.id !== item.id ||
            originalItem.selectedMeatOption !== item.selectedMeatOption ||
            originalItem.quantity !== item.quantity) {
          const itemKey = `${item.id}-${item.selectedMeatOption || ''}-${newIdx}`;
          if (!originalItemKeys.has(itemKey)) {
            const existsInOriginal = originalOrder.items.some(origItem =>
              origItem.id === item.id &&
              origItem.selectedMeatOption === item.selectedMeatOption
            );
            if (!existsInOriginal || processedItems.length > originalOrder.items.length) {
              newItemIndices.push(newIdx);
            }
          }
        }
      });

      if (processedItems.length > originalOrder.items.length) {
        for (let i = originalOrder.items.length; i < processedItems.length; i++) {
          if (!newItemIndices.includes(i)) newItemIndices.push(i);
        }
      }

      // Only include indices where quantity *increased* (or new lines). Decreases/removals amend the main card only.
      const quantityIncreaseIndices: number[] = [];
      const batchDeltas: { index: number; deltaQty: number }[] = [];
      processedItems.forEach((item, newIdx) => {
        if (item.removed) return;
        const origSameIndex = originalOrder.items[newIdx];
        let origQty: number | undefined;
        if (origSameIndex && origSameIndex.id === item.id && origSameIndex.selectedMeatOption === item.selectedMeatOption) {
          origQty = origSameIndex.removed ? 0 : origSameIndex.quantity;
        } else {
          const matchByLine = originalOrder.items.find(
            (o) =>
              (o.lineId && o.lineId === item.lineId) ||
              (o.id === item.id && o.selectedMeatOption === item.selectedMeatOption)
          );
          if (matchByLine) {
            origQty = matchByLine.removed ? 0 : matchByLine.quantity;
          }
        }

        // Brand new line (not in original) – already captured in newItemIndices
        if (origQty == null) return;

        if (item.quantity !== origQty) {
          const deltaQty = item.quantity - origQty;
          if (deltaQty > 0) {
            quantityIncreaseIndices.push(newIdx);
            batchDeltas.push({ index: newIdx, deltaQty });
          }
        }
      });

      // For brand new lines, the entire quantity is the delta for this batch
      newItemIndices.forEach((idx) => {
        const item = processedItems[idx];
        if (!item) return;
        const existing = batchDeltas.find((d) => d.index === idx);
        if (!existing) {
          batchDeltas.push({ index: idx, deltaQty: item.quantity });
        }
      });

      deltaIndices = [...new Set([...newItemIndices, ...quantityIncreaseIndices])];
      const hasNewItems = deltaIndices.length > 0 || processedItems.length !== originalOrder.items.length;

      if (deltaIndices.length > 0) {
        const newBatchId = `batch-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const newBatch = {
          batchId: newBatchId,
          timestamp: Date.now(),
          itemIndices: deltaIndices,
          deltas: batchDeltas
        };
        updatedBatches = [...(originalOrder.orderBatches || []), newBatch];
      } else {
        updatedBatches = originalOrder.orderBatches || [];
      }
      
      // If there are new items or quantity changes, ensure order status allows visibility
      // Exception: takeaway/uber-doordash marked as served (picked up) must stay served
      // Exception: cancel/refund/comp must never be reset back to new/preparing
      const isPickupTypeServed = (updatedOrder.orderType === 'takeaway' || updatedOrder.orderType === 'uber-doordash') && updatedOrder.status === 'served';
      const isTerminalStatus =
        updatedOrder.status === 'cancelled' ||
        updatedOrder.status === 'refunded' ||
        updatedOrder.status === 'comped';
      if (hasNewItems && !isPickupTypeServed && !isTerminalStatus) {
        // If order was 'ready' or 'served', reset to 'new' so new items appear in kitchen display
        if (updatedOrder.status === 'ready' || updatedOrder.status === 'served') {
          orderStatus = 'new';
        } else if (updatedOrder.status === 'preparing') {
          // Keep as 'preparing' if already preparing
          orderStatus = 'preparing';
        } else if (updatedOrder.status !== 'new') {
          // Default to 'new' for new items if status is not already new/preparing
          orderStatus = 'new';
        }
      }
    } else if (updatedOrder.status === 'ready' || updatedOrder.status === 'served') {
      // If we can't find original order, reset to 'new' if status was ready/served
      // Exception: takeaway/uber-doordash marked as served (picked up) must stay served
      const isPickupTypeServed = (updatedOrder.orderType === 'takeaway' || updatedOrder.orderType === 'uber-doordash') && updatedOrder.status === 'served';
      if (!isPickupTypeServed) orderStatus = 'new';
      // If no original order and no batches exist, create initial batch
      if (!updatedBatches || updatedBatches.length === 0) {
        const initialBatchId = `batch-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        updatedBatches = [{
          batchId: initialBatchId,
          timestamp: Date.now(),
          itemIndices: processedItems.map((_, idx) => idx)
        }];
      }
    }

    // For Uber/DoorDash, preserve stored totals (they include platform %); otherwise use recalculated from items
    const useStoredTotals = updatedOrder.orderType === 'uber-doordash' &&
      updatedOrder.totalAmount != null &&
      updatedOrder.subtotal != null &&
      updatedOrder.taxAmount != null &&
      !Number.isNaN(updatedOrder.totalAmount) &&
      updatedOrder.totalAmount >= 0;

    // Build final order object, only including discountAmount if it has a value
    const finalOrder: Order = { 
      ...updatedOrder, 
      items: processedItems,
      ...(useStoredTotals
        ? {}
        : {
            holidaySurchargeAmount: holidaySnapUpdate.holidaySurchargeAmount,
            holidaySurchargePercent: holidaySnapUpdate.holidaySurchargePercent,
          }),
      subtotal: useStoredTotals ? updatedOrder.subtotal! : subtotal, 
      taxAmount: useStoredTotals ? updatedOrder.taxAmount! : taxAmount, 
      totalAmount: useStoredTotals ? updatedOrder.totalAmount! : totalAfterDiscounts, 
      status:
        clientStatus === 'cancelled' || clientStatus === 'refunded' || clientStatus === 'comped'
          ? clientStatus
          : orderStatus,
      orderBatches: updatedBatches
    };
    // lastSentItemSnapshot = "what kitchen had at last send". When we only have decreases/removals we
    // update it to current items so the main card shows amended qty and "(was X, Y removed)" without a running card.
    const isDiscountItem = (item: { price?: number; id?: unknown; isBuffetPackage?: boolean; isMealPlanSale?: boolean }) =>
      item.isBuffetPackage === true ||
      item.isMealPlanSale === true ||
      (typeof item.price === 'number' && item.price < 0) ||
      (typeof item.id === 'string' && item.id.includes('combo-discount'));
    const existingSnapshot = updatedOrder.lastSentItemSnapshot ?? originalOrder?.lastSentItemSnapshot;
    const qtyGrew =
      !!originalOrder &&
      orderMeaningfulItemQty({ ...finalOrder, items: processedItems }) >
        orderMeaningfulItemQty(originalOrder) + 0.001;
    const linesGrew =
      !!originalOrder &&
      processedItems.filter((i) => !i.removed).length >
        (originalOrder.items ?? []).filter((i) => !i.removed).length;
    // Only treat as decrease/removal when the cart actually shrank (qty cut / soft-remove).
    // A no-op Update (e.g. after KDS undo with unchanged items) must not enter this path.
    const onlyDecreasesOrRemovals =
      !!originalOrder &&
      deltaIndices.length === 0 &&
      (existingSnapshot?.length ?? 0) > 0 &&
      !qtyGrew &&
      !linesGrew &&
      isIntentionalOrderAmendment(originalOrder, {
        ...finalOrder,
        items: processedItems,
      });
    if (onlyDecreasesOrRemovals) {
      // Align lastSent with the post-cut cart so the next same-line add can diff.
      // POS already froze the pre-cut cart as kitchenPrintBaseline → lastKitchenPrintOrder
      // for the REMOVED ticket; persisting pre-cut lastSent here made reconcile/print think
      // the following re-add had nothing new (KDS still got a batch from App deltas).
      finalOrder.lastSentItemSnapshot = processedItems
        .filter((i) => !i.removed && !isDiscountItem(i))
        .map((i, idx) => ({
          lineId: i.lineId ?? `pos-${idx}-${i.id}-${i.selectedMeatOption ?? ''}`,
          name: i.name,
          quantity: i.quantity,
          notes: i.notes,
          selectedMeatOption: i.selectedMeatOption,
          ...liveFishSnapshotFields(i),
        }));
      // Cap unfinished running-order batch deltas to the remaining qty above baseline
      // so KDS does not keep showing a stale "+4" after POS reduces the line.
      finalOrder.orderBatches = capRunningOrderBatchDeltasToItems(
        {
          initialKitchenSnapshot:
            finalOrder.initialKitchenSnapshot ?? originalOrder.initialKitchenSnapshot,
          lastSentItemSnapshot: existingSnapshot,
        },
        processedItems,
        updatedBatches,
      );
    } else if (originalOrder) {
      // For additions (new items / increased quantities), snapshot = kitchen items BEFORE this update
      // so kitchen prints and running cards show only the delta vs last send.
      finalOrder.lastSentItemSnapshot = originalOrder.items
        .filter((i) => !i.removed && !isDiscountItem(i))
        .map((i, idx) => ({
          lineId: i.lineId ?? `pos-${idx}-${i.id}-${i.selectedMeatOption ?? ''}`,
          name: i.name,
          quantity: i.quantity,
          notes: i.notes,
          selectedMeatOption: i.selectedMeatOption,
          ...liveFishSnapshotFields(i),
        }));
    } else if (existingSnapshot && existingSnapshot.length > 0) {
      finalOrder.lastSentItemSnapshot = existingSnapshot;
    }
    
    // Only add discountAmount if it has a value (don't set undefined)
    if (totalDiscountAmount && totalDiscountAmount > 0) {
      finalOrder.discountAmount = totalDiscountAmount;
    }
    try {
      console.log(`🔄 Updating order ${finalOrder.id}:`, {
        orderId: finalOrder.id,
        status: finalOrder.status,
        itemsCount: finalOrder.items.length,
        items: finalOrder.items.map(item => ({
          id: item.id,
          name: item.name,
          status: item.status,
          served: item.served,
          quantity: item.quantity,
          price: item.price,
          hasStatus: !!item.status,
          hasServed: item.served !== undefined
        }))
      });
      
      // Pass the ENTIRE order object, not just updates, to ensure items array is included
      // The update function will extract what it needs, but we want to ensure items are passed
      await updateOrder(finalOrder.id, finalizeOrderDocumentForWrite(normalizeOrderRecord(finalOrder, menuItems)));
      if (originalOrder && originalOrder.status !== finalOrder.status) {
        addAuditLog('order.status_change', 'order', finalOrder.id, {
          from: originalOrder.status,
          to: finalOrder.status,
          summary: `Status ${originalOrder.status} → ${finalOrder.status}`,
        });
      }
      if (finalOrder.status === 'cancelled') {
        addAuditLog('order.cancelled', 'order', finalOrder.id, {
          previousStatus: originalOrder?.status,
          summary: 'Order cancelled',
        });
      }
      if (originalOrder) {
        const edits = orderEditSummaries(auditBeforeItems, finalOrder.items ?? []);
        if (edits.removed.length) {
          addAuditLog('order.line_removed', 'order', finalOrder.id, {
            summary: edits.removed.join('; '),
          });
        }
        if (edits.changed.length) {
          addAuditLog('order.updated', 'order', finalOrder.id, {
            summary: edits.changed.join('; '),
          });
        }
      }
      // Handle cancellation - release table and ensure it doesn't block register closing
      if (finalOrder.status === 'cancelled') {
        console.log(`🚫 Order ${finalOrder.id} cancelled, releasing table if needed...`);
        
        // Release table if dine-in / buffet order
        if ((finalOrder.orderType === 'dine-in' || finalOrder.orderType === 'buffet') && finalOrder.tableId != null) {
          try {
            const tableId = Number(finalOrder.tableId);
            const otherActiveOrders = orders.filter(
              (o) => o.id !== finalOrder.id && isActiveTableOrder(o, tableId),
            );

            if (otherActiveOrders.length === 0) {
              await updateTable(tableId, { status: 'available' });
              console.log(`✅ Table ${tableId} released after order cancellation`);
            } else {
              console.log(`⚠️ Table ${tableId} still has ${otherActiveOrders.length} active order(s), keeping as occupied`);
            }
            try {
              await maybeAutoUnmergeMergedTablesRef.current({
                ...finalOrder,
                status: 'cancelled',
              });
            } catch (unmergeErr) {
              console.error('❌ Auto-unmerge after cancellation failed:', unmergeErr);
            }
          } catch (tableError) {
            console.error('❌ Failed to release table after cancellation:', tableError);
            // Don't fail the cancellation if table update fails
          }
        }
      }

      // Buffet end / served: unmerge once the session is no longer floor-active
      if (
        finalOrder.status === 'served' &&
        originalOrder?.status !== 'served' &&
        (finalOrder.orderType === 'dine-in' || finalOrder.orderType === 'buffet') &&
        finalOrder.tableId != null
      ) {
        try {
          await completeSeatedReservationsForOrderRef.current(finalOrder);
        } catch (resErr) {
          console.error('❌ Auto-complete reservation after served failed:', resErr);
        }
        try {
          await maybeAutoUnmergeMergedTablesRef.current(finalOrder);
        } catch (unmergeErr) {
          console.error('❌ Auto-unmerge after served failed:', unmergeErr);
        }
      }
      
      console.log(`✅ Order ${finalOrder.id} updated successfully with ${finalOrder.items.length} items, status: ${finalOrder.status}`);
      console.log(`📦 Items details:`, finalOrder.items.map(item => ({ 
        id: item.id, 
        name: item.name, 
        status: item.status, 
        served: item.served, 
        quantity: item.quantity,
        price: item.price
      })));
      
      // Verify the order was actually saved by checking if it appears in orders array
      setTimeout(() => {
        const savedOrder = orders.find(o => o.id === finalOrder.id);
        if (savedOrder) {
          console.log(`✅ Verified: Order ${finalOrder.id} found in orders array with ${savedOrder.items.length} items`);
          console.log(`📦 Saved order items:`, savedOrder.items.map(item => ({
            id: item.id,
            name: item.name,
            status: item.status,
            served: item.served
          })));
        } else {
          console.warn(`⚠️ Order ${finalOrder.id} not found in orders array after update`);
        }
      }, 1000);
      return finalOrder;
    } catch (error) {
      console.error('❌ Failed to update order:', error);
      alert('Failed to update order. Please try again.');
      throw error;
    }
  }, [updateOrder, orders, addAuditLog, menuItems, isActiveTableOrder, integrationSettings]);

  // Hard remove (soft-delete) an order from the system – currently not used by Kitchen Display anymore.
  const handleRemoveOrder = useCallback(async (orderId: string) => {
    const order = orders.find(o => o.id === orderId);
    if (!order) return;

    try {
      await removeOrder(orderId);

      // Audit: track soft-delete of orders
      const actor = currentUser;
      await auditLogsService.add({
        timestamp: Date.now(),
        actorId: actor?.id,
        actorName: actor?.name,
        actorRole: actor?.role,
        action: 'order.soft_delete',
        entityType: 'order',
        entityId: orderId,
        details: {
          source: 'app',
        },
      });
      
      if ((order.orderType === 'dine-in' || order.orderType === 'buffet') && order.tableId != null) {
        const tableId = Number(order.tableId);
        const otherActive = orders.some(
          (o) => o.id !== orderId && isActiveTableOrder(o, tableId),
        );
        if (!otherActive) {
          await updateTable(tableId, { status: 'available' });
        }
      }
    } catch (error) {
      console.error('Failed to remove order:', error);
      throw error;
    }
  }, [orders, removeOrder, updateTable, currentUser, isActiveTableOrder]);

  // Kitchen-only remove: hide order from Kitchen Display without deleting it from POS/reports
  const handleHideOrderFromKitchen = useCallback(async (orderId: string) => {
    try {
      await updateOrder(orderId, { hideFromKitchen: true });

      // Audit: track hide-from-kitchen actions separately from deletes
      const actor = currentUser;
      await auditLogsService.add({
        timestamp: Date.now(),
        actorId: actor?.id,
        actorName: actor?.name,
        actorRole: actor?.role,
        action: 'order.hide_from_kitchen',
        entityType: 'order',
        entityId: orderId,
        details: {
          source: 'kds',
        },
      });
    } catch (error) {
      console.error('Failed to hide order from kitchen:', error);
      alert('Failed to hide order from kitchen. Please try again.');
    }
  }, [updateOrder, currentUser]);

  const handleProcessPayment = useCallback(async (orderId: string, payment: Payment) => {
    const { logger } = await import('./lib/logger');
    logger.payment('Processing payment', { orderId, amount: payment.amount, method: payment.method });

    // Prefer in-memory order, but fall back to Firestore. Live listener windows can omit tickets
    // that Pending/KDS still show via explicit fetches — without this, card payments accept on
    // the terminal then fail with "Order not found" and leave the POS on Please wait.
    let order = orders.find((o) => o.id === orderId);
    if (!order) {
      try {
        const { ordersService } = await import('./lib/database');
        order = (await ordersService.getById(orderId)) ?? undefined;
      } catch (e) {
        logger.error('Order lookup failed', { orderId, error: (e as Error)?.message });
      }
    }
    if (!order || order.isDeleted) {
      logger.error('Order not found', { orderId });
      const err = new Error('Order not found. Please try again.');
      alert(err.message);
      throw err;
    }
    if (!Array.isArray(order.payments)) {
      order = { ...order, payments: [] };
    }

    // Prevent payment processing for comped orders
    if (order.status === 'comped' || order.isComped) {
      alert('This order has been comped and cannot accept payments.');
      return;
    }

    // Ensure the payment has a stable id so sub-bill bookkeeping (and refunds) can reference it.
    const paymentWithId: Payment = payment.id
      ? payment
      : {
          ...payment,
          id:
            typeof crypto !== 'undefined' && typeof (crypto as Crypto).randomUUID === 'function'
              ? `pay_${(crypto as Crypto).randomUUID()}`
              : `pay_${Date.now()}_${Math.floor(Math.random() * 1e6)}`,
        };

    // ---- Sub-bill (itemized) payment validation + bookkeeping ------------------------------
    let updatedItems: OrderItem[] | null = null;
    let subBillToAppend: SubBill | null = null;
    if (paymentWithId.itemAllocations && paymentWithId.itemAllocations.length > 0) {
      const mode = paymentWithId.discountAllocationMode ?? 'proportional';
      const selections = paymentWithId.itemAllocations.map((a) => ({ lineId: a.lineId, quantity: a.quantity }));
      const recomputed = computeSubBillTotals(order, selections, mode);
      // Refuse if any line was already paid by another register since the cashier built the picker.
      if (recomputed.hasOverflow) {
        alert('One or more items in this sub-bill were just paid by another register. Please rebuild the sub-bill.');
        return;
      }
      // Tolerance: 1c per line for floating-point drift.
      const tolerance = Math.max(0.05, recomputed.allocations.length * 0.01);
      if (Math.abs(recomputed.total - paymentWithId.amount) > tolerance) {
        logger.warn('Sub-bill recompute mismatch', {
          orderId,
          clientTotal: paymentWithId.amount,
          serverTotal: recomputed.total,
        });
        alert(
          `Sub-bill total has changed (was $${paymentWithId.amount.toFixed(2)}, now $${recomputed.total.toFixed(2)}). Please rebuild the sub-bill.`
        );
        return;
      }
      // Bump paidQuantity / paidQuantityByPaymentId on each allocated line.
      const paymentId = paymentWithId.id!;
      updatedItems = order.items.map((it, idx) => {
        const lineId = getItemLineId(it, idx);
        const alloc = recomputed.allocations.find((a) => a.lineId === lineId);
        if (!alloc) return it;
        const prevPaid = it.paidQuantity ?? 0;
        const prevMap = it.paidQuantityByPaymentId ?? {};
        return {
          ...it,
          paidQuantity: prevPaid + alloc.quantity,
          paidQuantityByPaymentId: { ...prevMap, [paymentId]: (prevMap[paymentId] ?? 0) + alloc.quantity },
        };
      });
      // Append (or merge into) a SubBill entry. v1 = one payment per sub-bill but the schema
      // already supports paymentIds[] so future "split a sub-bill across cash + card" is easy.
      const existingSubBills = order.subBills ?? [];
      const existing = paymentWithId.subBillId
        ? existingSubBills.find((sb) => sb.id === paymentWithId.subBillId)
        : undefined;
      if (existing) {
        subBillToAppend = {
          ...existing,
          total: Math.round((existing.total + recomputed.total) * 100) / 100,
          paymentIds: [...existing.paymentIds, paymentId],
        };
      } else {
        subBillToAppend = {
          id: paymentWithId.subBillId ?? paymentId,
          name: paymentWithId.subBillName,
          createdAt: paymentWithId.timestamp,
          createdByStaffId: currentUser?.id,
          total: recomputed.total,
          paymentIds: [paymentId],
          itemAllocations: recomputed.allocations,
          discountAllocationMode: mode,
        };
      }
    }

    const newPayments = [...order.payments, paymentWithId];
    const totalPaid = newPayments.reduce((sum, p) => sum + p.amount, 0);
    const orderTotalForPayment = getOrderTotalFromItems(order) ?? order.totalAmount ?? 0;
    const dollarFullyPaid = totalPaid >= orderTotalForPayment - 0.01;

    // Mixed-payment safety: when a non-itemized payment fully reconciles the dollar balance and
    // the order has been touched by 1+ itemized sub-bills, automatically settle any remaining
    // unpaid item quantities so the order doesn't get stuck in PARTIAL with paidQuantity gaps.
    if (dollarFullyPaid && !paymentWithId.itemAllocations) {
      const baseItems = updatedItems ?? order.items;
      const orderHadAnyPaidQty = baseItems.some(
        (it) => (it.paidQuantity ?? 0) > 0 || it.paidQuantityByPaymentId,
      );
      if (orderHadAnyPaidQty) {
        const paymentId = paymentWithId.id!;
        updatedItems = baseItems.map((it, idx) => {
          if (it.removed) return it;
          if ((typeof it.price === 'number' && it.price < 0) || String(it.id).includes('combo-discount')) return it;
          const remaining = (it.quantity ?? 0) - (it.paidQuantity ?? 0);
          if (remaining <= 0) return it;
          const prevMap = it.paidQuantityByPaymentId ?? {};
          return {
            ...it,
            paidQuantity: (it.paidQuantity ?? 0) + remaining,
            paidQuantityByPaymentId: { ...prevMap, [paymentId]: (prevMap[paymentId] ?? 0) + remaining },
          };
        });
      }
    }

    // Order is "paid" only when both dollar balance is met AND every non-removed line has been
    // settled (when itemized payments are in play). For orders with no itemized payments the
    // second check is vacuously true (no paidQuantity ever set).
    const itemsSettled = updatedItems
      ? !hasUnpaidItems({ ...order, items: updatedItems })
      : !hasUnpaidItems(order);
    const newPaymentStatus: 'paid' | 'unpaid' = dollarFullyPaid && itemsSettled ? 'paid' : 'unpaid';

    // Calculate total tip across all payments
    const totalTip = newPayments.reduce((sum, p) => sum + (p.tip || 0), 0);

    logger.payment('Payment details', {
      orderId,
      currentPayments: order.payments.length,
      amount: paymentWithId.amount,
      method: paymentWithId.method,
      totalPaid,
      totalTip,
      orderTotal: orderTotalForPayment,
      newPaymentStatus,
      subBillId: paymentWithId.subBillId,
      itemAllocations: paymentWithId.itemAllocations?.length ?? 0,
    });

    try {
      const orderForTotals = updatedItems ? { ...order, items: updatedItems } : order;
      const { subtotal, taxAmount, totalAmount } = getOrderTotals(orderForTotals);
      const paymentUpdates: Partial<Order> = {
        payments: newPayments,
        paymentStatus: newPaymentStatus,
        totalTip: totalTip > 0 ? totalTip : undefined,
        subtotal,
        taxAmount,
        totalAmount,
      };
      if (updatedItems) paymentUpdates.items = updatedItems;
      if (subBillToAppend) {
        const existingSubBills = order.subBills ?? [];
        const replaced = existingSubBills.some((sb) => sb.id === subBillToAppend!.id);
        paymentUpdates.subBills = replaced
          ? existingSubBills.map((sb) => (sb.id === subBillToAppend!.id ? subBillToAppend! : sb))
          : [...existingSubBills, subBillToAppend];
      }
      // Buffet: keep session status until ended — stay on floor; extras may reopen kitchen
      if (isActiveBuffetSession(order)) {
        paymentUpdates.status =
          order.status === 'new' || order.status === 'buffet' ? 'ready' : order.status;
        if (!order.readyAt && (!order.items?.some((i) => !i.isBuffetPackage && i.status === 'pending'))) {
          paymentUpdates.readyAt = Date.now();
        }
      }

      if (paymentWithId.method === 'card' && paymentWithId.transactionId) {
        paymentUpdates.lastCardTransactionId = paymentWithId.transactionId;
        logger.payment('Storing card transaction ID', {
          orderId,
          transactionId: paymentWithId.transactionId,
          paymentAmount: paymentWithId.amount
        });
      } else if (paymentWithId.method === 'card' && !paymentWithId.transactionId) {
        logger.warn('Card payment missing transaction ID', {
          orderId,
          paymentAmount: paymentWithId.amount,
          paymentObject: JSON.stringify(paymentWithId)
        });
      }

      const mealPlanSalesToActivate = order.mealPlanSales?.length
        ? order.mealPlanSales
        : order.mealPlanSale
          ? [order.mealPlanSale]
          : [];
      let mealPlanJustActivated = false;
      if (newPaymentStatus === 'paid' && mealPlanSalesToActivate.some((sale) => !sale.activatedAt)) {
        const { activatePaidMealPlanOrder } = await import('./lib/mealPlanSale');
        Object.assign(paymentUpdates, await activatePaidMealPlanOrder({
          orderId,
          sales: mealPlanSalesToActivate,
          staffId: currentUser?.id,
        }));
        mealPlanJustActivated = true;
      }

      await updateOrder(orderId, paymentUpdates);
      if (mealPlanJustActivated) {
        try {
          const { sendMealPlanSaleReceiptEmail } = await import('./lib/mealPlanReceiptEmail');
          const sales =
            (paymentUpdates.mealPlanSales as typeof mealPlanSalesToActivate | undefined)?.length
              ? (paymentUpdates.mealPlanSales as typeof mealPlanSalesToActivate)
              : paymentUpdates.mealPlanSale
                ? [paymentUpdates.mealPlanSale as (typeof mealPlanSalesToActivate)[number]]
                : mealPlanSalesToActivate;
          await sendMealPlanSaleReceiptEmail({
            order: { ...order, ...paymentUpdates, paymentStatus: 'paid' },
            sales,
            customers,
            businessInfo: integrationSettings?.businessInfo,
          });
        } catch (emailErr) {
          console.error('Meal plan receipt email failed:', emailErr);
        }
      }
      addAuditLog('order.payment', 'order', orderId, {
        amount: paymentWithId.amount,
        method: paymentWithId.method,
        totalPaid: totalPaid,
        newPaymentStatus: newPaymentStatus,
        transactionId: paymentWithId.transactionId ?? undefined,
        subBillId: paymentWithId.subBillId ?? undefined,
        subBillName: paymentWithId.subBillName ?? undefined,
        itemAllocationsCount: paymentWithId.itemAllocations?.length ?? 0,
      });
      logger.payment('Payment processed successfully', { orderId, amount: paymentWithId.amount });
      // Update register session tips if session exists and payment has tip
      if (paymentWithId.tip && paymentWithId.tip > 0 && order.sessionId && currentSession && currentSession.id === order.sessionId) {
        const currentTips = currentSession.tips || 0;
        const newTips = currentTips + paymentWithId.tip;

        // Update session tips
        try {
          const { registerSessionsService } = await import('./lib/database');
          const currentTipRecords = currentSession.tipRecords || [];
          await registerSessionsService.update(currentSession.id, {
            tips: newTips,
            tipRecords: [
              ...currentTipRecords,
              {
                orderId: order.id,
                paymentId: paymentWithId.transactionId,
                amount: paymentWithId.tip,
                timestamp: Date.now(),
                method: paymentWithId.method
              }
            ]
          });
          console.log('✅ Register session tips updated');

          // Update local session state to reflect new tips
          setCurrentSession(prev => prev ? {
            ...prev,
            tips: newTips,
            tipRecords: [
              ...currentTipRecords,
              {
                orderId: order.id,
                paymentId: paymentWithId.transactionId,
                amount: paymentWithId.tip,
                timestamp: Date.now(),
                method: paymentWithId.method
              }
            ]
          } : null);
        } catch (sessionError) {
          console.error('❌ Failed to update session tips:', sessionError);
          // Don't fail payment if session update fails
        }
      }

      if (newPaymentStatus === 'paid') {
        console.log('✅ Order is fully paid, updating table and inventory...');
        const tableId = order.tableId != null ? Number(order.tableId) : undefined;
        if ((order.orderType === 'dine-in' || order.orderType === 'buffet') && tableId != null) {
          // Complete the reservation first so occupancy sync stops forcing "occupied"
          // (otherwise tables blink red↔green while seated lags behind payment).
          try {
            await completeSeatedReservationsForOrderRef.current({
              ...order,
              paymentStatus: 'paid',
            });
          } catch (resErr) {
            console.error('❌ Auto-complete reservation after payment failed:', resErr);
          }
          try {
            const todayKey = new Date().toDateString();
            const table = tables.find((t) => Number(t.id) === tableId);
            const freeIds = new Set<number>([tableId]);
            if (table?.mergedWith?.length) {
              for (const id of table.mergedWith) freeIds.add(Number(id));
            }
            const otherUnpaidOrders = orders.filter(o =>
              o.id !== orderId &&
              [...freeIds].some((id) => isActiveTableOrder(o, id, todayKey))
            );
            if (otherUnpaidOrders.length === 0) {
              for (const id of freeIds) {
                await updateTable(id, { status: 'available' });
              }
              console.log('✅ Table status updated to available');
            } else {
              console.log(`⚠️ Table ${tableId} still has ${otherUnpaidOrders.length} unpaid order(s), keeping as occupied`);
            }
          } catch (tableError) {
            console.error('❌ Failed to update table status:', tableError);
          }
          // Auto-split physical merge when this ticket no longer holds the floor (dine-in paid;
          // buffet waits until session ends — see maybeAutoUnmergeMergedTables).
          try {
            await maybeAutoUnmergeMergedTablesRef.current({
              ...order,
              paymentStatus: 'paid',
            });
          } catch (unmergeErr) {
            console.error('❌ Auto-unmerge after payment failed:', unmergeErr);
          }
        }

        // Update inventory based on recipes (only when not in cost-only mode; only for inventory-linked ingredients)
        if (integrationSettings?.recipeInventoryMode !== 'cost_only') {
          order.items.forEach(orderItem => {
            const recipe = recipes.find(r => r.menuItemId === orderItem.id);
            if (recipe) {
              recipe.ingredients.forEach(ingredient => {
                if (ingredient.inventoryItemId == null) return;
                const invItem = inventory.find(i => i.id === ingredient.inventoryItemId);
                if (invItem) {
                  const newStock = invItem.stock - (ingredient.quantity * orderItem.quantity);
                  updateInventory(invItem.id, {
                    stock: Math.max(0, newStock),
                    lastUpdated: Date.now()
                  });
                }
              });
            }
          });
        }
      }
    } catch (error) {
      console.error('❌ Failed to process payment:', error);
      alert(`Failed to process payment: ${error instanceof Error ? error.message : 'Unknown error'}`);
      throw error;
    }
  }, [orders, updateOrder, updateTable, recipes, inventory, updateInventory, isActiveTableOrder, addAuditLog, integrationSettings?.recipeInventoryMode, integrationSettings?.businessInfo, currentUser, customers]);

  /**
   * Manager-only: refund a single sub-bill. Restores paidQuantity on the allocated lines so those
   * items become re-payable, writes a negative Payment tagged with the same subBillId, and
   * recomputes paymentStatus. Out-of-band cash/card refund is the manager's responsibility — this
   * just records it in the order.
   */
  const handleRefundSubBill = useCallback(async (orderId: string, subBillId: string): Promise<void> => {
    const order = orders.find((o) => o.id === orderId);
    if (!order) return;
    const sb = (order.subBills ?? []).find((s) => s.id === subBillId);
    if (!sb) {
      alert('Sub-bill not found.');
      return;
    }
    // Pick the dominant payment method used to charge this sub-bill (for the negative refund record).
    const sbPayments = order.payments.filter((p) => p.id && sb.paymentIds.includes(p.id));
    const refundMethod: Payment['method'] = sbPayments.find((p) => p.method === 'card')?.method ?? sbPayments[0]?.method ?? 'cash';
    const now = Date.now();
    const refundPayment: Payment = {
      id:
        typeof crypto !== 'undefined' && typeof (crypto as Crypto).randomUUID === 'function'
          ? `pay_${(crypto as Crypto).randomUUID()}`
          : `pay_${now}_${Math.floor(Math.random() * 1e6)}`,
      method: refundMethod,
      amount: -Math.round(sb.total * 100) / 100,
      timestamp: now,
      subBillId: sb.id,
      subBillName: sb.name,
      // Allocations on a refund mirror the original (positive quantities = "items being refunded").
      itemAllocations: sb.itemAllocations,
      discountAllocationMode: sb.discountAllocationMode,
    };
    // Roll back paidQuantity and paidQuantityByPaymentId for every line in this sub-bill.
    const newItems = order.items.map((it, idx) => {
      const lineId = getItemLineId(it, idx);
      const alloc = sb.itemAllocations.find((a) => a.lineId === lineId);
      if (!alloc) return it;
      const prevPaid = it.paidQuantity ?? 0;
      const newPaid = Math.max(0, prevPaid - alloc.quantity);
      const prevMap = it.paidQuantityByPaymentId ?? {};
      const newMap: Record<string, number> = { ...prevMap };
      // Remove this sub-bill's payment ids from the per-payment map.
      for (const pid of sb.paymentIds) {
        delete newMap[pid];
      }
      return { ...it, paidQuantity: newPaid, paidQuantityByPaymentId: newMap };
    });
    const newPayments = [...order.payments, refundPayment];
    const newSubBills = (order.subBills ?? []).filter((s) => s.id !== sb.id);

    // Recompute payment status from the post-refund snapshot.
    const orderForTotals = { ...order, items: newItems };
    const { totalAmount } = getOrderTotals(orderForTotals);
    const paidSum = newPayments.reduce((sum, p) => sum + p.amount, 0);
    const dollarFullyPaid = paidSum >= totalAmount - 0.01;
    const itemsSettled = !hasUnpaidItems(orderForTotals);
    const newPaymentStatus: 'paid' | 'unpaid' = dollarFullyPaid && itemsSettled ? 'paid' : 'unpaid';
    try {
      await updateOrder(orderId, {
        items: newItems,
        payments: newPayments,
        subBills: newSubBills,
        paymentStatus: newPaymentStatus,
      });
      addAuditLog('order.subbill_refund', 'order', orderId, {
        subBillId: sb.id,
        subBillName: sb.name ?? null,
        amount: sb.total,
        itemQuantitiesRestored: sb.itemAllocations.reduce((s, a) => s + a.quantity, 0),
      });
      alert(`Sub-bill "${sb.name || 'Guest'}" refunded ($${sb.total.toFixed(2)}). Items are now available to re-pay.`);
    } catch (error) {
      console.error('Failed to refund sub-bill:', error);
      alert('Failed to refund sub-bill. Please try again.');
    }
  }, [orders, updateOrder, addAuditLog]);

  /** Manager-only: mark order as paid for reconciliation when payment was taken but app didn't persist (e.g. 0-9233, 0-5427 stuck in Pending). */
  const handleMarkOrderAsPaid = useCallback(async (orderId: string) => {
    let order = orders.find((o) => o.id === orderId);
    if (!order) {
      try {
        const { ordersService } = await import('./lib/database');
        order = await ordersService.getById(orderId);
      } catch (e) {
        console.error('Mark as paid: could not load order', orderId, e);
      }
    }
    if (!order) {
      alert('Order not found. Refresh Pending and try again.');
      return;
    }
    if (order.paymentStatus === 'paid' && !hasBalanceDue(order)) return;
    const total = getOrderTotalFromItems(order) ?? order.totalAmount ?? 0;
    const { subtotal, taxAmount, totalAmount } = getOrderTotals(order);
    const payments = order.payments?.length
      ? order.payments
      : [{ method: 'cash' as const, amount: total, timestamp: Date.now() }];
    try {
      const paidPatch: Partial<Order> = { paymentStatus: 'paid', payments, subtotal, taxAmount, totalAmount };
      const mealPlanSalesToActivate = order.mealPlanSales?.length
        ? order.mealPlanSales
        : order.mealPlanSale
          ? [order.mealPlanSale]
          : [];
      let mealPlanJustActivated = false;
      if (mealPlanSalesToActivate.some((sale) => !sale.activatedAt)) {
        const { activatePaidMealPlanOrder } = await import('./lib/mealPlanSale');
        Object.assign(paidPatch, await activatePaidMealPlanOrder({
          orderId,
          sales: mealPlanSalesToActivate,
          staffId: currentUser?.id,
        }));
        mealPlanJustActivated = true;
      }
      await updateOrder(orderId, paidPatch);
      if (mealPlanJustActivated) {
        try {
          const { sendMealPlanSaleReceiptEmail } = await import('./lib/mealPlanReceiptEmail');
          const sales =
            (paidPatch.mealPlanSales as typeof mealPlanSalesToActivate | undefined)?.length
              ? (paidPatch.mealPlanSales as typeof mealPlanSalesToActivate)
              : paidPatch.mealPlanSale
                ? [paidPatch.mealPlanSale as (typeof mealPlanSalesToActivate)[number]]
                : mealPlanSalesToActivate;
          await sendMealPlanSaleReceiptEmail({
            order: { ...order, ...paidPatch, paymentStatus: 'paid' },
            sales,
            customers,
            businessInfo: integrationSettings?.businessInfo,
          });
        } catch (emailErr) {
          console.error('Meal plan receipt email failed:', emailErr);
        }
      }
      addAuditLog('order.paid', 'order', orderId, { method: 'mark_as_paid', totalAmount });
      const tableId = order.tableId != null ? Number(order.tableId) : undefined;
      if ((order.orderType === 'dine-in' || order.orderType === 'buffet') && tableId != null) {
        try {
          await completeSeatedReservationsForOrderRef.current({
            ...order,
            paymentStatus: 'paid',
          });
        } catch (resErr) {
          console.error('❌ Auto-complete reservation after mark-as-paid failed:', resErr);
        }
        const todayKey = new Date().toDateString();
        const otherUnpaid = orders.filter(
          (o) => o.id !== orderId && isActiveTableOrder(o, tableId, todayKey)
        );
        if (otherUnpaid.length === 0) await updateTable(tableId, { status: 'available' });
        try {
          await maybeAutoUnmergeMergedTablesRef.current({
            ...order,
            paymentStatus: 'paid',
          });
        } catch (unmergeErr) {
          console.error('❌ Auto-unmerge after mark-as-paid failed:', unmergeErr);
        }
      }
      if (integrationSettings?.recipeInventoryMode !== 'cost_only') {
        order.items.forEach((orderItem) => {
          const recipe = recipes.find((r) => r.menuItemId === orderItem.id);
          if (recipe) {
            recipe.ingredients.forEach((ingredient) => {
              if (ingredient.inventoryItemId == null) return;
              const invItem = inventory.find((i) => i.id === ingredient.inventoryItemId);
              if (invItem) {
                const newStock = invItem.stock - ingredient.quantity * orderItem.quantity;
                updateInventory(invItem.id, { stock: Math.max(0, newStock), lastUpdated: Date.now() });
              }
            });
          }
        });
      }
    } catch (e) {
      console.error('Mark as paid failed:', e);
      alert('Failed to mark order as paid.');
    }
  }, [orders, updateOrder, updateTable, recipes, inventory, updateInventory, isActiveTableOrder, integrationSettings?.recipeInventoryMode, integrationSettings?.businessInfo, currentUser, customers]);

  // -------------------------------------------------------------------------
  // Stuck-payment-status auto-recovery (managers only, runs once per session).
  //
  // Backstop for the regression where `hasUnpaidItems` returned true for every order
  // with no itemized payments, leaving fully-paid orders stuck on `paymentStatus: 'unpaid'`.
  // The bug is fixed in `lib/orderUtils.ts`, but historical orders still carry the bad status.
  //
  // We scan the last 14 days of orders and "auto-mark paid" only orders that meet ALL of:
  //   - `paymentStatus === 'unpaid'`
  //   - sum of `payments[].amount` >= `totalAmount - 0.01`  (full payment evidence)
  //   - not cancelled / refunded / comped / deleted
  //   - has at least one non-pending payment (avoids touching genuinely unpaid orders)
  //
  // Each fix writes an audit-log entry so the change is traceable.
  //
  // NOTE: this hook MUST live AFTER `addAuditLog` is declared (otherwise the dependency
  // array hits a temporal-dead-zone ReferenceError in the const declaration).
  // -------------------------------------------------------------------------
  const stuckPaymentRecoveryDoneRef = useRef(false);
  useEffect(() => {
    if (!currentUser) return;
    if (!canManageRegister(currentUser)) return; // managers only
    if (stuckPaymentRecoveryDoneRef.current) return;

    let cancelled = false;
    const timeout = setTimeout(async () => {
      if (cancelled || stuckPaymentRecoveryDoneRef.current) return;
      stuckPaymentRecoveryDoneRef.current = true;
      try {
        const { ordersService } = await import('./lib/database');
        const now = Date.now();
        const fourteenDaysAgo = now - 14 * 24 * 60 * 60 * 1000;
        const recent = await ordersService.getByDateRange(fourteenDaysAgo, now);
        if (cancelled) return;

        const stuck = recent.filter((o) => {
          if (o.paymentStatus !== 'unpaid') return false;
          if (o.isDeleted) return false;
          if (o.status === 'cancelled' || o.status === 'refunded' || o.status === 'comped') return false;
          if (o.isComped) return false;
          const payments = Array.isArray(o.payments) ? o.payments : [];
          if (payments.length === 0) return false;
          const settledPayments = payments.filter((p) => p.paymentStatus !== 'pending');
          if (settledPayments.length === 0) return false;
          const paid = settledPayments.reduce((sum, p) => sum + (typeof p.amount === 'number' ? p.amount : 0), 0);
          const total = typeof o.totalAmount === 'number' ? o.totalAmount : 0;
          return total > 0 && paid >= total - 0.01;
        });

        if (stuck.length === 0) return;
        console.log(`🔧 Auto-recovering ${stuck.length} order(s) stuck in 'unpaid' (full payment present)…`);

        let fixed = 0;
        for (const o of stuck) {
          if (cancelled) break;
          try {
            await ordersService.update(o.id, { paymentStatus: 'paid' });
            const paid = (o.payments ?? []).reduce((sum, p) => sum + (typeof p.amount === 'number' ? p.amount : 0), 0);
            addAuditLog('order.payment_status_recovery', 'order', o.id, {
              from: 'unpaid',
              to: 'paid',
              totalAmount: o.totalAmount,
              totalPaid: paid,
              reason: 'auto_recover_stuck_unpaid_with_full_payment',
            });
            fixed += 1;
          } catch (err: any) {
            console.warn(`Could not auto-recover order ${o.id}:`, err?.message || err);
          }
        }

        if (fixed > 0) {
          console.log(`✅ Auto-recovery: marked ${fixed} order(s) as paid (full payment was already recorded).`);
        }

        // Uber/DoorDash handed off (served) but still unpaid in POS — platform payment never synced.
        const uberServedUnpaid = recent.filter(
          (o) =>
            o.orderType === 'uber-doordash' &&
            o.status === 'served' &&
            o.paymentStatus === 'unpaid' &&
            !o.isDeleted
        );
        for (const o of uberServedUnpaid) {
          if (cancelled) break;
          try {
            const { totalAmount } = getOrderTotals(o);
            const payments = o.payments?.length
              ? o.payments
              : [{ method: 'card' as const, amount: totalAmount, timestamp: Date.now() }];
            await ordersService.update(o.id, { paymentStatus: 'paid', payments });
            addAuditLog('order.payment_status_recovery', 'order', o.id, {
              from: 'unpaid',
              to: 'paid',
              totalAmount,
              reason: 'auto_recover_uber_served_unpaid',
            });
          } catch (err: any) {
            console.warn(`Could not auto-recover Uber order ${o.id}:`, err?.message || err);
          }
        }
      } catch (err: any) {
        console.warn('Stuck-payment auto-recovery skipped:', err?.message || err);
      }
    }, 4000); // run after the transaction-recovery pass

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [currentUser, addAuditLog]);

  const handleUpdateOrderStatus = useCallback(async (orderId: string, status: OrderStatus) => {
    try {
      addAuditLog('order.status_change', 'order', orderId, { to: status, source: 'service' });
      if (status === 'served') {
        await updateOrder(orderId, (currentOrder) => {
          if (!currentOrder) return {};
          const isDiscountItem = (i: OrderItem) => i.price < 0 || i.id.toString().includes('combo-discount');
          const isPickupType =
            currentOrder.orderType === 'takeaway' ||
            currentOrder.orderType === 'online' ||
            currentOrder.orderType === 'uber-doordash';
          // Takeaway/Uber/DoorDash "Mark Picked Up": always set served and mark all items served (no KDS gate)
          const newItems = (currentOrder.items ?? []).map((i) => {
            if (isDiscountItem(i) || i.removed) return i;
            if (isPickupType) {
              return { ...i, served: true, quantityServed: i.quantity };
            }
            const ready = getEffectiveQuantityCompleted(i) >= i.quantity;
            return ready ? { ...i, served: true, quantityServed: i.quantity } : i;
          });
          const serveable = newItems.filter(i => !isDiscountItem(i) && !i.removed);
          const allReadyAndServed = isPickupType || (serveable.length > 0 && serveable.every(i =>
            (i.quantityServed ?? (i.served ? i.quantity : 0)) >= i.quantity
          ));
          const base: Partial<Order> = {
            items: newItems,
            status: allReadyAndServed ? ('served' as OrderStatus) : currentOrder.status,
            ...(allReadyAndServed ? { servedAt: Date.now() } : {}),
          };
          // Uber/DoorDash "Mark Picked Up": payment is via platform, so set paid here so order moves to Completed
          if (allReadyAndServed && currentOrder.orderType === 'uber-doordash') {
            const { totalAmount } = getOrderTotals({ ...currentOrder, items: newItems });
            const payments = currentOrder.payments?.length
              ? currentOrder.payments
              : [{ method: 'card' as const, amount: totalAmount, timestamp: Date.now() }];
            base.paymentStatus = 'paid';
            base.payments = payments;
          }
          return base;
        });
        const servedOrder = orders.find((o) => o.id === orderId);
        if (servedOrder && (servedOrder.orderType === 'dine-in' || servedOrder.orderType === 'buffet')) {
          try {
            await completeSeatedReservationsForOrderRef.current({ ...servedOrder, status: 'served' });
          } catch (resErr) {
            console.error('❌ Auto-complete reservation after status served failed:', resErr);
          }
          try {
            await maybeAutoUnmergeMergedTablesRef.current({ ...servedOrder, status: 'served' });
          } catch (unmergeErr) {
            console.error('❌ Auto-unmerge after status served failed:', unmergeErr);
          }
        }
      } else {
        const updates: Partial<Order> = { status };
        if (status === 'ready') {
          updates.readyAt = Date.now();
        }
        await updateOrder(orderId, (currentOrder) => {
          if (!currentOrder) return updates;
          const next: Partial<Order> = { ...updates };
          if (status === 'ready' && currentOrder.orderType === 'uber-doordash') {
            next.deliveryInfo = {
              ...currentOrder.deliveryInfo,
              uberReadySentAt:
                currentOrder.deliveryInfo?.uberReadySentAt ?? Date.now(),
            };
          }
          return next;
        });
      }
    } catch (error) {
      console.error('Failed to update order status:', error);
    }
  }, [updateOrder, addAuditLog]);

  const handleUpdateOrderItemStatus = useCallback((
    orderId: string,
    itemIndex: number,
    status: 'pending' | 'completed',
    quantityCompleted?: number,
    batchIndex?: number,
    targetQtyForCard?: number
  ) => {
    updateOrder(orderId, (currentOrder) => {
      if (!currentOrder) return {};

      const newItems = [...currentOrder.items];
      const item = newItems[itemIndex];
      if (item) {
        if (quantityCompleted !== undefined) {
          let finalQuantityCompleted: number;
          let quantityCompletedByBatch: Record<string, number> | undefined;

          if (batchIndex != null && targetQtyForCard != null) {
            const existing = item.quantityCompletedByBatch ?? {};
            quantityCompletedByBatch = { ...existing };
            quantityCompletedByBatch[String(batchIndex)] = quantityCompleted;
            finalQuantityCompleted = Object.values(quantityCompletedByBatch).reduce((a, b) => a + b, 0);
            // Drop zeroed batch keys so undo fully clears progress for that card.
            if (quantityCompleted <= 0) {
              delete quantityCompletedByBatch[String(batchIndex)];
            }
            if (Object.keys(quantityCompletedByBatch).length === 0) {
              quantityCompletedByBatch = undefined;
            }
          } else {
            finalQuantityCompleted = quantityCompleted;
            quantityCompletedByBatch = undefined;
          }

          const { quantityCompletedByBatch: _omit, ...itemRest } = item;
          newItems[itemIndex] = {
            ...itemRest,
            quantityCompleted: finalQuantityCompleted,
            ...(quantityCompletedByBatch != null
              ? { quantityCompletedByBatch }
              : {}),
            status: finalQuantityCompleted >= item.quantity ? 'completed' as const : 'pending' as const,
          };
        } else {
          newItems[itemIndex] = { ...item, status };
          if (status === 'completed') {
            newItems[itemIndex] = { ...newItems[itemIndex], quantityCompleted: item.quantity };
          } else {
            // Full undo: clear global and per-batch kitchen progress so POS can edit again.
            const { quantityCompletedByBatch: _drop, ...rest } = newItems[itemIndex];
            newItems[itemIndex] = { ...rest, quantityCompleted: 0, status: 'pending' as const };
          }
        }
      }

      const isDiscountItem = (i: OrderItem) =>
        i.price < 0 || i.id.toString().includes('combo-discount');
      const preparableItems = newItems.filter(i => !isDiscountItem(i) && !i.removed);
      const allItemsCompleted = preparableItems.length > 0 && preparableItems.every(i => i.status === 'completed');

      const updatedItems = newItems.map(i => {
        if (isDiscountItem(i) && allItemsCompleted) {
          return { ...i, status: 'completed' as const };
        }
        return i;
      });

      const draft: Order = { ...currentOrder, items: updatedItems };
      const lifecycle = deriveOrderLifecycleStatus(draft as any);
      const mappedStatus = mapOrderStatusToLegacy(lifecycle, draft.orderType);
      const readyAt =
        mappedStatus === 'ready' && currentOrder.status !== 'ready'
          ? Date.now()
          : currentOrder.readyAt;

      const result: Partial<Order> = { items: updatedItems, status: mappedStatus };
      if (readyAt && readyAt !== currentOrder.readyAt) {
        result.readyAt = readyAt;
      }
      return result;
    });
  }, [updateOrder]);

  const handleStartOrder = useCallback((orderId: string, batchIndex?: number) => {
    addAuditLog('order.kitchen_start', 'order', orderId, { source: 'kds', batchIndex });
    updateOrder(orderId, (currentOrder) => {
      if (!currentOrder) return {};
      const now = Date.now();
      // Running-order card: record only this batch as started so other cards keep their Start button
      if (batchIndex != null && batchIndex >= 1) {
        const preparingStartedByBatch = { ...(currentOrder.preparingStartedByBatch ?? {}), [String(batchIndex)]: now };
        return { preparingStartedByBatch };
      }
      // Base card (or no batch): change order status and global started time
      if (currentOrder.status === 'new') {
        return { status: 'preparing' as OrderStatus, preparingStartedAt: now };
      }
      return {};
    });
  }, [updateOrder, addAuditLog]);

  const handleUpdateOrderItemServedStatus = useCallback(async (
    orderId: string,
    itemIndex: number,
    served: boolean,
    targetQty?: number,
    batchIndex?: number,
    targetQtyForCard?: number
  ) => {
    // Get the current order to check item details before updating
    const currentOrder = orders.find(o => o.id === orderId);
    if (currentOrder && currentOrder.items[itemIndex]) {
      const orderItem = currentOrder.items[itemIndex];
      const menuItem = menuItems.find(mi => mi.id === orderItem.id);
      const getPrevServed = () => {
        if (batchIndex != null && orderItem.quantityServedByBatch) {
          return Object.values(orderItem.quantityServedByBatch).reduce((a, b) => a + b, 0);
        }
        return orderItem.quantityServed ?? (orderItem.served ? orderItem.quantity : 0);
      };
      const prevServed = getPrevServed();
      let newServed: number;
      if (batchIndex != null && targetQtyForCard != null && served) {
        const existing = orderItem.quantityServedByBatch ?? {};
        const batchVal = targetQtyForCard;
        newServed = prevServed - (existing[String(batchIndex)] ?? 0) + batchVal;
      } else {
        newServed = served && typeof targetQty === 'number' ? Math.max(prevServed, targetQty) : (served ? orderItem.quantity : 0);
      }
      const quantityToDeduct = Math.max(0, newServed - prevServed);

      // If marking as served and item has daily availability, deduct only the newly served quantity
      if (served && quantityToDeduct > 0 && menuItem?.hasDailyAvailability) {
        const newAvailability = (menuItem.dailyAvailability ?? 0) - quantityToDeduct;
        try {
          await updateMenuItem(menuItem.id, {
            dailyAvailability: Math.max(0, newAvailability),
          });
        } catch (error) {
          console.error('Failed to update menu item availability:', error);
        }
      }
    }

    updateOrder(orderId, (currentOrder) => {
      if (!currentOrder) return {};

      const newItems = [...currentOrder.items];
      if (newItems[itemIndex]) {
        const item = newItems[itemIndex];
        if (served && batchIndex != null && targetQtyForCard != null) {
          const existing = item.quantityServedByBatch ?? {};
          const quantityServedByBatch = { ...existing };
          quantityServedByBatch[String(batchIndex)] = targetQtyForCard;
          const quantityServed = Object.values(quantityServedByBatch).reduce((a, b) => a + b, 0);
          const { quantityServedByBatch: _omit, ...itemRest } = item;
          newItems[itemIndex] = { ...itemRest, quantityServed, quantityServedByBatch, served: quantityServed >= item.quantity };
        } else if (served && typeof targetQty === 'number') {
          const quantityServed = Math.max(item.quantityServed ?? (item.served ? item.quantity : 0), targetQty);
          const { quantityServedByBatch: _omit, ...itemRest } = item;
          newItems[itemIndex] = { ...itemRest, quantityServed, served: quantityServed >= item.quantity };
        } else {
          const { quantityServedByBatch: _omit, ...itemRest } = item;
          newItems[itemIndex] = { ...itemRest, served, quantityServed: served ? item.quantity : 0 };
        }
      }

      const isDiscountItem = (i: OrderItem) => i.price < 0 || i.id.toString().includes('combo-discount');
      const serveableItems = newItems.filter(item => !isDiscountItem(item) && !item.removed);
      const allServed = serveableItems.length > 0 && serveableItems.every(item => {
        if (item.quantityServedByBatch) {
          return Object.values(item.quantityServedByBatch).reduce((a, b) => a + b, 0) >= item.quantity;
        }
        return (item.quantityServed ?? (item.served ? item.quantity : 0)) >= item.quantity;
      });

      const draft: Order = { ...currentOrder, items: newItems };
      const lifecycle = deriveOrderLifecycleStatus(draft as any);
      const mappedStatus = mapOrderStatusToLegacy(lifecycle, draft.orderType);
      const servedAt =
        allServed && mappedStatus === 'served' && currentOrder.status !== 'served'
          ? Date.now()
          : currentOrder.servedAt;

      const result: Partial<Order> = { items: newItems, status: mappedStatus };
      if (servedAt && servedAt !== currentOrder.servedAt) {
        result.servedAt = servedAt;
      }
      return result;
    });
  }, [updateOrder]);

  const handleSaveMenuItem = useCallback(async (item: MenuItem) => {
    try {
      // Firestore field limit is ~1MB; never save base64 data URLs
      const safeItem = typeof item.imageUrl === 'string' && item.imageUrl.startsWith('data:image')
        ? { ...item, imageUrl: '' }
        : item;
      if (safeItem.id && menuItems.some(i => i.id === safeItem.id)) {
        // When updating, omit imageUrl/imageUrlThumb if empty so we don't overwrite existing
        const updates = { ...safeItem };
        if (updates.imageUrl === '') delete updates.imageUrl;
        if (updates.imageUrlThumb === '') delete updates.imageUrlThumb;
        await updateMenuItem(safeItem.id, updates);
      } else {
        const { id, ...itemData } = safeItem;
        await createMenuItem(itemData);
      }
      await refreshMenuItems();
    } catch (error) {
      console.error('Failed to save menu item:', error);
      alert('Failed to save menu item. Please try again.');
    }
  }, [menuItems, createMenuItem, updateMenuItem, refreshMenuItems]);

  const handleBulkAddMenuItems = useCallback(async (items: Array<Omit<MenuItem, 'id' | 'imageUrl' | 'categoryId'> & { categoryName: string }>) => {
    const failedItems: string[] = [];
    let successCount = 0;
    
    // Refresh categories to ensure we have the latest list
    await refreshCategories();
    
    // Wait a bit for state to update, then rebuild map with current categories
    // Use a function to get fresh categories from the hook's internal state
    const buildCategoryMap = (cats: Category[]) => {
      const map = new Map<string, Category>();
      cats.forEach(cat => {
        map.set(cat.name.toLowerCase().trim(), cat);
      });
      return map;
    };
    
    // Start with current categories, but update map as we create new ones
    let categoryMap = buildCategoryMap(categories);

    for (const item of items) {
      try {
        // Normalize category name for lookup
        const categoryNameNormalized = item.categoryName.toLowerCase().trim();
        let category = categoryMap.get(categoryNameNormalized);
        
        // If category doesn't exist, create it
        if (!category) {
          try {
            const categoryName = item.categoryName.trim();
            const newCategory = await createCategory({ name: categoryName });
            // Add to map for subsequent items in this batch
            categoryMap.set(categoryNameNormalized, newCategory);
            category = newCategory;
            console.log(`✅ Created new category: "${categoryName}" (ID: ${category.id})`);
          } catch (error) {
            console.error(`❌ Failed to create category "${item.categoryName}":`, error);
            failedItems.push(`${item.name} (Failed to create category: ${item.categoryName})`);
            continue;
          }
        }
        
        // Create the menu item
        await createMenuItem({
          name: item.name,
          price: item.price,
          categoryId: category.id,
          isSignatureDish: item.isSignatureDish || false,
          imageUrl: '',
        });
        successCount++;
        console.log(`Added menu item: ${item.name} to category: ${category.name}`);
      } catch (error) {
        console.error(`Failed to add menu item "${item.name}":`, error);
        failedItems.push(`${item.name} (Error: ${error instanceof Error ? error.message : 'Unknown error'})`);
      }
    }
    
    // Refresh categories again to show newly created ones in the UI
    await refreshCategories();
    
    return { successCount, failedItems };
  }, [categories, createMenuItem, createCategory, refreshCategories]);

  const handleDeleteMenuItem = useCallback(async (itemId: number) => {
    try {
      await removeMenuItem(itemId);
    } catch (error) {
      console.error('Failed to delete menu item:', error);
      alert('Failed to delete menu item. Please try again.');
    }
  }, [removeMenuItem]);

  const handleSaveCategory = useCallback(async (category: Category) => {
    try {
      if (category.id != null && category.id !== '' && categories.some(c => String(c.id) === String(category.id))) {
        await updateCategory(category.id, category);
      } else {
        const { id, ...categoryData } = category;
        await createCategory(categoryData);
      }
      await refreshCategories();
    } catch (error) {
      console.error('Failed to save category:', error);
      alert('Failed to save category. Please try again.');
    }
  }, [categories, createCategory, updateCategory, refreshCategories]);

  const handleMoveCategory = useCallback(async (categoryId: number | string, direction: 'up' | 'down') => {
    try {
      console.log('🔄 Moving category:', { categoryId, direction, categoriesCount: categories.length });
      const currentIndex = categories.findIndex(c => String(c.id) === String(categoryId));
      console.log('📍 Current index:', currentIndex);
      
      if (currentIndex === -1) {
        console.error('❌ Category not found:', categoryId);
        return;
      }
      
      const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (newIndex < 0 || newIndex >= categories.length) {
        console.warn('⚠️ Cannot move - already at edge');
        return;
      }
      
      const currentCategory = categories[currentIndex];
      const targetCategory = categories[newIndex];
      
      console.log('🔄 Swapping categories:', {
        current: { id: currentCategory.id, name: currentCategory.name, displayOrder: currentCategory.displayOrder },
        target: { id: targetCategory.id, name: targetCategory.name, displayOrder: targetCategory.displayOrder }
      });
      
      // Swap display orders
      const currentOrder = currentCategory.displayOrder ?? currentIndex;
      const targetOrder = targetCategory.displayOrder ?? newIndex;
      
      console.log('📝 Updating display orders:', { currentOrder, targetOrder });
      
      await Promise.all([
        updateCategory(currentCategory.id, { displayOrder: targetOrder }),
        updateCategory(targetCategory.id, { displayOrder: currentOrder }),
      ]);
      
      console.log('✅ Categories updated, refreshing...');
      
      // Refresh categories to show new order
      await refreshCategories();
      
      console.log('✅ Category reordering complete');
    } catch (error) {
      console.error('❌ Failed to move category:', error);
      alert('Failed to reorder category. Please try again.');
    }
  }, [categories, updateCategory, refreshCategories]);

  const handleReorderCategory = useCallback(async (categoryId: number | string, targetIndex: number) => {
    if (targetIndex < 0 || targetIndex >= categories.length) return;

    const currentIndex = categories.findIndex(c => String(c.id) === String(categoryId));
    if (currentIndex === -1 || currentIndex === targetIndex) return;

    const reordered = [...categories];
    const [movedCategory] = reordered.splice(currentIndex, 1);
    reordered.splice(targetIndex, 0, movedCategory);

    try {
      await Promise.all(reordered.map((category, index) => {
        const desiredOrder = index;
        const currentOrder = category.displayOrder ?? index;
        if (desiredOrder === currentOrder) return Promise.resolve();
        return updateCategory(category.id, { displayOrder: desiredOrder });
      }));
      await refreshCategories();
    } catch (error) {
      console.error('Failed to reorder categories:', error);
      alert('Failed to reorder categories. Please try again.');
    }
  }, [categories, updateCategory, refreshCategories]);

  const handleDeleteCategory = useCallback(async (categoryId: number | string) => {
      const isCategoryInUse = menuItems.some(item => String(item.categoryId) === String(categoryId));
      if (isCategoryInUse) {
          alert("Cannot delete category. It is currently in use. Please reassign items to another category first.");
          return;
      }
      try {
          await removeCategory(categoryId);
      } catch (error) {
          console.error('Failed to delete category:', error);
          alert('Failed to delete category. Please try again.');
      }
  }, [menuItems, removeCategory]);

  const handleSaveCombo = useCallback(async (combo: Omit<QuickAddCombo, 'id'> | QuickAddCombo) => {
    try {
      if ('id' in combo && combo.id && quickAddCombos.some(c => c.id === combo.id)) {
        await updateCombo(combo.id, combo);
      } else {
        const { id, ...comboData } = combo as any;
        await createCombo(comboData);
      }
    } catch (error) {
      console.error('Failed to save quick add combo:', error);
      alert('Failed to save quick add combo. Please try again.');
    }
  }, [quickAddCombos, createCombo, updateCombo]);

  const handleDeleteCombo = useCallback(async (comboId: number) => {
    try {
      await removeCombo(comboId);
    } catch (error) {
      console.error('Failed to delete quick add combo:', error);
      alert('Failed to delete quick add combo. Please try again.');
    }
  }, [removeCombo]);

  const handleSaveRecipe = useCallback(async (recipe: Recipe) => {
    try {
      await saveRecipe(recipe);
      console.log('Recipe saved for menu item', recipe.menuItemId);
    } catch (err) {
      console.error('Failed to save recipe:', err);
      alert('Failed to save recipe. Please try again.');
    }
  }, [saveRecipe]);

  const handleSaveInventoryItem = useCallback(async (item: InventoryItem) => {
    try {
      if (item.id && inventory.some(i => i.id === item.id)) {
        await updateInventory(item.id, { ...item, lastUpdated: Date.now() });
        addAuditLog('inventory.update', 'inventory', String(item.id), { name: item.name, stock: item.stock });
      } else {
        const { id, ...itemData } = item;
        const created = await createInventoryItem({ ...itemData, lastUpdated: Date.now() });
        addAuditLog('inventory.create', 'inventory', String(created.id), { name: created.name, stock: created.stock });
      }
    } catch (error) {
      console.error('Failed to save inventory item:', error);
      alert('Failed to save inventory item. Please try again.');
    }
  }, [inventory, createInventoryItem, updateInventory, addAuditLog]);

  const handleDeleteInventoryItem = useCallback(async (itemId: number) => {
    try {
      await removeInventoryItem(itemId);
    } catch (error) {
      console.error('Failed to delete inventory item:', error);
      alert('Failed to delete inventory item. Please try again.');
    }
  }, [removeInventoryItem]);

  const handleSaveAsset = useCallback(async (asset: RestaurantAsset) => {
    try {
      if (asset.id && assets.some((a) => a.id === asset.id)) {
        await updateAsset(asset.id, { ...asset, updatedAt: Date.now() });
      } else {
        const { id: _id, ...assetData } = asset;
        await createAsset({ ...assetData, createdAt: Date.now(), updatedAt: Date.now() });
      }
    } catch (error) {
      console.error('Failed to save asset:', error);
      alert('Failed to save asset. Please try again.');
    }
  }, [assets, createAsset, updateAsset]);

  const handleBulkSaveAssets = useCallback(async (items: RestaurantAsset[]) => {
    try {
      for (const asset of items) {
        const { id: _id, ...assetData } = asset;
        await createAsset({ ...assetData, createdAt: Date.now(), updatedAt: Date.now() });
      }
    } catch (error) {
      console.error('Failed to bulk save assets:', error);
      alert('Failed to save some assets. Rows that already saved stay in the register — retry the rest.');
      throw error;
    }
  }, [createAsset]);

  const handleDeleteAsset = useCallback(async (assetId: string) => {
    try {
      await removeAsset(assetId);
    } catch (error) {
      console.error('Failed to delete asset:', error);
      alert('Failed to delete asset. Please try again.');
    }
  }, [removeAsset]);

  const handleAdjustStock = useCallback(async (itemId: number, adjustment: number) => {
    const item = inventory.find(i => i.id === itemId);
    if (!item) return;
    try {
      const newStock = item.stock + adjustment;
      await updateInventory(itemId, { stock: newStock, lastUpdated: Date.now() });
      addAuditLog('inventory.adjust_stock', 'inventory', String(itemId), { name: item.name, adjustment, previousStock: item.stock, newStock });
    } catch (error) {
      console.error('Failed to adjust stock:', error);
    }
  }, [inventory, updateInventory, addAuditLog]);

  const handleStockTake = useCallback(async (physicalCounts: { [itemId: number]: number }) => {
      try {
        for (const [itemId, count] of Object.entries(physicalCounts)) {
          await updateInventory(Number(itemId), { stock: count, lastUpdated: Date.now() });
        }
        addAuditLog('inventory.stock_take', 'inventory', undefined, { itemCount: Object.keys(physicalCounts).length, physicalCounts });
        alert("Stock take complete. All inventory levels have been updated.");
      } catch (error) {
        console.error('Failed to update stock:', error);
        alert('Failed to complete stock take. Please try again.');
      }
  }, [updateInventory, addAuditLog]);

  const handleBulkAddInventoryItems = useCallback(async (items: Omit<InventoryItem, 'id' | 'lastUpdated'>[]): Promise<{ successCount: number, failedItems: string[] }> => {
    let successCount = 0;
    const failedItems: string[] = [];
    
    for (const item of items) {
      try {
        await createInventoryItem({ ...item, lastUpdated: Date.now() });
        successCount++;
      } catch (error) {
        failedItems.push(item.name);
        console.error(`Failed to add inventory item ${item.name}:`, error);
      }
    }
    
    return { successCount, failedItems };
  }, [createInventoryItem]);

  const handleSaveStaffMember = useCallback(async (member: StaffMember & { password?: string }) => {
      try {
        const roleNeedsEmailPassword = member.role?.toLowerCase() === 'manager' 
          || member.role?.toLowerCase() === 'third_party_tester' 
          || member.isAdmin;
        // Match by numeric id (including 0) or by firestoreId so we never treat an edit as create
        const memberFid = (member as { firestoreId?: string }).firestoreId;
        const existingStaff = staff.find(s =>
          s.id === member.id ||
          (!!memberFid && (s as { firestoreId?: string }).firestoreId === memberFid)
        );
        const isExisting = !!existingStaff;
        const updateId = existingStaff
          ? ((existingStaff as { firestoreId?: string }).firestoreId ?? existingStaff.id)
          : member.id;
        const { id, password, ...memberData } = member;
        const staffFirestoreId = String(
          (existingStaff as { firestoreId?: string } | undefined)?.firestoreId
            ?? updateId
            ?? member.id,
        );

        const emailChanged =
          !!member.email &&
          !!existingStaff &&
          (member.email.trim().toLowerCase() !== (existingStaff.email || '').trim().toLowerCase());
        const roleOrAdminChanged =
          !!existingStaff &&
          (member.role !== existingStaff.role || !!member.isAdmin !== !!existingStaff.isAdmin);
        const needsAuthUpdate =
          !!existingStaff?.authUserId && (!!password || emailChanged || roleOrAdminChanged);

        // Auth / role / email / password changes require admin JWT (matches Firestore + callable).
        if ((needsAuthUpdate || (member.password && member.email && roleNeedsEmailPassword)) && !currentUser?.isAdmin) {
          throw new Error('Only admins can change staff login or role.');
        }
        
        // If password is provided and role needs email/password, create/update auth user
        if (member.password && member.email && roleNeedsEmailPassword) {
          const { authService } = await import('./lib/auth');
          
          if (!isExisting) {
            // New staff: create staff record, then create Auth user via Cloud Function (sets claims; admin stays signed in)
            console.log('Creating new staff member with data:', memberData);
            const newStaff = await createStaffMember(memberData);
            console.log('Staff member created:', newStaff);

            const newStaffFirestoreId = (newStaff as { firestoreId?: string }).firestoreId || String(newStaff.id);
            if (!newStaffFirestoreId) {
              throw new Error('Failed to get staff record ID. Staff member may not have been created properly.');
            }

            try {
              await authService.createStaffUserViaFunction({
                email: member.email!,
                password: member.password!,
                staffFirestoreId: newStaffFirestoreId,
                isAdmin: !!member.isAdmin,
                displayName: member.name,
              });
              console.log('Auth user created and claims set via Cloud Function');
            } catch (err: any) {
              const code = err?.code as string | undefined;
              const msg = err?.message || String(err);
              if (code === 'functions/already-exists' || msg.includes('already registered')) {
                throw new Error('This email is already registered. Please use a different email.');
              }
              if (code === 'functions/unauthenticated') {
                throw new Error('You must be signed in as an admin to create staff users.');
              }
              if (code === 'functions/permission-denied') {
                throw new Error('Only admins can create staff users.');
              }
              // Function not deployed or callable failed (e.g. in production)
              if (code === 'functions/not-found' || code === 'functions/unavailable' || msg?.includes('NOT_FOUND') || msg?.toLowerCase().includes('not found')) {
                throw new Error('Could not create login account: the createStaffUser Cloud Function is not deployed or not available. Deploy it with: firebase use production && firebase deploy --only functions. See docs/PRODUCTION_SETUP.md.');
              }
              if (code === 'functions/internal' || code === 'functions/unknown') {
                throw new Error('Could not create login account. The server may be misconfigured. Ensure the createStaffUser Cloud Function is deployed to this project (see docs/PRODUCTION_SETUP.md).');
              }
              throw err;
            }
          } else {
            // Existing staff: update Firestore, then Auth email/password/claims when needed
            await updateStaffMember(updateId, memberData);

            if (existingStaff.authUserId) {
              try {
                await authService.updateStaffAuthUserViaFunction({
                  authUserId: existingStaff.authUserId,
                  staffFirestoreId,
                  email: emailChanged || password ? member.email : undefined,
                  password: password || undefined,
                  isAdmin: !!member.isAdmin,
                  displayName: member.name,
                });
              } catch (err: any) {
                const code = err?.code as string | undefined;
                const msg = err?.message || String(err);
                if (code === 'functions/already-exists' || msg.includes('already registered')) {
                  throw new Error('This email is already registered. Please use a different email.');
                }
                if (code === 'functions/permission-denied') {
                  throw new Error('Only admins can change staff login/role.');
                }
                if (code === 'functions/not-found' || code === 'functions/unavailable' || msg?.toLowerCase().includes('not found')) {
                  throw new Error('Could not update login: deploy the updateStaffAuthUser Cloud Function (firebase deploy --only functions).');
                }
                throw err;
              }
            } else if (password) {
              try {
                await authService.createStaffUserViaFunction({
                  email: member.email!,
                  password,
                  staffFirestoreId,
                  isAdmin: !!member.isAdmin,
                  displayName: member.name,
                });
                console.log('Auth user created for existing staff via Cloud Function');
              } catch (err: any) {
                const code = err?.code as string | undefined;
                const msg = err?.message || String(err);
                if (code === 'functions/already-exists' || msg.includes('already registered')) {
                  throw new Error('This email is already registered. Please use a different email.');
                }
                if (code === 'functions/not-found' || code === 'functions/unavailable' || msg?.toLowerCase().includes('not found')) {
                  throw new Error('Could not create login account: deploy the createStaffUser Cloud Function (see docs/PRODUCTION_SETUP.md).');
                }
                throw err;
              }
            }
          }
        } else {
          // No password provided or role doesn't need email/password
          if (isExisting) {
            await updateStaffMember(updateId, memberData);
            if (needsAuthUpdate && existingStaff?.authUserId) {
              const { authService } = await import('./lib/auth');
              try {
                await authService.updateStaffAuthUserViaFunction({
                  authUserId: existingStaff.authUserId,
                  staffFirestoreId,
                  email: emailChanged ? member.email : undefined,
                  isAdmin: !!member.isAdmin,
                  displayName: member.name,
                });
              } catch (err: any) {
                const code = err?.code as string | undefined;
                if (code === 'functions/permission-denied') {
                  throw new Error('Only admins can change staff login/role.');
                }
                if (code === 'functions/not-found' || code === 'functions/unavailable') {
                  throw new Error('Could not update login: deploy the updateStaffAuthUser Cloud Function.');
                }
                throw err;
              }
            }
          } else {
            await createStaffMember(memberData);
          }
        }

        // Refresh signed-in session when editing the current staff (POS extras flags)
        const editingCurrentUser =
          !!currentUser &&
          (currentUser.id === member.id ||
            (!!existingStaff && existingStaff.id === currentUser.id) ||
            (!!memberFid &&
              (currentUser as { firestoreId?: string }).firestoreId === memberFid));
        if (editingCurrentUser) {
          setCurrentUser((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              ...memberData,
              id: prev.id,
              permissions: prev.permissions,
              canManageMenu: member.canManageMenu === true,
              canManageCustomers: member.canManageCustomers === true,
            };
          });
        }
      } catch (error: any) {
        console.error('Failed to save staff member:', error);
        // Re-throw error so modal can display it
        throw error;
      }
  }, [staff, createStaffMember, updateStaffMember, currentUser?.isAdmin, currentUser?.id, currentUser]);
  
  const handleDeleteStaffMember = useCallback(async (memberId: number | string) => {
      try {
        console.log('🗑️ App: Deleting staff member with ID:', memberId);
        await removeStaffMember(memberId);
        console.log('✅ App: Staff member deleted successfully');
        // The staff list will automatically update via the real-time listener in useStaff hook
      } catch (error) {
        console.error('❌ App: Failed to delete staff member:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to delete staff member';
        alert(`Failed to delete staff member: ${errorMessage}`);
        throw error; // Re-throw so the UI can handle it
      }
  }, [removeStaffMember]);

  const handleSendStaffInvitation = useCallback(async (email: string) => {
    try {
      const { authService } = await import('./lib/auth');
      await authService.sendInvitation(email);
    } catch (error) {
      console.error('Failed to send staff invitation:', error);
      throw error;
    }
  }, []);

  const handleSaveCustomer = useCallback(async (customer: Customer) => {
      try {
        if (customer.id && customers.some(c => c.id === customer.id)) {
          await updateCustomer(customer.id, customer);
        } else {
          const { id, ...customerData } = customer;
          await createCustomer({ ...customerData, loyaltyPoints: customerData.loyaltyPoints || 0 });
        }
      } catch (error) {
        console.error('Failed to save customer:', error);
        alert('Failed to save customer. Please try again.');
      }
  }, [customers, createCustomer, updateCustomer]);

  const handleDeleteCustomer = useCallback(async (customerId: number) => {
      try {
        await removeCustomer(customerId);
      } catch (error) {
        console.error('Failed to delete customer:', error);
        alert('Failed to delete customer. Please try again.');
      }
  }, [removeCustomer]);
  
  const handleSaveReward = useCallback(async (reward: LoyaltyReward) => {
      try {
        await saveLoyaltyReward(reward);
      } catch (error) {
        console.error('Failed to save loyalty reward:', error);
        alert('Failed to save loyalty reward. Please try again.');
      }
  }, [saveLoyaltyReward]);

  const handleDeleteReward = useCallback(async (rewardId: number) => {
      try {
        await removeLoyaltyReward(rewardId);
      } catch (error) {
        console.error('Failed to delete loyalty reward:', error);
        alert('Failed to delete loyalty reward. Please try again.');
      }
  }, [removeLoyaltyReward]);

  const handleSaveTable = useCallback(async (table: Omit<Table, 'status'>) => {
    try {
      if (table.id && tables.some(t => t.id === table.id)) {
        await updateTable(table.id, { name: table.name, capacity: table.capacity, shape: table.shape || 'square' });
      } else {
        const { id, ...tableData } = table;
        await createTable({ ...tableData, status: 'available', shape: table.shape || 'square' });
      }
    } catch (error) {
      console.error('Failed to save table:', error);
      alert('Failed to save table. Please try again.');
    }
  }, [tables, createTable, updateTable]);

  const handleDeleteTable = useCallback(async (tableId: number) => {
    try {
      await removeTable(tableId);
    } catch (error) {
      console.error('Failed to delete table:', error);
      alert('Failed to delete table. Please try again.');
    }
  }, [removeTable]);
  
  const handleUpdateTablePosition = useCallback(async (tableId: number, x: number, y: number) => {
    try {
      await updateTable(tableId, { x, y });
    } catch (error) {
      console.error('Failed to update table position:', error);
    }
  }, [updateTable]);

  const handleMergeTables = useCallback(async (
    tableIds: number[],
    options?: { allowReservedOrOccupied?: boolean; silent?: boolean },
  ): Promise<boolean> => {
    const silent = options?.silent === true;
    const allowReservedOrOccupied = options?.allowReservedOrOccupied === true;
    try {
      const normalizedIds = [...new Set(tableIds.map(id => Number(id)))];
      if (normalizedIds.length < 2) {
        if (!silent) alert('Please select at least 2 distinct tables to merge.');
        return false;
      }

      // Get all tables to merge
      const tablesToMerge = normalizedIds
        .map(id => tables.find(t => Number(t.id) === id))
        .filter(Boolean) as Table[];

      if (tablesToMerge.length !== normalizedIds.length) {
        if (!silent) alert('Some selected tables were not found. Refresh the floor plan and try again.');
        return false;
      }

      const todayKey = new Date().toDateString();

      const alreadyInMerge = tablesToMerge.filter(
        t => t.mergedWith && Array.isArray(t.mergedWith) && t.mergedWith.length > 1,
      );
      if (alreadyInMerge.length > 0) {
        // Already one group covering exactly these ids — treat as success for seating.
        const primary = alreadyInMerge[0];
        const mergedIds = (primary.mergedWith ?? []).map(Number).sort((a, b) => a - b);
        const wanted = [...normalizedIds].sort((a, b) => a - b);
        if (
          alreadyInMerge.length === tablesToMerge.length &&
          mergedIds.length === wanted.length &&
          mergedIds.every((id, i) => id === wanted[i])
        ) {
          return true;
        }
        if (!silent) {
          const names = alreadyInMerge.map(t => `"${t.name}"`).join(', ');
          alert(
            `Cannot merge: ${names} ${alreadyInMerge.length === 1 ? 'is' : 'are'} already part of a merged group. Split that group first, then merge again.`,
          );
        }
        return false;
      }

      // Validate: Check for active orders
      for (const table of tablesToMerge) {
        const hasActiveOrder = orders.some(order => isActiveTableOrder(order, table.id, todayKey));
        if (hasActiveOrder) {
          if (!silent) {
            alert(`Cannot merge: "${table.name}" has an active order for today. Finish or cancel it first.`);
          }
          return false;
        }
        // Arrival merge: only block on active orders (reserved/occupied/amber tables are fine).
        if (!allowReservedOrOccupied && table.status !== 'available') {
          if (!silent) {
            alert(
              `Cannot merge: "${table.name}" is ${table.status}. Only available tables can be merged.`,
            );
          }
          return false;
        }
      }

      // Use first table as primary
      const primaryTable = tablesToMerge[0];
      const otherTables = tablesToMerge.slice(1);

      // Calculate combined capacity
      const combinedCapacity = tablesToMerge.reduce((sum, table) => sum + table.capacity, 0);

      // Generate combined name
      const combinedName = tablesToMerge.map(t => t.name).join('+');

      // Get IDs of all merged tables (including primary)
      const allMergedIds = tablesToMerge.map(t => t.id);

      // Update primary table with merged info (store original properties)
      await updateTable(primaryTable.id, {
        name: combinedName,
        capacity: combinedCapacity,
        originalName: primaryTable.originalName || primaryTable.name,
        originalCapacity: primaryTable.originalCapacity || primaryTable.capacity,
        mergedWith: allMergedIds,
      });

      // Update other tables: store original properties and mark as merged
      for (const table of otherTables) {
        await updateTable(table.id, {
          originalName: table.originalName || table.name,
          originalCapacity: table.originalCapacity || table.capacity,
          mergedWith: allMergedIds,
          status: 'available', // Keep as available but visually merged
        });
      }

      console.log(`✅ Successfully merged tables: ${combinedName}`);
      return true;
    } catch (error) {
      console.error('Failed to merge tables:', error);
      if (!silent) alert(`Failed to merge tables. ${describeMergeSplitFirestoreError(error)}`);
      return false;
    }
  }, [tables, orders, updateTable, isActiveTableOrder]);

  const handleSplitTables = useCallback(async (
    mergedTableId: number,
    options?: { silent?: boolean },
  ) => {
    const silent = options?.silent === true;
    try {
      const mergedTable = tables.find(t => Number(t.id) === Number(mergedTableId));
      if (!mergedTable || !mergedTable.mergedWith || mergedTable.mergedWith.length < 2) {
        if (!silent) alert('This table is not part of a merged group.');
        return;
      }

      const mergedIdNums = mergedTable.mergedWith.map(id => Number(id));

      // Validate against every physical table in the merge (tickets usually use primary)
      const todayKey = new Date().toDateString();
      const hasActiveOrder = orders.some(order =>
        mergedIdNums.some(id => isActiveTableOrder(order, id, todayKey)),
      );
      if (hasActiveOrder) {
        if (!silent) {
          alert(
            'Cannot split: this merged group still has an active table order for today. Complete payment or cancel the order first.',
          );
        }
        return;
      }

      // Get all tables in the merge
      const mergedTableIds = mergedTable.mergedWith;
      const allMergedTables = mergedTableIds
        .map(id => tables.find(t => Number(t.id) === Number(id)))
        .filter(Boolean) as Table[];

      if (allMergedTables.length !== mergedTableIds.length) {
        if (!silent) {
          alert(
            'Some physical tables in this merge are missing from the floor plan. Refresh or fix tables in settings, then try again.',
          );
        }
        return;
      }

      const restoredNameByTableId = new Map<number, string>();
      for (const table of allMergedTables) {
        const restored = table.originalName || table.name;
        restoredNameByTableId.set(Number(table.id), restored);
      }

      // Restore original properties for all tables
      for (const table of allMergedTables) {
        const originalName = table.originalName || table.name;
        const originalCapacity = table.originalCapacity || table.capacity;
        
        // Update table with restored properties and clear mergedWith
        await updateTable(table.id, {
          name: originalName,
          capacity: originalCapacity,
          mergedWith: [], // Clear mergedWith by setting to empty array
          status: 'available',
        });
      }
      
      // In a second pass, clear the original fields (if database supports it)
      // Some databases might need this as a separate operation
      try {
        for (const table of allMergedTables) {
          if (table.originalName || table.originalCapacity) {
            // Try to clear original fields - this might not work in all databases
            // but it's safe to try
            await updateTable(table.id, {
              originalName: null as any,
              originalCapacity: null as any,
            } as any);
          }
        }
      } catch (error) {
        // If clearing original fields fails, that's okay - they're just metadata
        console.log('Note: Could not clear original table fields (this is optional)');
      }

      const orderPatches: { id: string; tableName: string }[] = [];
      for (const order of orders) {
        if (order.orderType !== 'dine-in' && order.orderType !== 'buffet') continue;
        const tid = order.tableId != null ? Number(order.tableId) : NaN;
        if (!mergedIdNums.includes(tid)) continue;
        if (new Date(order.timestamp).toDateString() !== todayKey) continue;
        const correctName = restoredNameByTableId.get(tid);
        if (correctName == null || order.tableName === correctName) continue;
        orderPatches.push({ id: order.id, tableName: correctName });
      }
      if (orderPatches.length > 0) {
        try {
          await Promise.all(orderPatches.map(p => updateOrder(p.id, { tableName: p.tableName })));
        } catch (patchErr) {
          console.error('Split tables: failed to refresh order table labels for kitchen display:', patchErr);
          if (!silent) {
            alert(
              `Tables were split, but updating ticket labels failed: ${describeMergeSplitFirestoreError(patchErr)}. Refresh the POS; kitchen tickets should fix after orders reload.`,
            );
          }
        }
      }

      console.log(`✅ Successfully split merged table: ${mergedTable.name}`);
    } catch (error) {
      console.error('Failed to split tables:', error);
      if (!silent) alert(`Failed to split tables. ${describeMergeSplitFirestoreError(error)}`);
    }
  }, [tables, orders, updateTable, isActiveTableOrder, updateOrder]);

  /**
   * After payment (dine-in) or buffet session end: if the order's table is in a physical
   * merge and no floor-active tickets remain on that group, split silently.
   * Does not unmerge while an active buffet session is still dining.
   */
  const maybeAutoUnmergeMergedTables = useCallback(async (order: Order) => {
    if (order.orderType !== 'dine-in' && order.orderType !== 'buffet') return;
    if (order.tableId == null) return;
    if (order.orderType === 'buffet' && isActiveBuffetSession(order)) return;

    const tableId = Number(order.tableId);
    const table = tables.find(t => Number(t.id) === tableId);
    if (!table?.mergedWith || table.mergedWith.length < 2) return;

    const primaryId = Number(table.mergedWith[0]);
    const mergeIds = table.mergedWith.map(id => Number(id));
    const todayKey = new Date().toDateString();
    const stillActive = orders.some(
      o => o.id !== order.id && mergeIds.some(id => isActiveTableOrder(o, id, todayKey)),
    );
    if (stillActive) {
      console.log(`⏭️ Skip auto-unmerge: merge group for table ${tableId} still has active order(s)`);
      return;
    }

    console.log(`🔓 Auto-unmerging tables after order ${order.id} cleared floor`);
    await handleSplitTables(primaryId, { silent: true });
  }, [tables, orders, isActiveTableOrder, handleSplitTables]);

  useEffect(() => {
    maybeAutoUnmergeMergedTablesRef.current = maybeAutoUnmergeMergedTables;
  }, [maybeAutoUnmergeMergedTables]);

  const handleExtendBuffetTime = useCallback(async (orderId: string, extensionMinutes: number, reason?: string) => {
    const order = orders.find(o => o.id === orderId);
    if (!order || order.orderType !== 'buffet' || !order.buffetEndTime) {
      alert('Invalid buffet order for time extension.');
      return;
    }

    try {
      const extensionMs = extensionMinutes * 60 * 1000;
      const newEndTime = order.buffetEndTime + extensionMs;
      const currentExtendedTime = order.buffetExtendedTime || 0;
      const totalExtendedTime = currentExtendedTime + extensionMs;

      const updatedOrder: Order = {
        ...order,
        buffetEndTime: newEndTime,
        buffetExtendedTime: totalExtendedTime,
        buffetExtendedBy: currentUser?.id,
        buffetExtensionReason: reason,
      };

      await updateOrder(removeUndefined(updatedOrder));
      console.log(`✅ Buffet time extended by ${extensionMinutes} minutes for order ${orderId}`);
    } catch (error) {
      console.error('❌ Failed to extend buffet time:', error);
      alert('Failed to extend buffet time. Please try again.');
      throw error;
    }
  }, [orders, updateOrder, currentUser]);

  const handleTransferOrder = useCallback(async (orderId: string, targetTableId: number) => {
    try {
      const order = orders.find(o => o.id === orderId);
      if (!order || (order.orderType !== 'dine-in' && order.orderType !== 'buffet')) {
        alert('Order not found or not a dine-in/buffet order.');
        return;
      }

      const sourceTable = tables.find(t => t.id === order.tableId);
      const targetTable = tables.find(t => t.id === targetTableId);

      if (!sourceTable || !targetTable) {
        alert('Source or target table not found.');
        return;
      }

      // Validate: Source table must have active order
      const todayKey = new Date().toDateString();
      const hasActiveOrder = orders.some(o => isActiveTableOrder(o, sourceTable.id, todayKey) && o.id === orderId);
      if (!hasActiveOrder) {
        alert('Source table does not have an active order.');
        return;
      }

      // Validate: Target table must be available
      if (targetTable.status !== 'available') {
        alert('Target table is not available.');
        return;
      }

      // Keep tableId and tableNumber aligned so floor-plan matching stays unambiguous.
      await updateOrder(orderId, {
        tableId: targetTableId,
        tableName: targetTable.name,
        tableNumber: targetTableId,
      });

      // Move seated reservations with the order — otherwise the source table stays floor-red
      // (displayStatus treats status=seated as occupied even with no active order).
      const tz = integrationSettings?.businessHours?.timezone || 'Pacific/Auckland';
      const todayNz = new Date().toLocaleDateString('en-CA', { timeZone: tz });
      const todayUtc = new Date().toISOString().split('T')[0];
      const seatedToMove = seatedReservationsToMoveOnOrderTransfer(
        reservations,
        sourceTable.id,
        [todayNz, todayUtc],
      );
      for (const r of seatedToMove) {
        const resUpdates = buildReservationUpdatesForOrderTransfer(r, sourceTable.id, targetTableId);
        if (!resUpdates) continue;
        try {
          const oldHeldIds = [
            ...(r.tableId ? [Number(r.tableId)] : []),
            ...(r.assignedTableIds ?? []).map(Number),
          ].filter((n) => Number.isFinite(n) && n > 0);
          await updateReservation(r.id, resUpdates);
          // Free tables the booking no longer holds (join siblings + the source).
          for (const oldTid of new Set(oldHeldIds)) {
            if (Number(oldTid) === Number(targetTableId)) continue;
            const stillHasOrder = orders.some(
              (o) => o.id !== orderId && isActiveTableOrder(o, oldTid, todayKey),
            );
            const stillSeated = tableHasSeatedReservation(reservations, oldTid, todayNz, r.id)
              || tableHasSeatedReservation(reservations, oldTid, todayUtc, r.id);
            if (!stillHasOrder && !stillSeated) {
              await updateTable(oldTid, { status: 'available' });
            }
          }
          console.log(
            `✅ Reservation ${r.id} (${r.customerName}) moved with order from ${sourceTable.name} to ${targetTable.name}`,
          );
        } catch (resErr) {
          console.error(`Failed to move reservation ${r.id} with transfer:`, resErr);
        }
      }

      // Update table statuses
      // Check if source table has other active orders
      const otherActiveOrders = orders.filter(o => 
        o.id !== orderId &&
        isActiveTableOrder(o, sourceTable.id, todayKey)
      );

      if (otherActiveOrders.length === 0) {
        await updateTable(sourceTable.id, { status: 'available' });
      }

      await updateTable(targetTableId, { status: 'occupied' });

      console.log(`✅ Successfully transferred order from ${sourceTable.name} to ${targetTable.name}`);
    } catch (error) {
      console.error('Failed to transfer order:', error);
      alert('Failed to transfer order. Please try again.');
    }
  }, [orders, tables, reservations, updateOrder, updateReservation, updateTable, isActiveTableOrder, integrationSettings?.businessHours?.timezone]);

  // Fire mode state
  // Manager override handler
  const handleOverrideOrder = useCallback(async (orderId: string, reason: string) => {
    if (!currentUser) {
      alert('You must be logged in to override orders.');
      return;
    }

    // Check if user has manager permissions (access_reports or manage_staff typically indicates manager)
    const isManager = currentUser.permissions.has('access_reports') || 
                      currentUser.permissions.has('manage_staff') ||
                      currentUser.isAdmin;
    
    if (!isManager) {
      alert('Only managers can override orders.');
      return;
    }

    try {
      const order = orders.find(o => o.id === orderId);
      if (!order) {
        alert('Order not found.');
        return;
      }

      // Mark order as comped
      await updateOrder(orderId, {
        status: 'comped',
        paymentStatus: 'paid', // Mark as paid (comped)
        isComped: true,
        overrideReason: reason,
        overriddenBy: currentUser.id,
        overrideTimestamp: Date.now(),
      });

      // Still deduct inventory when not in cost-only mode (only inventory-linked ingredients)
      if (order.paymentStatus === 'unpaid' && integrationSettings?.recipeInventoryMode !== 'cost_only') {
        order.items.forEach(orderItem => {
          const recipe = recipes.find(r => r.menuItemId === orderItem.id);
          if (recipe) {
            recipe.ingredients.forEach(ingredient => {
              if (ingredient.inventoryItemId == null) return;
              const invItem = inventory.find(i => i.id === ingredient.inventoryItemId);
              if (invItem) {
                const newStock = invItem.stock - (ingredient.quantity * orderItem.quantity);
                updateInventory(invItem.id, {
                  stock: Math.max(0, newStock),
                  lastUpdated: Date.now()
                });
              }
            });
          }
        });
      }

      if ((order.orderType === 'dine-in' || order.orderType === 'buffet') && order.tableId != null) {
        const tableId = Number(order.tableId);
        const otherActive = orders.some(
          (o) => o.id !== orderId && isActiveTableOrder(o, tableId),
        );
        if (!otherActive) {
          await updateTable(tableId, { status: 'available' });
        }
      }

      console.log(`✅ Order ${orderId} overridden by manager ${currentUser.name} with reason: ${reason}`);
    } catch (error) {
      console.error('Failed to override order:', error);
      alert('Failed to override order. Please try again.');
    }
  }, [orders, currentUser, updateOrder, updateTable, recipes, inventory, updateInventory, integrationSettings?.recipeInventoryMode, isActiveTableOrder]);

  // Fire mode handlers
  const handleActivateFireMode = useCallback(async () => {
    if (!currentUser) {
      alert('You must be logged in to activate fire mode.');
      return;
    }

    const isManager = currentUser.permissions.has('access_reports') || 
                      currentUser.permissions.has('manage_staff') ||
                      currentUser.isAdmin;
    
    if (!isManager) {
      alert('Only managers can activate fire mode.');
      return;
    }

    const fireModeSettings = integrationSettings.fireMode;
    const orderAction = fireModeSettings?.orderAction || 'pause';

    // Handle active orders based on configured action
    const todayKey = new Date().toDateString();
    const activeOrders = orders.filter(o => {
      if (o.paymentStatus === 'paid') return false;
      if (o.status === 'comped') return false;
      const orderDateKey = new Date(o.timestamp).toDateString();
      return orderDateKey === todayKey;
    });

    for (const order of activeOrders) {
      if (orderAction === 'pause') {
        await updateOrder(order.id, { status: 'paused' });
      } else if (orderAction === 'cancel') {
        await updateOrder(order.id, { status: 'cancelled' });
        if ((order.orderType === 'dine-in' || order.orderType === 'buffet') && order.tableId != null) {
          await updateTable(Number(order.tableId), { status: 'available' });
        }
      } else if (orderAction === 'hold') {
        await updateOrder(order.id, { status: 'held' });
      }
    }

    setFireModeActive(true);
    console.log('🔥 Fire evacuation mode activated');
  }, [currentUser, orders, updateOrder, updateTable, integrationSettings]);

  const handleDeactivateFireMode = useCallback(async () => {
    if (!currentUser) {
      alert('You must be logged in to deactivate fire mode.');
      return;
    }

    const isManager = currentUser.permissions.has('access_reports') || 
                      currentUser.permissions.has('manage_staff') ||
                      currentUser.isAdmin;
    
    if (!isManager) {
      alert('Only managers can deactivate fire mode.');
      return;
    }

    const fireModeSettings = integrationSettings.fireMode;
    const orderAction = fireModeSettings?.orderAction || 'pause';

    // Process orders based on configured action
    const todayKey = new Date().toDateString();
    const activeOrders = orders.filter(o => {
      if (o.paymentStatus === 'paid') return false;
      const orderDateKey = new Date(o.timestamp).toDateString();
      return orderDateKey === todayKey;
    });

    for (const order of activeOrders) {
      if (order.status === 'queued') {
        // Resume queued orders
        await updateOrder(order.id, { status: 'new' });
      } else if (order.status === 'paused') {
        // Resume paused orders
        await updateOrder(order.id, { status: order.status === 'paused' ? 'preparing' : 'new' });
      } else if (order.status === 'held') {
        // Process held orders
        await updateOrder(order.id, { status: 'new' });
      }
    }

    setFireModeActive(false);
    console.log('✅ Fire evacuation mode deactivated');
  }, [currentUser, orders, updateOrder, integrationSettings]);

  // Remake order handler
  const handleRemakeOrder = useCallback(async (orderId: string, itemsToRemake: OrderItem[], reason: string) => {
    try {
      const order = orders.find(o => o.id === orderId);
      if (!order) {
        alert('Order not found.');
        return;
      }

      // Create remake items (copy of original items with remake flags)
      const remakeItems: OrderItem[] = itemsToRemake.map(item => ({
        ...item,
        id: item.id, // Keep same menu item ID
        isRemake: true,
        remakeReason: reason,
        remakeOfItemId: item.id,
        price: 0, // Free remake
        status: 'pending',
        served: false,
      }));

      // Add remake items to existing order
      const updatedItems = [...order.items, ...remakeItems];

      // Recalculate totals (remake items are $0)
      const subtotal = updatedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      const taxAmount = subtotal * 0.15; // Assuming 15% tax
      const totalAmount = subtotal + taxAmount;

      await updateOrder(orderId, {
        items: updatedItems,
        subtotal,
        taxAmount,
        totalAmount,
      });

      // Deduct inventory for remake items (only when not cost-only; only inventory-linked ingredients)
      if (integrationSettings?.recipeInventoryMode !== 'cost_only') {
        remakeItems.forEach(orderItem => {
          const recipe = recipes.find(r => r.menuItemId === orderItem.id);
          if (recipe) {
            recipe.ingredients.forEach(ingredient => {
              if (ingredient.inventoryItemId == null) return;
              const invItem = inventory.find(i => i.id === ingredient.inventoryItemId);
              if (invItem) {
                const newStock = invItem.stock - (ingredient.quantity * orderItem.quantity);
                updateInventory(invItem.id, {
                  stock: Math.max(0, newStock),
                  lastUpdated: Date.now()
                });
              }
            });
          }
        });
      }

      console.log(`✅ Remade ${remakeItems.length} items for order ${orderId} with reason: ${reason}`);
    } catch (error) {
      console.error('Failed to remake order:', error);
      alert('Failed to remake order. Please try again.');
    }
  }, [orders, updateOrder, recipes, inventory, updateInventory, integrationSettings?.recipeInventoryMode]);

  const handleUpdateFixture = useCallback(async (fixtureId: string, updates: Partial<FloorFixture>) => {
    try {
      await updateFloorFixture(fixtureId, updates);
    } catch (error) {
      console.error('Failed to update layout component:', error);
      alert('Failed to update layout component. Please try again.');
    }
  }, [updateFloorFixture]);

  const handleUpdateFixturePosition = useCallback(async (fixtureId: string, x: number, y: number) => {
    try {
      await updateFloorFixture(fixtureId, { x, y });
    } catch (error) {
      console.error('Failed to move layout component:', error);
      alert('Failed to move layout component. Please try again.');
    }
  }, [updateFloorFixture]);

  const handleCreateFixture = useCallback(async (type: FloorFixture['type']) => {
    try {
      const componentTypeOptions = [
        { value: 'entrance' as const, defaultSize: { width: 30, height: 5 } },
        { value: 'bar' as const, defaultSize: { width: 8, height: 60 } },
        { value: 'patio' as const, defaultSize: { width: 30, height: 25 } },
        { value: 'kitchen' as const, defaultSize: { width: 20, height: 20 } },
        { value: 'restroom' as const, defaultSize: { width: 10, height: 10 } },
        { value: 'storage' as const, defaultSize: { width: 15, height: 15 } },
        { value: 'counter' as const, defaultSize: { width: 25, height: 8 } },
        { value: 'window' as const, defaultSize: { width: 20, height: 5 } },
        { value: 'wall' as const, defaultSize: { width: 5, height: 100 } },
        { value: 'custom' as const, defaultSize: { width: 15, height: 15 } },
      ];
      const defaultSize = componentTypeOptions.find(opt => opt.value === type)?.defaultSize || { width: 15, height: 15 };
      const labels: Record<FloorFixture['type'], string> = {
        entrance: 'Entrance',
        bar: 'Bar',
        patio: 'Patio',
        kitchen: 'Kitchen',
        restroom: 'Restroom',
        storage: 'Storage',
        counter: 'Counter',
        window: 'Window',
        wall: 'Wall',
        custom: 'Custom',
      };
      await createFloorFixture({
        type,
        label: labels[type],
        x: 50,
        y: 50,
        width: defaultSize.width,
        height: defaultSize.height,
        rotation: 0,
      });
    } catch (error) {
      console.error('Failed to create layout component:', error);
      alert('Failed to create layout component. Please try again.');
    }
  }, [createFloorFixture]);

  const handleDeleteFixture = useCallback(async (fixtureId: string) => {
    try {
      await removeFloorFixture(fixtureId);
    } catch (error) {
      console.error('Failed to delete layout component:', error);
      alert('Failed to delete layout component. Please try again.');
    }
  }, [removeFloorFixture]);

  const handleSaveReservation = useCallback(async (reservation: Omit<Reservation, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      await createReservation(reservation);
      const now = new Date();
      const asReservation = reservation as Reservation;
      if (isReservationSoonForFloor({ ...asReservation, status: reservation.status, id: 0 } as Reservation, now)) {
        if (reservation.tableId) {
          await updateTable(reservation.tableId, { status: 'reserved' });
        }
        if (reservation.assignedTableIds?.length) {
          for (const tid of reservation.assignedTableIds) {
            await updateTable(tid, { status: 'reserved' });
          }
        }
      }
    } catch (error) {
      console.error('Failed to save reservation:', error);
      alert('Failed to save reservation. Please try again.');
      throw error;
    }
  }, [createReservation, updateTable]);

  const handleUpdateReservation = useCallback(async (id: number, updates: Partial<Reservation>) => {
    try {
      const reservation = reservations.find(r => r.id === id);
      if (!reservation) return;

      await updateReservation(id, updates);

      const today =
        new Date().toLocaleDateString('en-CA', {
          timeZone: integrationSettings?.businessHours?.timezone || 'Pacific/Auckland',
        });
      const reservationDate = updates.reservationDate ?? reservation.reservationDate;
      const isToday = reservationDate === today;
      const tableIdsToConsider = [
        ...(reservation.tableId ? [Number(reservation.tableId)] : []),
        ...(reservation.assignedTableIds ?? []).map(Number),
      ].filter((n) => Number.isFinite(n) && n > 0);
      const updatedTableId = updates.tableId ?? reservation.tableId;
      const updatedAssignedIds = updates.assignedTableIds ?? reservation.assignedTableIds;
      const updatedTableIds = [
        ...(updatedTableId ? [Number(updatedTableId)] : []),
        ...(updatedAssignedIds ?? []).map(Number),
      ].filter((n) => Number.isFinite(n) && n > 0);

      const now = new Date();

      const tableStillHeld = (tableId: number) => {
        if (orders.some((o) => isActiveTableOrder(o, tableId))) return true;
        // Exclude the reservation being updated (e.g. seated → completed).
        if (tableHasSeatedReservation(reservations, tableId, today, id)) return true;
        return reservations.some(
          (r) =>
            r.id !== id &&
            isReservationSoonForFloor(r, now) &&
            reservationHoldsTable(r, tableId),
        );
      };

      const maybeFreeTable = async (tid: number) => {
        if (tableStillHeld(tid)) return;
        const table = tables.find((t) => Number(t.id) === Number(tid));
        if (table?.status === 'reserved' || table?.status === 'occupied') {
          await updateTable(tid, { status: 'available' });
        }
      };

      if (isToday && updates.status) {
        if (updates.status === 'seated') {
          const joinIds = [
            ...new Set(
              (updatedAssignedIds && updatedAssignedIds.length > 0
                ? updatedAssignedIds.map(Number)
                : tableIdsToConsider
              ).filter((n) => Number.isFinite(n) && n > 0),
            ),
          ];
          const shouldMerge = joinIds.length >= 2;

          if (shouldMerge) {
            // Physical merge on arrival — do this before occupying so staff don't need a second button.
            const merged = await handleMergeTables(joinIds, {
              allowReservedOrOccupied: true,
              silent: false,
            });
            if (!merged) {
              console.warn('Seat customer: physical merge did not complete; marking tables occupied');
            }
          }

          const occupyIds = joinIds.length > 0 ? joinIds : tableIdsToConsider;
          for (const tid of occupyIds) {
            await updateTable(tid, { status: 'occupied' });
          }
        } else if (updates.status === 'cancelled' || updates.status === 'completed') {
          for (const tid of tableIdsToConsider) {
            await maybeFreeTable(tid);
          }
        } else if (updates.status === 'pending' || updates.status === 'confirmed') {
          for (const tid of updatedTableIds) {
            const hasOrder = orders.some((o) => isActiveTableOrder(o, tid));
            const hasSeated = tableHasSeatedReservation(reservations, tid, today, id);
            const soon = isReservationSoonForFloor(
              { ...reservation, ...updates, status: updates.status } as Reservation,
              now,
            );
            if (!hasOrder && !hasSeated && soon) {
              await updateTable(tid, { status: 'reserved' });
            }
          }
        }
      } else if (!isToday) {
        for (const tid of tableIdsToConsider) {
          await maybeFreeTable(tid);
        }
      }

      // Table reassignment (e.g. transfer moved a booking): free old holds, merge/occupy new.
      if (
        isToday &&
        (updates.tableId != null || updates.assignedTableIds != null) &&
        updates.status !== 'cancelled' &&
        updates.status !== 'completed'
      ) {
        const statusAfter = updates.status ?? reservation.status;
        const newIdSet = new Set(updatedTableIds);
        for (const tid of tableIdsToConsider) {
          if (!newIdSet.has(tid)) {
            await maybeFreeTable(tid);
          }
        }
        if (statusAfter === 'seated') {
          const joinIds = [...newIdSet];
          if (joinIds.length >= 2) {
            const merged = await handleMergeTables(joinIds, {
              allowReservedOrOccupied: true,
              silent: false,
            });
            if (!merged) {
              console.warn('Transfer: physical merge of new tables did not complete; marking occupied');
            }
          }
          for (const tid of newIdSet) {
            await updateTable(tid, { status: 'occupied' });
          }
        } else if (statusAfter === 'pending' || statusAfter === 'confirmed') {
          const soon = isReservationSoonForFloor(
            { ...reservation, ...updates, status: statusAfter } as Reservation,
            now,
          );
          if (soon) {
            for (const tid of newIdSet) {
              const hasOrder = orders.some((o) => isActiveTableOrder(o, tid));
              if (!hasOrder) {
                await updateTable(tid, { status: 'reserved' });
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Failed to update reservation:', error);
      alert('Failed to update reservation. Please try again.');
      throw error;
    }
  }, [reservations, tables, orders, updateReservation, updateTable, isActiveTableOrder, handleMergeTables, integrationSettings?.businessHours?.timezone]);

  /** Move a pending/confirmed/seated reservation to another table (or join set) before any order exists. */
  const handleTransferReservation = useCallback(async (
    reservationId: number,
    targetTableId: number,
    sourceTableId?: number,
    options?: { assignedTableIds?: number[] },
  ) => {
    try {
      const reservation = reservations.find((r) => r.id === reservationId);
      if (!reservation) {
        alert('Reservation not found.');
        return;
      }
      if (!RESERVATION_TABLE_TRANSFER_STATUSES.includes(reservation.status)) {
        alert('Only pending, confirmed, or seated reservations can be transferred.');
        return;
      }

      const joinTargets = (options?.assignedTableIds ?? [])
        .map(Number)
        .filter((n) => Number.isFinite(n) && n > 0);
      const uniqueJoin = [...new Set(joinTargets)];
      const isJoinTransfer = uniqueJoin.length >= 2;

      const targetIds = isJoinTransfer ? uniqueJoin : [Number(targetTableId)];
      for (const tid of targetIds) {
        const targetTable = tables.find((t) => Number(t.id) === Number(tid));
        if (!targetTable) {
          alert('Target table not found.');
          return;
        }
        const heldByThis = reservationHoldsTable(reservation, tid);
        if (!heldByThis && targetTable.status !== 'available') {
          alert(`Target table "${targetTable.name}" is not available.`);
          return;
        }
      }

      const primaryId = Number(reservation.tableId);
      const heldSource =
        sourceTableId != null && reservationHoldsTable(reservation, sourceTableId)
          ? Number(sourceTableId)
          : primaryId;
      if (!Number.isFinite(heldSource) || heldSource <= 0) {
        alert('Reservation has no assigned table.');
        return;
      }
      if (!isJoinTransfer && Number(heldSource) === Number(targetTableId)) {
        alert('Choose a different table.');
        return;
      }

      const todayKey = new Date().toDateString();
      const oldHeldIds = [
        ...(reservation.tableId ? [Number(reservation.tableId)] : []),
        ...(reservation.assignedTableIds ?? []).map(Number),
      ].filter((n) => Number.isFinite(n) && n > 0);
      for (const oldTid of new Set(oldHeldIds)) {
        const orderOnSource = orders.some((o) => isActiveTableOrder(o, oldTid, todayKey));
        if (orderOnSource) {
          alert('This table has an active order. Use Transfer on the order instead.');
          return;
        }
      }

      const primaryTarget = isJoinTransfer
        ? (uniqueJoin.includes(Number(targetTableId)) ? Number(targetTableId) : uniqueJoin[0])
        : Number(targetTableId);

      const resUpdates = buildReservationUpdatesForTableTransfer(
        reservation,
        heldSource,
        primaryTarget,
        isJoinTransfer ? { assignedTableIds: uniqueJoin } : undefined,
      );
      if (!resUpdates) {
        alert('Could not transfer this reservation.');
        return;
      }

      // Seated join moves: split the old physical merge first so the new merge can succeed.
      if (reservation.status === 'seated') {
        const leavingIds = oldHeldIds.filter((id) => !targetIds.includes(Number(id)));
        for (const tid of leavingIds) {
          const t = tables.find((x) => Number(x.id) === Number(tid));
          if (t?.mergedWith && t.mergedWith.length > 1) {
            await handleSplitTables(Number(t.mergedWith[0]), { silent: true });
            break;
          }
        }
      }

      await handleUpdateReservation(reservationId, resUpdates);

      const label = isJoinTransfer
        ? uniqueJoin
            .map((id) => tables.find((t) => Number(t.id) === Number(id))?.name ?? `Table ${id}`)
            .join(' + ')
        : tables.find((t) => Number(t.id) === Number(primaryTarget))?.name ?? String(primaryTarget);
      console.log(
        `✅ Reservation ${reservationId} (${reservation.customerName}) transferred to ${label}`,
      );
    } catch (error) {
      console.error('Failed to transfer reservation:', error);
      alert('Failed to transfer reservation. Please try again.');
    }
  }, [reservations, tables, orders, isActiveTableOrder, handleUpdateReservation, handleSplitTables]);

  /**
   * Mark today's reservation on this table as completed when payment is finished
   * (or when the visit is served — idempotent if already completed).
   */
  const completeSeatedReservationsForOrder = useCallback(async (order: Order) => {
    if (order.orderType !== 'dine-in' && order.orderType !== 'buffet') return;

    const tableId = order.tableId != null ? Number(order.tableId) : NaN;
    if (!Number.isFinite(tableId)) return;

    const tz = integrationSettings?.businessHours?.timezone || 'Pacific/Auckland';
    const todayNz = new Date().toLocaleDateString('en-CA', { timeZone: tz });
    const todayUtc = new Date().toISOString().split('T')[0];

    const table = tables.find((t) => Number(t.id) === tableId);
    const relatedIds = new Set<number>([tableId]);
    if (table?.mergedWith?.length) {
      for (const id of table.mergedWith) relatedIds.add(Number(id));
    }
    // Also include original physical ids from the reservation side via any open hold on related tables.

    const completableStatuses: Reservation['status'][] = ['seated', 'confirmed'];
    const toComplete = reservations.filter((r) => {
      if (!completableStatuses.includes(r.status)) return false;
      if (r.reservationDate !== todayNz && r.reservationDate !== todayUtc) return false;
      return [...relatedIds].some((id) => reservationHoldsTable(r, id));
    });

    for (const r of toComplete) {
      try {
        await handleUpdateReservation(r.id, { status: 'completed' });
        console.log(`✅ Reservation ${r.id} (${r.customerName}) marked completed after order ${order.id}`);
      } catch (err) {
        console.error(`Failed to complete reservation ${r.id}:`, err);
      }
    }
  }, [reservations, tables, handleUpdateReservation, integrationSettings?.businessHours?.timezone]);

  useEffect(() => {
    completeSeatedReservationsForOrderRef.current = completeSeatedReservationsForOrder;
  }, [completeSeatedReservationsForOrder]);

  const handleDeleteReservation = useCallback(async (id: number) => {
    try {
      const reservation = reservations.find(r => r.id === id);
      if (!reservation) return;

      await removeReservation(id);

      const today = new Date().toISOString().split('T')[0];
      if (reservation.reservationDate !== today) return;

      const now = new Date();

      const tableIdsToFree = [
        ...(reservation.tableId ? [reservation.tableId] : []),
        ...(reservation.assignedTableIds ?? []),
      ];
      for (const tid of tableIdsToFree) {
        const hasOrder = orders.some((o) => isActiveTableOrder(o, tid));
        if (hasOrder) continue;
        if (tableHasSeatedReservation(reservations, tid, today, id)) continue;
        const otherSoon = reservations.some(
          (r) =>
            r.id !== id &&
            isReservationSoonForFloor(r, now) &&
            reservationHoldsTable(r, tid),
        );
        if (otherSoon) continue;
        const table = tables.find((t) => t.id === tid);
        if (table?.status === 'reserved' || table?.status === 'occupied') {
          await updateTable(tid, { status: 'available' });
        }
      }
    } catch (error) {
      console.error('Failed to delete reservation:', error);
      alert('Failed to delete reservation. Please try again.');
      throw error;
    }
  }, [reservations, tables, orders, removeReservation, updateTable, isActiveTableOrder]);

  const handleSaveEvent = useCallback(
    async (event: Omit<Event, 'id' | 'createdAt' | 'updatedAt'>) => {
      try {
        await createEvent(event);
      } catch (error) {
        console.error('Failed to save event:', error);
        alert('Failed to save event. Please try again.');
        throw error;
      }
    },
    [createEvent]
  );

  const handleUpdateEvent = useCallback(
    async (id: string | number, updates: Partial<Event>) => {
      try {
        await updateEvent(id, updates);
      } catch (error) {
        console.error('Failed to update event:', error);
        alert('Failed to update event. Please try again.');
        throw error;
      }
    },
    [updateEvent]
  );

  const handleDeleteEvent = useCallback(
    async (id: string | number) => {
      if (!window.confirm('Delete this event? Reservations linked to it will keep the event reference.')) return;
      try {
        await removeEvent(id);
      } catch (error) {
        console.error('Failed to delete event:', error);
        alert('Failed to delete event. Please try again.');
        throw error;
      }
    },
    [removeEvent]
  );

  const showCustomerView = useCallback((table: Table) => {
    setCustomerViewParams({ table });
    setViewMode('customer');
  }, []);

  const showAdminView = useCallback(() => {
    setViewMode('admin');
    setCustomerViewParams(null);
  }, []);

  const handleSaveSupplier = useCallback(async (supplier: Supplier) => {
    const exists = suppliers.some((s) => s.id === supplier.id && supplier.id !== 0);
    if (exists) {
      await updateSupplier(supplier.id, supplier);
    } else {
      const { id: _id, ...data } = supplier;
      await createSupplier(data);
    }
  }, [suppliers, createSupplier, updateSupplier]);

  const handleDeleteSupplier = useCallback(async (supplierId: number) => {
    if (!window.confirm('Delete this supplier?')) return;
    await deleteSupplier(supplierId);
  }, [deleteSupplier]);

  // Shipment handlers
  const handleSaveShipment = useCallback(async (shipment: Omit<OverseasShipment, 'id'>) => {
    try {
      const { shipmentService } = await import('./lib/database');
      await shipmentService.create(shipment);
      // Don't manually update state - let the subscription handle it to avoid duplicates
    } catch (error) {
      console.error('Failed to save shipment:', error);
      alert('Failed to save shipment. Please try again.');
    }
  }, []);

  const handleMarkShipmentArrived = useCallback(async (shipmentId: string) => {
    try {
      const { shipmentService } = await import('./lib/database');
      await shipmentService.update(shipmentId, {
        status: 'arrived',
        actualArrivalDate: Date.now(),
      });
      setShipments(prev => prev.map(s => 
        s.id === shipmentId 
          ? { ...s, status: 'arrived' as const, actualArrivalDate: Date.now() }
          : s
      ));
    } catch (error) {
      console.error('Failed to mark shipment as arrived:', error);
      alert('Failed to update shipment. Please try again.');
    }
  }, []);

  const handleUpdateReceivedQuantities = useCallback(async (shipmentId: string, items: ShipmentItem[]) => {
    try {
      const { shipmentService } = await import('./lib/database');
      const shipment = shipments.find(s => s.id === shipmentId);
      if (!shipment) return;

      await shipmentService.update(shipmentId, {
        items: items,
        status: 'receiving' as const,
      });
      setShipments(prev => prev.map(s => 
        s.id === shipmentId 
          ? { ...s, items: items, status: 'receiving' as const }
          : s
      ));
    } catch (error) {
      console.error('Failed to update received quantities:', error);
      alert('Failed to update received quantities. Please try again.');
    }
  }, [shipments]);

  const handleCompleteReceiving = useCallback(async (shipmentId: string, inventoryUpdates: any[]) => {
    try {
      const { shipmentService } = await import('./lib/database');
      const { inventoryService } = await import('./lib/database');
      
      // Apply inventory updates
      for (const update of inventoryUpdates) {
        if (update.action === 'add') {
          const item = inventory.find(i => i.id === update.itemId);
          if (item) {
            await inventoryService.update(update.itemId, {
              stock: item.stock + update.quantity,
              lastUpdated: Date.now(),
            });
            setInventory(prev => prev.map(i => 
              i.id === update.itemId 
                ? { ...i, stock: i.stock + update.quantity, lastUpdated: Date.now() }
                : i
            ));
          }
        } else if (update.action === 'adjust') {
          await inventoryService.update(update.itemId, {
            stock: update.quantity,
            lastUpdated: Date.now(),
          });
          setInventory(prev => prev.map(i => 
            i.id === update.itemId 
              ? { ...i, stock: update.quantity, lastUpdated: Date.now() }
              : i
          ));
        } else if (update.action === 'create' && update.newItem) {
          const created = await inventoryService.create(update.newItem);
          setInventory(prev => [...prev, created]);
        }
      }

      // Update shipment items with inventory actions for reversal tracking
      const shipment = shipments.find(s => s.id === shipmentId);
      if (shipment) {
        const updatedItems = shipment.items?.map(item => {
          const update = inventoryUpdates.find(u => 
            u.itemId === item.inventoryItemId || 
            (u.newItem && u.newItem.name === item.expectedName)
          );
          if (update) {
            return {
              ...item,
              inventoryAction: update.action,
              inventoryItemId: update.itemId,
              inventoryQuantityChange: update.quantity,
            };
          }
          return item;
        }) || [];

        // Mark shipment as completed with updated items
        await shipmentService.update(shipmentId, {
          status: 'completed' as const,
          dateReceived: Date.now(),
          receivedBy: currentUser?.id,
          items: updatedItems,
        });
        setShipments(prev => prev.map(s => 
          s.id === shipmentId 
            ? { 
                ...s, 
                status: 'completed' as const, 
                dateReceived: Date.now(), 
                receivedBy: currentUser?.id,
                items: updatedItems,
              }
            : s
        ));
      }
    } catch (error) {
      console.error('Failed to complete receiving:', error);
      alert('Failed to complete receiving. Please try again.');
    }
  }, [inventory, currentUser]);

  const handleCancelShipment = useCallback(async (shipmentId: string) => {
    try {
      const { shipmentService } = await import('./lib/database');
      await shipmentService.update(shipmentId, {
        status: 'cancelled' as const,
      });
      setShipments(prev => prev.map(s => 
        s.id === shipmentId 
          ? { ...s, status: 'cancelled' as const }
          : s
      ));
    } catch (error) {
      console.error('Failed to cancel shipment:', error);
      alert('Failed to cancel shipment. Please try again.');
    }
  }, []);

  const handleUpdateShipment = useCallback(async (shipmentId: string, updates: Partial<OverseasShipment>) => {
    try {
      const { shipmentService } = await import('./lib/database');
      await shipmentService.update(shipmentId, updates);
      // Don't manually update state - let subscription handle it
    } catch (error) {
      console.error('Failed to update shipment:', error);
      alert('Failed to update shipment. Please try again.');
    }
  }, []);

  // Remove/cancel a completed shipment and reverse inventory changes
  const handleRemoveShipment = useCallback(async (shipmentId: string) => {
    if (!window.confirm('Are you sure you want to remove this shipment? This will reverse all inventory changes made when this shipment was received.')) {
      return;
    }

    try {
      const { shipmentService } = await import('./lib/database');
      const { inventoryService } = await import('./lib/database');
      
      // Get the shipment
      const shipment = shipments.find(s => s.id === shipmentId);
      if (!shipment) {
        alert('Shipment not found');
        return;
      }

      // If shipment was completed, reverse inventory changes
      if (shipment.status === 'completed') {
        // We need to track what inventory changes were made
        // Since we don't store this directly, we'll reverse based on the received quantities
        // and the action that was taken (add/adjust/create)
        
        // For each item that was received, we need to reverse the inventory change
        // This is tricky because we don't store the original inventory state
        // We'll need to ask the user or make assumptions
        
        const confirmed = window.confirm(
          'This shipment was completed and inventory was updated. ' +
          'To reverse the changes, we need to:\n\n' +
          '1. For items that were ADDED: Subtract the received quantity\n' +
          '2. For items that were ADJUSTED: Restore previous quantity (this may not be accurate)\n' +
          '3. For items that were CREATED: Delete the inventory item\n\n' +
          'Continue with reversal?'
        );
        
        if (!confirmed) return;

        // Reverse inventory changes based on received items
        for (const item of shipment.items || []) {
          if (item.receivedQuantity && item.receivedQuantity > 0) {
            // Find matching inventory item
            const inventoryItem = inventory.find(i => 
              i.id === item.inventoryItemId || 
              i.name.toLowerCase() === item.expectedName.toLowerCase()
            );

            if (inventoryItem) {
              // If this item was created during receiving, delete it
              // Otherwise, we'll subtract the received quantity
              // Note: We can't perfectly restore "adjust" actions without storing original values
              
              if (item.inventoryAction === 'create') {
                // Delete the created item
                await inventoryService.delete(inventoryItem.id);
                setInventory(prev => prev.filter(i => i.id !== inventoryItem.id));
              } else if (item.inventoryAction === 'add') {
                // Subtract the received quantity
                const newStock = Math.max(0, inventoryItem.stock - item.receivedQuantity);
                await inventoryService.update(inventoryItem.id, {
                  stock: newStock,
                  lastUpdated: Date.now(),
                });
                setInventory(prev => prev.map(i => 
                  i.id === inventoryItem.id 
                    ? { ...i, stock: newStock, lastUpdated: Date.now() }
                    : i
                ));
              } else if (item.inventoryAction === 'adjust') {
                // For adjust, we can't restore the original value perfectly
                // We'll subtract the received quantity as a best-effort reversal
                const newStock = Math.max(0, inventoryItem.stock - item.receivedQuantity);
                await inventoryService.update(inventoryItem.id, {
                  stock: newStock,
                  lastUpdated: Date.now(),
                });
                setInventory(prev => prev.map(i => 
                  i.id === inventoryItem.id 
                    ? { ...i, stock: newStock, lastUpdated: Date.now() }
                    : i
                ));
                console.warn(`⚠️ Adjusted item "${inventoryItem.name}" - original value may not be perfectly restored`);
              }
            }
          }
        }
      }

      // Delete the shipment
      await shipmentService.delete(shipmentId);
      setShipments(prev => prev.filter(s => s.id !== shipmentId));
      
      alert('Shipment removed successfully. Inventory changes have been reversed.');
    } catch (error) {
      console.error('Failed to remove shipment:', error);
      alert('Failed to remove shipment. Please try again.');
    }
  }, [shipments, inventory]);

  const handleCreateInventoryItem = useCallback(async (item: Omit<InventoryItem, 'id' | 'lastUpdated'>): Promise<InventoryItem> => {
    const { inventoryService } = await import('./lib/database');
    const created = await inventoryService.create(item);
    setInventory(prev => [...prev, created]);
    return created;
  }, []);

  const handleUpdateInventoryItem = useCallback(async (itemId: number, updates: Partial<InventoryItem>): Promise<void> => {
    const { inventoryService } = await import('./lib/database');
    await inventoryService.update(itemId, updates);
    setInventory(prev => prev.map(i => i.id === itemId ? { ...i, ...updates } : i));
  }, []);
  
  const handleSavePurchaseOrder = useCallback(async (po: PurchaseOrder) => {
    const normalized = normalizePurchaseOrder(po, inventory);
    const supplier = suppliers.find((s) => s.id === normalized.supplierId);
    const supplierName = supplier?.name ?? '';
    const existing = normalized.id ? purchaseOrders.find((p) => p.id === normalized.id) : undefined;

    try {
      if (existing) {
        if (existing.status === 'received' && normalized.status === 'cancelled') {
          throw new Error('A received purchase order cannot be cancelled.');
        }
        const receiving = existing.status !== 'received' && normalized.status === 'received';
        const ordering = existing.status !== 'ordered' && normalized.status === 'ordered';
        let payload = normalized;
        if (ordering && !normalized.dateOrdered) {
          payload = { ...normalized, dateOrdered: Date.now() };
        }
        if (receiving) {
          payload = { ...payload, dateReceived: payload.dateReceived ?? Date.now() };
        }
        if (receiving) {
          const result = await applyPurchaseOrderReceive(payload, inventory, supplierName);
          await refreshInventory();
          await updatePurchaseOrder(existing.id, { ...payload, items: result.items });
          alert(formatReceiveSummary(result));
        } else {
          await updatePurchaseOrder(existing.id, payload);
        }
        return;
      }

      const toCreate = {
        ...normalized,
        dateCreated: normalized.dateCreated || Date.now(),
      };
      const created = await createPurchaseOrder(toCreate);
      if (normalized.status === 'received') {
        const withReceived = { ...created, status: 'received' as const, dateReceived: Date.now() };
        const result = await applyPurchaseOrderReceive(withReceived, inventory, supplierName);
        await refreshInventory();
        await updatePurchaseOrder(created.id, { ...withReceived, items: result.items });
        alert(formatReceiveSummary(result));
      }
    } catch (err) {
      console.error('Failed to save purchase order:', err);
      alert(err instanceof Error ? err.message : 'Failed to save purchase order.');
      throw err;
    }
  }, [
    inventory,
    suppliers,
    purchaseOrders,
    createPurchaseOrder,
    updatePurchaseOrder,
    refreshInventory,
  ]);

  const navigateToInvoices = useCallback((purchaseOrderId?: string) => {
    setInvoicePrefillPoId(purchaseOrderId);
    setActivePage('invoices');
  }, []);

  const handleSaveBillingClient = useCallback(async (client: BillingClient) => {
    const exists = billingClients.some((c) => c.id === client.id && client.id !== 0);
    if (exists) {
      await updateBillingClient(client.id, client);
    } else {
      const { id: _id, ...data } = client;
      await createBillingClient(data);
    }
  }, [billingClients, createBillingClient, updateBillingClient]);

  const handleDeleteBillingClient = useCallback(async (clientId: number) => {
    if (!window.confirm('Delete this billing client?')) return;
    await deleteBillingClient(clientId);
  }, [deleteBillingClient]);

  const handleSaveSupplierInvoice = useCallback(async (invoice: SupplierInvoice) => {
    const { stampInvoiceLineCategories, planInvoiceCostWriteback } = await import('./lib/invoiceCostWriteback');
    const stamped = stampInvoiceLineCategories(invoice);
    const exists = stamped.id && supplierInvoices.some((i) => i.id === stamped.id);
    if (exists) {
      const { id, ...updates } = stamped;
      await updateSupplierInvoice(id, updates);
    } else {
      const { id: _id, ...data } = stamped;
      await createSupplierInvoice({
        ...data,
        dateCreated: data.dateCreated || Date.now(),
      });
    }
    const po = stamped.purchaseOrderId
      ? purchaseOrders.find((p) => p.id === stamped.purchaseOrderId)
      : undefined;
    const writeback = planInvoiceCostWriteback(stamped, po, inventory);
    if (po && writeback.nextPoItems) {
      await updatePurchaseOrder(po.id, { items: writeback.nextPoItems });
    }
    if (writeback.inventoryUpdates.length > 0) {
      const { inventoryService } = await import('./lib/database');
      for (const update of writeback.inventoryUpdates) {
        await inventoryService.update(update.inventoryItemId, {
          costPerUnit: update.costPerUnit,
          lastUpdated: Date.now(),
        });
      }
      await refreshInventory();
    }
  }, [
    supplierInvoices,
    createSupplierInvoice,
    updateSupplierInvoice,
    purchaseOrders,
    inventory,
    updatePurchaseOrder,
    refreshInventory,
  ]);

  const handleSaveClientInvoice = useCallback(async (invoice: ClientInvoice) => {
    const exists = invoice.id && clientInvoices.some((i) => i.id === invoice.id);
    if (exists) {
      const { id, ...updates } = invoice;
      await updateClientInvoice(id, updates);
    } else {
      const { id: _id, ...data } = invoice;
      await createClientInvoice({
        ...data,
        dateCreated: data.dateCreated || Date.now(),
      });
    }
  }, [clientInvoices, createClientInvoice, updateClientInvoice]);

  const handleAcceptDeliveryOrder = useCallback(async (deliveryOrder: DeliveryOrder) => {
      if (deliveryOrder.platform === 'Uber Eats') {
        try {
          await acceptUberEatsOrder(deliveryOrder.id);
          alert('Uber Eats order accepted and created in POS.');
        } catch (err) {
          console.error('Accept Uber Eats order failed:', err);
          alert((err as Error).message || 'Failed to accept order.');
        }
        return;
      }
      const orderItems: OrderItem[] = deliveryOrder.items.map((item, index) => {
          const existingMenuItem = menuItems.find(mi => mi.name.toLowerCase() === item.name.split(' (')[0].toLowerCase());
          return {
              id: existingMenuItem?.id ?? 900 + index, name: item.name, price: existingMenuItem?.price ?? 10,
              quantity: item.quantity, categoryId: existingMenuItem?.categoryId ?? 0, imageUrl: existingMenuItem?.imageUrl ?? '', status: 'pending'
          };
      });
      const orderNotes = deliveryOrder.items.filter(item => item.specialInstructions).map(item => `${item.quantity}x ${item.name}: "${item.specialInstructions}"`).join('; ');
      
      const totalBeforeDiscount = orderItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
      const totalAmount = totalBeforeDiscount;
      const taxAmount = totalAmount * (15 / 115);
      const subtotal = totalAmount - taxAmount;
      
      // Check fire mode - queue orders if active
      const orderStatus = fireModeActive ? 'queued' : 'new';
      
      const newInternalOrder: Order = {
          id: `ORD-DEL-${deliveryOrder.id}`, orderType: 'delivery', items: orderItems, status: orderStatus, timestamp: Date.now(), paymentStatus: 'unpaid', payments: [], notes: orderNotes,
          deliveryInfo: { platform: deliveryOrder.platform, customerName: deliveryOrder.customerName, orderId: deliveryOrder.platformOrderId },
          subtotal, taxAmount, totalAmount, deliveryNumber: deliveryCounter
      };
      setDeliveryCounter(prev => prev + 1);
      await createOrder(newInternalOrder);
      alert(fireModeActive 
        ? `${deliveryOrder.platform} order queued due to fire evacuation mode. It will be processed when fire mode is deactivated.`
        : `${deliveryOrder.platform} order accepted and sent to kitchen.`);
  }, [menuItems, deliveryCounter, fireModeActive, createOrder]);

  const handleRejectDeliveryOrder = useCallback((order: DeliveryOrder) => {
      if (order.platform === 'Uber Eats') {
        denyUberEatsOrder(order.id, { code: 'OTHER', explanation: 'Order denied' }).catch((err) => {
          console.error('Deny Uber Eats order failed:', err);
          alert((err as Error).message || 'Failed to deny order.');
        });
        return;
      }
      setMockRejectedIds(prev => new Set(prev).add(order.id));
  }, []);
  
  const handleUpdateTerminal = useCallback(async (terminalId: string, updates: Partial<SmartpayTerminal>) => {
      const updatedSettings = {
        ...integrationSettings,
        smartpay: {
          ...integrationSettings.smartpay,
          terminals: integrationSettings.smartpay.terminals.map(t => 
            t.id === terminalId ? { ...t, ...updates } : t
          ),
        },
      };
      setIntegrationSettings(updatedSettings);
      
      try {
        console.log('💾 Saving Smartpay terminal update...', terminalId, updates);
        const { integrationSettingsService } = await import('./lib/database');
        await integrationSettingsService.save(updatedSettings);
        console.log('✅ Smartpay terminal updated successfully!');
      } catch (error) {
        console.error('❌ Error saving Smartpay terminal update:', error);
      }
  }, [integrationSettings]);

  const handleAddTerminal = useCallback(async (terminal: SmartpayTerminal) => {
      const updatedSettings = {
        ...integrationSettings,
        smartpay: {
          ...integrationSettings.smartpay,
          terminals: [...integrationSettings.smartpay.terminals, terminal],
        },
      };
      setIntegrationSettings(updatedSettings);
      
      try {
        console.log('💾 Saving new Smartpay terminal...', terminal);
        const { integrationSettingsService } = await import('./lib/database');
        await integrationSettingsService.save(updatedSettings);
        console.log('✅ New Smartpay terminal saved successfully!');
      } catch (error) {
        console.error('❌ Error saving new Smartpay terminal:', error);
      }
  }, [integrationSettings]);

  const handleDeleteTerminal = useCallback(async (terminalId: string) => {
      const updatedSettings = {
        ...integrationSettings,
        smartpay: {
          ...integrationSettings.smartpay,
          terminals: integrationSettings.smartpay.terminals.filter(t => t.id !== terminalId),
        },
      };
      setIntegrationSettings(updatedSettings);
      
      try {
        console.log('💾 Deleting Smartpay terminal...', terminalId);
        const { integrationSettingsService } = await import('./lib/database');
        await integrationSettingsService.save(updatedSettings);
        console.log('✅ Smartpay terminal deleted successfully!');
      } catch (error) {
        console.error('❌ Error deleting Smartpay terminal:', error);
      }
  }, [integrationSettings]);
  
  const handleSavePrinter = useCallback(async (printer: ReceiptPrinter) => {
    const updatedSettings = {
      ...integrationSettings,
      receiptPrinters: (() => {
        const existing = integrationSettings.receiptPrinters.find(p => p.id === printer.id);
        if (existing) {
          return integrationSettings.receiptPrinters.map(p => p.id === printer.id ? printer : p);
        }
        return [...integrationSettings.receiptPrinters, { ...printer, id: `print-${Date.now()}` }];
      })(),
    };
    
    try {
      console.log('💾 Saving printer configuration...', printer);
      const { integrationSettingsService } = await import('./lib/database');
      await integrationSettingsService.save(updatedSettings);
      setIntegrationSettings(updatedSettings);
      console.log('✅ Printer configuration saved successfully!');
    } catch (error) {
      console.error('❌ Error saving printer configuration:', error);
      // Still update local state even if Firestore save fails
      setIntegrationSettings(updatedSettings);
    }
  }, [integrationSettings]);

  const handleDeletePrinter = useCallback(async (printerId: string) => {
    const updatedSettings = {
      ...integrationSettings,
      receiptPrinters: integrationSettings.receiptPrinters.filter(p => p.id !== printerId),
    };
    
    try {
      console.log('💾 Deleting printer configuration...', printerId);
      const { integrationSettingsService } = await import('./lib/database');
      await integrationSettingsService.save(updatedSettings);
      setIntegrationSettings(updatedSettings);
      console.log('✅ Printer configuration deleted successfully!');
    } catch (error) {
      console.error('❌ Error deleting printer configuration:', error);
      // Still update local state even if Firestore save fails
      setIntegrationSettings(updatedSettings);
    }
  }, [integrationSettings]);
  
  const handleSaveBuffetPackages = useCallback(async (packages: BuffetPackage[]) => {
    const updated = { ...integrationSettings, buffetPackages: packages };
    const { integrationSettingsService } = await import('./lib/database');
    await integrationSettingsService.save(updated);
    setIntegrationSettings(withNormalizedEventStall(updated));
  }, [integrationSettings]);
  
  const handleSaveIntegrationSettings = useCallback(async (settings: IntegrationSettings) => {
    try {
      console.log('💾 Saving integration settings...', settings);
      const { integrationSettingsService, publicOnlineOrderingService } = await import('./lib/database');
      await integrationSettingsService.save(settings);
      try {
        await publicOnlineOrderingService.syncFromSettings({
          businessInfo: settings.businessInfo,
          businessHours: settings.businessHours,
          holidaySurcharge: settings.holidaySurcharge,
        });
      } catch (syncError) {
        // Production may block client writes to public_config; Cloud Function syncs on settings save.
        console.warn('Public online ordering sync from client failed (server trigger will apply):', syncError);
      }
      setIntegrationSettings(withNormalizedEventStall(settings));
      console.log('✅ Integration settings saved successfully!');
      alert("Settings saved successfully!");
    } catch (error) {
      console.error('❌ Error saving integration settings:', error);
      alert('Failed to save settings. Please try again.');
    }
  }, []);

  // Force a fresh register-session check after a meaningful idle gap (tab hidden/backgrounded
  // for a while, or the network dropped) — see `registerWakeTick` comment above for why this
  // can't rely on `liveDataStatus.refreshUnhealthy()` alone. Gated by a minimum hidden duration
  // so quick alt-tabs during normal service don't retrigger the hydration spinner.
  useEffect(() => {
    const REWAKE_THRESHOLD_MS = 60_000;
    let hiddenAt: number | null = document.visibilityState === 'hidden' ? Date.now() : null;
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        return;
      }
      if (hiddenAt != null && Date.now() - hiddenAt >= REWAKE_THRESHOLD_MS) {
        setRegisterWakeTick((t) => t + 1);
      }
      hiddenAt = null;
    };
    const handleOnline = () => setRegisterWakeTick((t) => t + 1);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  // Shared register: dedicated active-session listener for currentSession (small, reliable).
  // useRegisterSessions provides bounded history for Reports; do not derive currentSession
  // from that hook alone — a failed history index must not hide an open register.
  useEffect(() => {
    if (isAuthLoading) {
      return;
    }
    if (!staffDataEnabled) {
      setCurrentSession(null);
      setRegisterSessionsHydrated(true);
      return;
    }

    setRegisterSessionsHydrated(false);
    let cancelled = false;

    void (async () => {
      try {
        const active = await registerSessionsService.getActiveSessions({ preferServer: true });
        if (cancelled) return;
        const session = active[0] ?? null;
        setCurrentSession(session);
        writeCachedRegisterSession(session);
      } catch (e) {
        console.error('Active register session prime read failed:', e);
      } finally {
        if (!cancelled) {
          setRegisterSessionsHydrated(true);
        }
      }
    })();

    const unsub = registerSessionsService.subscribeActive((sessions) => {
      if (cancelled) return;
      const session = sessions[0] ?? null;
      setCurrentSession(session);
      writeCachedRegisterSession(session);
      setRegisterSessionsHydrated(true);
    });

    return () => {
      cancelled = true;
      unsub();
    };
  }, [isAuthLoading, staffDataEnabled, registerWakeTick]);

  useEffect(() => {
    if (hadStaffSessionRef.current && !staffDataEnabled) {
      try {
        sessionStorage.removeItem(ACTIVE_REGISTER_SESSION_CACHE_KEY);
      } catch {
        /* ignore */
      }
    }
    hadStaffSessionRef.current = staffDataEnabled;
  }, [staffDataEnabled]);

  // Keep Reports register list in sync when opening any reports screen.
  useEffect(() => {
    if (!staffDataEnabled) return;
    if (activePage === 'reports' || activePage.startsWith('reports-')) {
      refreshRegisterSessions().catch(() => {});
    }
  }, [activePage, staffDataEnabled, refreshRegisterSessions]);

  // Subscribe to shipments when purchasing/invoices need overseas shipment data.
  useEffect(() => {
    if (!purchasingDataEnabled) {
      setShipments([]);
      return;
    }
    let unsubscribe: (() => void) | null = null;
    let cancelled = false;

    const subscribe = async () => {
      try {
        const { shipmentService } = await import('./lib/database');
        unsubscribe = shipmentService.subscribe((updatedShipments) => {
          if (cancelled) return;
          console.log('📦 Shipments updated:', updatedShipments.length);
          const uniqueShipments = updatedShipments.reduce((acc, shipment) => {
            if (!acc.find(s => s.id === shipment.id)) {
              acc.push(shipment);
            }
            return acc;
          }, [] as OverseasShipment[]);
          setShipments(uniqueShipments);
        });
      } catch (error) {
        console.error('❌ Failed to subscribe to shipments:', error);
      }
    };

    subscribe();

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [purchasingDataEnabled]);

  const handleOpenRegister = useCallback(async (openingFloat: number) => {
    console.log('🔓 handleOpenRegister called with float:', openingFloat);

    if (!currentUser) {
      alert('Please log in first');
      return;
    }

    // Only managers can open register
    if (!canManageRegister(currentUser)) {
      alert('Only managers can open the register. Please login as a manager.');
      return;
    }

    // Layer 1 (in-memory): cheapest guard — if the listener already knows about
    // an active session, adopt and return.
    if (currentSession && currentSession.isActive) {
      alert(
        `A register is already open on "${currentSession.terminalName || currentSession.terminalId}" ` +
        `by ${currentSession.cashierName || 'another user'}. This device is already sharing it.`
      );
      return;
    }

    setIsOpeningRegister(true);

    try {
      // Layer 2 (pre-flight): re-check the database immediately before writing.
      // Closes the gap between the last `subscribeActive` snapshot and the user
      // tapping Open (and the race window between two devices both reading null
      // and racing to create). One quick Firestore read with a server-side
      // filter, so the result set is bounded to active sessions only.
      const stillActive = await registerSessionsService.getActiveSessions({ preferServer: true });
      if (stillActive.length > 0) {
        const adopted = stillActive[0];
        setCurrentSession(adopted);
        writeCachedRegisterSession(adopted);
        await refreshRegisterSessions();
        addAuditLog('register.open_blocked_duplicate', 'register_session', adopted.id, {
          attemptedBy: currentUser.id,
          attemptedByName: currentUser.name,
          existingSessionStartedAt: adopted.startTime,
          existingCashier: adopted.cashierName,
          duplicateCount: stillActive.length,
        });
        setIsOpeningRegister(false);
        alert(
          `A register is already open by ${adopted.cashierName || 'another user'} ` +
          `on ${adopted.terminalName || adopted.terminalId}. This device is now sharing it.`
        );
        return;
      }

      const sessionData = {
        terminalId,
        terminalName,
        cashierId: currentUser.id,
        cashierName: currentUser.name,
        cashierEmail: currentUser.email,
        openingFloat,
        startTime: Date.now(),
        cashSales: 0,
        cardSales: 0,
        isActive: true,
      };
      console.log('📝 Opening shared register session:', sessionData);

      const newSession = await createRegisterSession(sessionData);
      console.log('✅ Session created in database:', newSession);
      addAuditLog('register.open', 'register_session', newSession.id, {
        terminalId: newSession.terminalId,
        terminalName: newSession.terminalName,
        openingFloat: newSession.openingFloat,
        cashierId: newSession.cashierId,
        cashierName: newSession.cashierName,
      });
      // Optimistically set the session — the `subscribeActive` listener will reconcile shortly.
      setCurrentSession(newSession);
      writeCachedRegisterSession(newSession);
      setCloseRegisterFailed(false);

      setTimeout(() => {
        setIsOpeningRegister(false);
        console.log('✅ Opening register flag cleared');
      }, 1000);
    } catch (error: any) {
      setIsOpeningRegister(false);
      // Layer 3 hit: service-level guard inside `registerSessionsService.create`
      // detected a parallel active session that slipped past Layers 1 & 2.
      // Adopt it and tell the user we're sharing rather than failing generically.
      if (error?.code === 'DUPLICATE_ACTIVE_REGISTER') {
        const existing = error.existingSession as RegisterSession | undefined;
        if (existing) {
          setCurrentSession(existing);
          writeCachedRegisterSession(existing);
          addAuditLog('register.open_blocked_duplicate', 'register_session', existing.id, {
            attemptedBy: currentUser.id,
            attemptedByName: currentUser.name,
            existingSessionStartedAt: existing.startTime,
            existingCashier: existing.cashierName,
            detectedAt: 'service_layer',
          });
        }
        // Force the listener to re-emit so all consumers see the adopted session.
        refreshRegisterSessions().catch(() => { /* best-effort */ });
        alert(
          existing
            ? `A register is already open by ${existing.cashierName || 'another user'} ` +
              `on ${existing.terminalName || existing.terminalId}. This device is now sharing it.`
            : 'A register is already open elsewhere. This device is now sharing it.'
        );
        return;
      }
      console.error('❌ Failed to open register:', error);
      alert('Failed to open register. Please try again.');
    }
  }, [currentUser, currentSession, terminalId, terminalName, createRegisterSession, addAuditLog, refreshRegisterSessions]);

  const handleCloseRegister = useCallback(
    async (
      closingFloat: number,
      session: RegisterSession,
      todaysSales: { cash: number; card: number; cashDenominations?: { denom: number; count: number }[] },
    ) => {
    // Only managers can close register
    if (!currentUser || !canManageRegister(currentUser)) {
      alert('Only managers can close the register.');
      return;
    }
    try {
      const updatedSession = await updateRegisterSession(session.id, {
        closingFloat,
        endTime: Date.now(),
        cashSales: todaysSales.cash,
        cardSales: todaysSales.card,
        variance: closingFloat - (session.openingFloat + todaysSales.cash),
        isActive: false,
        ...(todaysSales.cashDenominations && todaysSales.cashDenominations.length > 0
          ? { cashDenominations: todaysSales.cashDenominations }
          : {}),
      });
      addAuditLog('register.close', 'register_session', session.id, {
        closingFloat,
        cashSales: todaysSales.cash,
        cardSales: todaysSales.card,
        variance: closingFloat - (session.openingFloat + todaysSales.cash),
        openingFloat: session.openingFloat,
        terminalId: session.terminalId,
        terminalName: session.terminalName,
        ...(todaysSales.cashDenominations && todaysSales.cashDenominations.length > 0
          ? { cashDenominations: todaysSales.cashDenominations }
          : {}),
      });
      setCurrentSession(null);
      writeCachedRegisterSession(null);
      setCloseRegisterFailed(false);
      await refreshRegisterSessions();
    } catch (error) {
      console.error('Failed to close register:', error);
      setCloseRegisterFailed(true);
      alert('Failed to close register. Please try again. If the problem persists, use Force Close.');
    }
  }, [currentUser, updateRegisterSession, refreshRegisterSessions, addAuditLog]);

  const handleForceCloseSession = useCallback(async (sessionId: string, values: { closingFloat: number; cashSales: number; cardSales: number; variance: number; cashDenominations?: { denom: number; count: number }[] }) => {
    try {
      await updateRegisterSession(sessionId, {
        closingFloat: values.closingFloat,
        cashSales: values.cashSales,
        cardSales: values.cardSales,
        variance: values.variance,
        cashDenominations: values.cashDenominations,
        endTime: Date.now(),
        isActive: false,
      });
      addAuditLog('register.force_close', 'register_session', sessionId, {
        closingFloat: values.closingFloat,
        cashSales: values.cashSales,
        cardSales: values.cardSales,
        variance: values.variance,
        cashDenominations: values.cashDenominations,
      });
      await refreshRegisterSessions();
      if (currentSession && currentSession.id === sessionId) {
        setCurrentSession(null);
        writeCachedRegisterSession(null);
      }
    } catch (error) {
      console.error('Failed to force close session:', error);
      throw error;
    }
  }, [currentSession, updateRegisterSession, refreshRegisterSessions, addAuditLog]);

  const renderPage = () => {
    if (!currentUser) return null;

    switch (activePage) {
      case 'pos-mobile': return <MobilePOSPage onOpenRegister={handleOpenRegister} onRequireManagerPin={requestManagerPin} onPlaceOrder={handlePlaceOrder} onUpdateOrder={handleUpdateOrder} onProcessPayment={handleProcessPayment} menuItems={menuItems} categories={categories} orders={orders} tables={tables} terminals={integrationSettings.smartpay?.terminals || []} registerSession={currentSession || undefined} registerSessionsHydrated={registerSessionsHydrated} integrationSettings={integrationSettings} currentUser={currentUser || undefined} customers={customers} waitstaffMode={waitstaffMobileMode} onWaitstaffLock={handleWaitstaffLock} onUpdateOrderItemStatus={handleUpdateOrderItemStatus} onStartOrder={handleStartOrder} onHideOrderFromKitchen={handleHideOrderFromKitchen} slaSettings={integrationSettings.kitchenSLA} />;
      case 'pos': return <POSPage onPlaceOrder={handlePlaceOrder} onProcessPayment={handleProcessPayment} onRefundSubBill={handleRefundSubBill} onMarkAsPaid={handleMarkOrderAsPaid} onUpdateOrder={handleUpdateOrder} onRemoveOrder={handleRemoveOrder} menuItems={menuItems} categories={categories} customers={customers} tables={tables} layoutComponents={floorFixtures} rewards={rewards} orders={orders} onRefreshOrders={refreshOrders} reservations={reservations} onSaveReservation={handleSaveReservation} onUpdateReservation={handleUpdateReservation} onDeleteReservation={handleDeleteReservation} quickAddCombos={quickAddCombos} terminals={mainPosTerminals} registerSession={currentSession || undefined} registerSessionsHydrated={registerSessionsHydrated} activeSessions={activeSessions} terminalId={terminalId} terminalName={terminalName} onTerminalNameChange={setTerminalName} onOpenRegister={handleOpenRegister} onCloseRegister={handleCloseRegister} onForceCloseSession={forceCloseSession} showForceClose={closeRegisterFailed} onRequireManagerPin={requestManagerPin} onMoveCategory={handleMoveCategory} onReorderCategory={handleReorderCategory} staffMembers={staff} currentUser={currentUser || undefined} inventoryItems={inventory} recipes={recipes} onStockTake={handleStockTake} integrationSettings={integrationSettings} onUpdateMenuItem={updateMenuItem} onSaveMenuItem={handleSaveMenuItem} onDeleteMenuItem={handleDeleteMenuItem} onSaveCategory={handleSaveCategory} onDeleteCategory={handleDeleteCategory} onBulkAddMenuItems={handleBulkAddMenuItems} onSaveCombo={handleSaveCombo} onDeleteCombo={handleDeleteCombo} onSaveRecipe={handleSaveRecipe} onSaveBuffetPackages={handleSaveBuffetPackages} onSaveCustomer={handleSaveCustomer} onDeleteCustomer={handleDeleteCustomer} onMergeTables={handleMergeTables} onSplitTables={handleSplitTables} onTransferOrder={handleTransferOrder} onTransferReservation={handleTransferReservation} onOverrideOrder={handleOverrideOrder} onRemakeOrder={handleRemakeOrder} onAuditPrint={(type, order) => addAuditLog(type === 'customer' ? 'receipt.print' : 'kitchen.print', 'order', order.id, { orderType: order.orderType, tableId: order.tableId, totalAmount: order.totalAmount })} fireModeActive={fireModeActive} onActivateFireMode={handleActivateFireMode} onDeactivateFireMode={handleDeactivateFireMode} onExtendBuffetTime={handleExtendBuffetTime} onCreateCustomer={async (customerData) => { const newCustomer = await createCustomer({ ...customerData, loyaltyPoints: customerData.loyaltyPoints || 0 }); return newCustomer; }} uberPendingOrders={uberPendingOrders} enableUberIncomingAlert={activePage === 'pos'} onAcceptUberDeliveryOrder={handleAcceptDeliveryOrder} onRejectUberDeliveryOrder={handleRejectDeliveryOrder} onUpdateOrderStatus={handleUpdateOrderStatus} />;
      case 'kds': return <KitchenDisplayPage orders={orders} registerSession={currentSession} registerSessionsHydrated={registerSessionsHydrated} currentUser={currentUser} onUpdateOrderItemStatus={handleUpdateOrderItemStatus} onStartOrder={handleStartOrder} onRemoveOrder={handleHideOrderFromKitchen} slaSettings={integrationSettings.kitchenSLA} />;
      case 'service': return <ServicePage orders={orders} registerSession={currentSession} currentUser={currentUser} onUpdateStatus={handleUpdateOrderStatus} onUpdateItemStatus={handleUpdateOrderItemServedStatus} onUpdateOrderItemStatus={handleUpdateOrderItemStatus} />;
      case 'menu': return <MenuManagementPage menuItems={menuItems} categories={categories} quickAddCombos={quickAddCombos} recipes={recipes} inventoryItems={inventory} recipeInventoryMode={integrationSettings?.recipeInventoryMode ?? 'inventory_connected'} buffetPackages={getBuffetPackages(integrationSettings, undefined, { includeUnavailable: true })} onSaveBuffetPackages={handleSaveBuffetPackages} onSave={handleSaveMenuItem} onDelete={handleDeleteMenuItem} onSaveCategory={handleSaveCategory} onDeleteCategory={handleDeleteCategory} onBulkAddMenuItems={handleBulkAddMenuItems} onSaveCombo={handleSaveCombo} onDeleteCombo={handleDeleteCombo} onSaveRecipe={handleSaveRecipe} />;
      case 'inventory': return <InventoryPage 
        inventoryItems={inventory} 
        orders={orders} 
        recipes={recipes} 
        shipments={shipments}
        suppliers={suppliers}
        currentUserId={currentUser?.id || 0}
        onSave={handleSaveInventoryItem} 
        onDelete={handleDeleteInventoryItem} 
        onAdjustStock={handleAdjustStock} 
        onStockTake={handleStockTake} 
        onBulkAddInventoryItems={handleBulkAddInventoryItems}
        onSaveShipment={handleSaveShipment}
        onMarkShipmentArrived={handleMarkShipmentArrived}
        onUpdateReceivedQuantities={handleUpdateReceivedQuantities}
        onCompleteReceiving={handleCompleteReceiving}
        onCancelShipment={handleCancelShipment}
        onRemoveShipment={handleRemoveShipment}
        onUpdateShipment={handleUpdateShipment}
        onCreateInventoryItem={handleCreateInventoryItem}
        onUpdateInventoryItem={handleUpdateInventoryItem}
        onSaveSupplier={handleSaveSupplier}
      />;
      case 'assets': return <AssetRegisterPage
        assets={assets}
        loading={assetsLoading}
        onSave={handleSaveAsset}
        onBulkSave={handleBulkSaveAssets}
        onDelete={handleDeleteAsset}
      />;
      case 'staff': return (
        <StaffManagementPage 
          staffMembers={staff} 
          userRoles={mergedUserRoles} 
          onSave={handleSaveStaffMember} 
          onDelete={handleDeleteStaffMember}
          onSendInvitation={handleSendStaffInvitation}
          businessHours={integrationSettings.businessHours}
          currentUser={currentUser}
          businessInfo={integrationSettings.businessInfo}
        />
      );
      case 'user-roles': return (
        <UserRolesPage
          userRoles={userRoles}
          loading={userRolesLoading}
          error={userRolesError}
          onRefresh={refreshUserRoles}
          onAddRole={createUserRole}
          onUpdateRole={updateUserRole}
          onDeleteRole={deleteUserRole}
        />
      );
      case 'reports':
        return <ReportsPage orders={orders} registerSessions={registerSessions} activeRegisterSessions={activeSessions} currentRegisterSession={currentSession} inventoryItems={inventory} onForceCloseSession={handleForceCloseSession} onRequireManagerPin={requestManagerPin} currentUser={currentUser} tables={tables} recipes={recipes} menuItems={menuItems} staffMembers={staff} kitchenSLA={integrationSettings.kitchenSLA} categories={categories} integrationSettings={integrationSettings} onSaveIntegrationSettings={handleSaveIntegrationSettings} detailedSection="home" onNavigateToReportPage={requestPageAccess} onNavigateToCouponCampaigns={(campaignId) => { setCouponCampaignsInitialId(campaignId ?? null); requestPageAccess('coupon-campaigns'); }} />;
      case 'reports-sales-insights':
        return <ReportsPage orders={orders} registerSessions={registerSessions} activeRegisterSessions={activeSessions} currentRegisterSession={currentSession} inventoryItems={inventory} onForceCloseSession={handleForceCloseSession} onRequireManagerPin={requestManagerPin} currentUser={currentUser} tables={tables} recipes={recipes} menuItems={menuItems} staffMembers={staff} kitchenSLA={integrationSettings.kitchenSLA} categories={categories} integrationSettings={integrationSettings} onSaveIntegrationSettings={handleSaveIntegrationSettings} detailedSection="sales-insights" onNavigateToReportPage={requestPageAccess} onNavigateToCouponCampaigns={(campaignId) => { setCouponCampaignsInitialId(campaignId ?? null); requestPageAccess('coupon-campaigns'); }} />;
      case 'reports-event-stall':
        return <ReportsPage orders={orders} registerSessions={registerSessions} activeRegisterSessions={activeSessions} currentRegisterSession={currentSession} inventoryItems={inventory} onForceCloseSession={handleForceCloseSession} onRequireManagerPin={requestManagerPin} currentUser={currentUser} tables={tables} recipes={recipes} menuItems={menuItems} staffMembers={staff} kitchenSLA={integrationSettings.kitchenSLA} categories={categories} integrationSettings={integrationSettings} onSaveIntegrationSettings={handleSaveIntegrationSettings} detailedSection="event-stall" onNavigateToReportPage={requestPageAccess} onNavigateToCouponCampaigns={(campaignId) => { setCouponCampaignsInitialId(campaignId ?? null); requestPageAccess('coupon-campaigns'); }} />;
      case 'reports-monthly':
      case 'reports-financial':
      case 'reports-reconciliation':
        return (
          <Suspense
            fallback={
              <div className="flex min-h-[40vh] items-center justify-center p-6 text-sm text-gray-600">
                Loading operating report…
              </div>
            }
          >
            <MonthlyOperatingReportPage
              orders={orders}
              registerSessions={registerSessions}
              inventoryItems={inventory}
              onForceCloseSession={handleForceCloseSession}
              onRequireManagerPin={requestManagerPin}
              currentUser={currentUser}
              tables={tables}
              recipes={recipes}
              menuItems={menuItems}
              staffMembers={staff}
              kitchenSLA={integrationSettings.kitchenSLA}
              categories={categories}
              integrationSettings={integrationSettings}
              onNavigateToReportPage={requestPageAccess}
              onNavigateToCouponCampaigns={(campaignId) => {
                setCouponCampaignsInitialId(campaignId ?? null);
                requestPageAccess('coupon-campaigns');
              }}
            />
          </Suspense>
        );
      case 'reports-food-cost':
        return <ReportsPage orders={orders} registerSessions={registerSessions} activeRegisterSessions={activeSessions} currentRegisterSession={currentSession} inventoryItems={inventory} onForceCloseSession={handleForceCloseSession} onRequireManagerPin={requestManagerPin} currentUser={currentUser} tables={tables} recipes={recipes} menuItems={menuItems} staffMembers={staff} kitchenSLA={integrationSettings.kitchenSLA} categories={categories} integrationSettings={integrationSettings} detailedSection="food-cost" onNavigateToReportPage={requestPageAccess} onNavigateToCouponCampaigns={(campaignId) => { setCouponCampaignsInitialId(campaignId ?? null); requestPageAccess('coupon-campaigns'); }} />;
      case 'expenses': return <ExpensesPage currentUser={currentUser} />;
      case 'customers': return <CustomerManagementPage customers={customers} orders={orders} onSave={handleSaveCustomer} onDelete={handleDeleteCustomer} currentStaffLabel={currentUser?.name} />;
      case 'loyalty': return <LoyaltyPage rewards={rewards} onSave={handleSaveReward} onDelete={handleDeleteReward} offers={appOffers} onSaveOffer={saveAppOffer} onDeleteOffer={removeAppOffer} customers={customers} menuItems={menuItems} categories={categories} currentStaffId={currentUser?.id} currentStaffName={currentUser?.name} isManager={Boolean(currentUser?.isAdmin || currentUser?.role === 'manager')} onNavigate={setActivePage} mealPlansEnabled={integrationSettings.businessInfo?.mealPlansEnabled === true} />;
      case 'customer-app-push': return <CustomerAppPushPage onNavigate={setActivePage} />;
      case 'customer-app-feedback': return <CustomerAppFeedbackPage onNavigate={setActivePage} />;
      case 'customer-app-chat': return <CustomerAppChatPage onNavigate={setActivePage} />;
      case 'catering': return <CateringPage onNavigate={setActivePage} />;
      case 'customer-app-announcements': return <CustomerAppAnnouncementsPage announcements={appAnnouncements} onSave={saveAppAnnouncement} onDelete={removeAppAnnouncement} onNavigate={setActivePage} />;
      case 'qr-management': return <QrManagementPage tables={tables} onSimulateScan={showCustomerView} />;
      case 'table-management': return <TableManagementPage tables={tables} orders={orders} layoutComponents={floorFixtures} onSave={handleSaveTable} onDelete={handleDeleteTable} onUpdatePosition={handleUpdateTablePosition} onUpdateLayoutComponent={handleUpdateFixture} onUpdateLayoutComponentPosition={handleUpdateFixturePosition} onCreateLayoutComponent={handleCreateFixture} onDeleteLayoutComponent={handleDeleteFixture} />;
      case 'reservations': return <ReservationManagementPage reservations={reservations} tables={tables} events={events} orders={orders} layoutComponents={floorFixtures} emailLogoUrl={integrationSettings?.businessInfo?.emailLogoUrl?.trim() || undefined} businessName={integrationSettings?.businessInfo?.businessName?.trim() || undefined} onSave={handleSaveReservation} onUpdate={handleUpdateReservation} onDelete={handleDeleteReservation} onSaveEvent={handleSaveEvent} onUpdateEvent={handleUpdateEvent} onDeleteEvent={handleDeleteEvent} onRefresh={async () => { await Promise.all([refreshReservations(), refreshEvents(), refreshTables(), refreshOrders(), refreshFloorFixtures()]); }} />;
      case 'purchasing': return <PurchasingPage suppliers={suppliers} purchaseOrders={purchaseOrders} supplierInvoices={supplierInvoices} inventoryItems={inventory} businessInfo={integrationSettings.businessInfo} purchasingDefaults={integrationSettings.purchasingInvoiceDefaults} onSaveSupplier={handleSaveSupplier} onDeleteSupplier={handleDeleteSupplier} onSavePurchaseOrder={handleSavePurchaseOrder} onSaveSupplierInvoice={handleSaveSupplierInvoice} onNavigateToInvoices={navigateToInvoices} currentStaffId={currentUser?.id} />;
      case 'invoices': return <InvoicesPage suppliers={suppliers} purchaseOrders={purchaseOrders} billingClients={billingClients} supplierInvoices={supplierInvoices} clientInvoices={clientInvoices} billingClientsLoading={billingClientsLoading} supplierInvoicesLoading={supplierInvoicesLoading} clientInvoicesLoading={clientInvoicesLoading} businessInfo={integrationSettings.businessInfo} purchasingDefaults={integrationSettings.purchasingInvoiceDefaults} buffetBreakfastPrice={integrationSettings.buffetPricing?.breakfast} currentStaffId={currentUser?.id} prefillSupplierPoId={invoicePrefillPoId} onClearPrefill={() => setInvoicePrefillPoId(undefined)} onSaveBillingClient={handleSaveBillingClient} onDeleteBillingClient={handleDeleteBillingClient} onSaveSupplierInvoice={handleSaveSupplierInvoice} onSaveClientInvoice={handleSaveClientInvoice} />;
      case 'delivery': return <DeliveryIntegrationsPage deliveryOrders={deliveryOrders} onAccept={handleAcceptDeliveryOrder} onReject={handleRejectDeliveryOrder} />;
      case 'settings': return <SettingsPage settings={integrationSettings} orders={orders} onSave={handleSaveIntegrationSettings} onUpdateTerminal={handleUpdateTerminal} onAddTerminal={handleAddTerminal} onDeleteTerminal={handleDeleteTerminal} onSavePrinter={handleSavePrinter} onDeletePrinter={handleDeletePrinter} onClearMenuPhotos={async () => { await clearAllMenuPhotos(); refreshMenuItems(); }} currentUser={currentUser} />;
      case 'coupon-campaigns': return <CouponCampaignsPage currentUser={currentUser} initialCampaignId={couponCampaignsInitialId} onInitialCampaignConsumed={clearCouponCampaignInitialId} />;
      case 'profile': return <ProfilePage currentUser={currentUser} onUpdateUser={handleUpdateCurrentUser} />;
      case 'dashboard': default: return <DashboardPage setActivePage={requestPageAccess} currentUser={currentUser} />;
    }
  };

  // Public event booking - no login required (shareable link: /book?event=EVENT_ID)
  if (viewMode === 'book' && bookingEventId) {
    return (
      <PublicBookingPage
        eventId={bookingEventId}
        businessName={integrationSettings?.businessInfo?.businessName || undefined}
        logoUrl={integrationSettings?.businessInfo?.emailLogoUrl?.trim() || undefined}
      />
    );
  }

  // Online ordering view - no login required, check this FIRST
  if (viewMode === 'online') {
    if (publicOnlineOrderingLoading && !windcaveBrowserReturn) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500" />
        </div>
      );
    }
    if (!isPublicOnlineOrderingEnabled(publicOnlineOrderingConfig)) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div className="text-center max-w-md">
            <h1 className="text-2xl font-semibold text-gray-900 mb-2">
              Online ordering is currently unavailable
            </h1>
            <p className="text-gray-600">
              {publicOnlineOrderingConfig?.businessName
                ? `${publicOnlineOrderingConfig.businessName} has temporarily disabled online ordering.`
                : 'We have temporarily disabled online ordering. Please order in store or by phone.'}
            </p>
          </div>
        </div>
      );
    }
    /**
     * Merge business info for public online ordering:
     * - Guests never load integration settings from Firestore (integration stays at initial placeholders + empty phone/email).
     * - Do NOT spread full `businessInfo` after `publicOnlineOrderingConfig` — empty strings wipe synced public fields.
     * - Start from initial + public doc, then overlay only non-empty strings from integration (saved Settings win).
     */
    const pub = publicOnlineOrderingConfig ?? {};
    const bi = integrationSettings.businessInfo ?? initialIntegrationSettings.businessInfo;
    const onlineBusinessInfo: BusinessInfo = {
      ...initialIntegrationSettings.businessInfo,
      ...pub,
    };
    (
      ['businessName', 'address', 'phone', 'email', 'gstNumber', 'website', 'nzbn'] as const
    ).forEach((key) => {
      const v = bi[key];
      if (typeof v === 'string' && v.trim()) {
        (onlineBusinessInfo as Record<string, string>)[key] = v.trim();
      }
    });
    if (bi.onlineOrderStartTime?.trim()) {
      onlineBusinessInfo.onlineOrderStartTime = bi.onlineOrderStartTime.trim();
    }
    if (bi.onlineOrderEndTime?.trim()) {
      onlineBusinessInfo.onlineOrderEndTime = bi.onlineOrderEndTime.trim();
    }
    // Guests never load integration settings — bi stays at initial defaults
    // (mealPlansEnabled: false). public_config is the source of truth; only
    // overlay when Settings actually has meal plans ON / custom labels so we
    // never clobber a synced true with the placeholder false.
    if (bi.mealPlansEnabled === true) {
      onlineBusinessInfo.mealPlansEnabled = true;
    }
    if (bi.mealPlanPublicTitle?.trim()) {
      onlineBusinessInfo.mealPlanPublicTitle = bi.mealPlanPublicTitle.trim();
    }
    if (bi.mealPlanNavLabel?.trim()) {
      onlineBusinessInfo.mealPlanNavLabel = bi.mealPlanNavLabel.trim();
    }
    return (
      <OnlineOrderPage
        businessInfo={onlineBusinessInfo}
        orderingSchedule={
          pub.orderingDays?.length
            ? { timezone: pub.timezone, days: pub.orderingDays }
            : undefined
        }
        holidaySurcharge={pub.holidaySurcharge ?? null}
        onOrderPlaced={(order) => {
          console.log('Online order placed:', order);
          // Order is already saved via useOrders hook in OnlineOrderPage
        }}
      />
    );
  }

  if (viewMode === 'receipt' && receiptShareData) {
    return (
      <Suspense
        fallback={
          <div className="flex min-h-screen items-center justify-center bg-gray-100 text-sm text-gray-600">
            Loading receipt…
          </div>
        }
      >
        <ReceiptSharePage encodedData={receiptShareData} />
      </Suspense>
    );
  }

  // Customer view (QR code ordering) - no login required
  if (viewMode === 'customer' && customerViewParams) {
    return <CustomerMenuPage 
      table={customerViewParams.table} 
      menuItems={menuItems} 
      categories={categories} 
      orders={orders} 
      onPlaceOrder={(items, notes) => handlePlaceOrder(items, 'dine-in', { tableId: customerViewParams.table.id, notes })} 
      onUpdateOrder={handleUpdateOrder}
      onProcessPayment={handleProcessPayment}
      onExit={showAdminView} 
      onUpdateMenuItem={updateMenuItem}
      terminals={integrationSettings.smartpay?.terminals || []}
      integrationSettings={integrationSettings}
    />;
  }

  // Show loading while checking auth
  if (isAuthLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-pink-500 mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  // Show login page if not authenticated (except for customer view which is handled above)
  if (!currentUser) {
    return (
      <>
        <SignInPage onSignIn={handleSignIn} onSetupMode={handleSignIn} />
        <PinEntryModal
          isOpen={showPinEntry}
          onClose={() => setShowPinEntry(false)}
          onPinVerified={handlePinVerified}
          getStaffByPin={getStaffByPin}
        />
      </>
    );
  }

  // Always render main app so POS/page state is preserved. When standby or locked,
  // overlay StandbyScreen on top instead of unmounting the app (fixes refresh clearing open order).
  return (
    <NavigationProvider value={{ navigate: requestPageAccess, previousPage }}>
      <MainLayout
        activePage={activePage}
        setActivePage={requestPageAccess} 
        currentUser={currentUser} 
        onLogout={handleLogout}
        onLockRegister={handleLockRegister}
        isRegisterLocked={isRegisterLocked}
        registerSession={currentSession}
        hideSidebar={waitstaffMobileMode && activePage === 'pos-mobile'}
      >
        <Suspense
          fallback={
            <div className="flex min-h-[40vh] items-center justify-center p-6 text-sm text-gray-600">
              Loading…
            </div>
          }
        >
          {renderPage()}
        </Suspense>
      </MainLayout>
      {currentUser && (
        <>
          <PinEntryModal
            isOpen={showPinEntry}
            onClose={handlePinModalClose}
            onPinVerified={handlePinVerified}
            getStaffByPin={getStaffByPin}
          />
        </>
      )}
      {(isStandby || isRegisterLocked) && (
        <div className="fixed inset-0 z-[100]" aria-hidden="false">
          <StandbyScreen 
            onWake={() => {
              if (!isRegisterLocked) {
                setIsStandby(false);
                reset();
              }
            }}
            isLocked={isRegisterLocked}
            onUnlock={isRegisterLocked ? handleUnlockRegister : undefined}
            logoImage="/logo.png"
            logoText="KINGS PARK"
            brandSubtext="Restaurant POS System"
          />
        </div>
      )}
    </NavigationProvider>
  );
};

export default App;